import React from "react";
import { useFillCount } from "../components/Skeleton";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import { errorText, fetchWithAuth } from "../utils/authFetch";
import { creditsChanged } from "../utils/useCredits";
import JobDetailView, {
  JobsVarsScope,
  Tabs,
  Tab,
  jobIdOf,
  firstCompany,
  companyColor,
  initialOf,
} from "../components/JobDetailView";
import CompanyLogo from "../components/CompanyLogo";
import FilterAssistant from "../components/FilterAssistant";
import { PROVIDER_BRAND } from "../theme/ThemeContext";
import {
  FiArrowLeft,
  FiBriefcase,
  FiCheck,
  FiChevronRight,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiDatabase,
  FiFileText,
  FiFolder,
  FiGlobe,
  FiLock,
  FiPlus,
  FiSliders,
  FiSlash,
  FiStar,
  FiUser,
  FiUsers,
  FiX,
  FiCheckSquare,
  FiDownload,
} from "react-icons/fi";
import { BrandLinkedIn, Spark, StarFilled } from "../components/ui/icons";
import { toCsv, downloadCsv, csvFileName, locationCells, profileCells, formatLanguageProficiency } from "../utils/csvExport";
import { useAuth } from "../context/AuthContext";
import { useFindNotification } from "../context/FindNotificationContext";
import { CircleFlag } from "react-circle-flags";
import { countries } from "country-data-list";
import { extractLocationCountry } from "../utils/locationUtils";
import {
  JobRowCard,
  JobRowSkeleton,
  SearchWrap,
  Search as JobsSearch,
  FilterRow,
  FilterField,
  PanelFieldLabel,
  ClearLink,
  FilterDropdown,
  FiltersPopover,
  POSTED_OPTIONS,
  seniorityRank,
} from "./Jobs";
import { formatNumber } from "../utils/formatNumber";
import {
  MAX_KEYWORDS,
  MAX_EMPLOYERS,
  MAX_POSITIONS,
  briefFilterRows,
  planOf,
} from "../utils/briefRows";
import { matchLevelOf } from "../utils/matchLevel";
import { PageScrollLock } from "../utils/useLockPageScroll";
import { usePlan } from "../utils/usePlan";
import { activeTourId, isTourActive, startTour, useTourTarget } from "../components/GuidedTour";
import { markGetStarted, useGetStarted } from "../lib/getStartedService";
import PlanLock from "../components/PlanLock";

function formatYmRange(start, end) {
  const part = (y) => {
    if (!y || typeof y !== "object") return "";
    const yy = y.year;
    if (!yy) return "";
    const mm =
      y.month && y.month > 0 ? `-${String(y.month).padStart(2, "0")}` : "";
    return `${yy}${mm}`;
  };
  const a = part(start);
  const b = part(end);
  if (!a && !b) return "";
  if (!b || !end?.year) return `${a} – Present`;
  return `${a} – ${b}`;
}

function decodeHtmlEntities(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'");
}


/** Modal shape from a sourced candidate's linkdapi_profile enrichment fields. */
function normalizePreviewProfile(details) {
  const pp = details?.linkdapi_profile;
  if (!pp || typeof pp !== "object") return {};
  const experiences = (pp.employment_history || []).map((e) => ({
    company: e.organization_name || "Company",
    positions: [
      {
        title: e.title,
        date_range: [
          (e.start_date || "").slice(0, 7),
          e.current ? "Present" : (e.end_date || "").slice(0, 7),
        ]
          .filter(Boolean)
          .join(" – "),
        location: e.location || "",
        // Present only once the full profile has loaded (the bulk shape omits it).
        description: decodeHtmlEntities(e.description || ""),
      },
    ],
  }));
  const education = (pp.educations || []).map((ed) => ({
    school: ed.schoolName || ed.school?.name,
    degree: ed.degree,
    field: ed.fieldOfStudy,
    date_range: formatYmRange(ed.start, ed.end),
    description: ed.description,
  }));
  return {
    profile_picture: details.photo_url || null,
    // Job title first: the headline repeats the employer shown beside it.
    headline: details.title || details.headline || "",
    summary: decodeHtmlEntities(pp.summary || ""),
    locationLine: pp.location || "",
    experiences,
    education,
    languages: pp.languages || [],
    skills: Array.isArray(pp.skills) ? pp.skills.filter(Boolean) : [],
    certifications: Array.isArray(pp.certifications) ? pp.certifications : [],
    // The bulk fetch carries a lean block; the rest arrives when the profile is opened.
    full: !!details._full,
  };
}

/* LinkedIn ships language proficiency as an enum (NATIVE_OR_BILINGUAL). Show
   the wording LinkedIn itself uses; anything unrecognised is de-shouted rather
   than printed raw. */
// Candidate photo with a fallback: a stored copy can be missing when LinkedIn's link had already expired.
function CandidatePhoto({ src, fallback = null }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [src]);
  return src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : fallback;
}

function getCandidateDisplayName(details) {
  if (!details || typeof details !== "object") return "";
  return (details.name || "").trim();
}

function getCandidateSubtitle(details) {
  if (!details || typeof details !== "object") return "";
  return (details.title || details.headline || "").trim();
}

const STAGES = [
  { id: 1, label: "Finding best candidates on the internet", startMs: 0 },
  { id: 2, label: "Looking for more candidates", startMs: 60_000 },
  {
    id: 3,
    label: "Finalizing and summarizing candidates experience",
    startMs: 140_000,
  },
  { id: 4, label: "Ranking candidates suitability", startMs: 205_000 },
];

const TOTAL_DURATION_MS = 240_000; // 4 minutes mock duration when `stage` is absent

const REAL_STAGES = [
  { key: "brief", label: "Reading your brief", expectedMs: 3_000 },
  { key: "search", label: "Screening profiles at scale", expectedMs: 4_000 },
  { key: "prerank", label: "Shortlisting the best matches", expectedMs: 4_000 },
  { key: "enrich", label: "Pulling full career histories", expectedMs: 5_000 },
  { key: "rank", label: "Ranking candidates suitability", expectedMs: 10_000 },
];

/* Whether a search is running is the server's answer (FindNotificationContext's
   `activeRun`), so none of it is mirrored here. The one thing the server can't tell
   us is that a run was started as another round of a past search, and therefore
   where to go back to — that alone is what this record carries across a remount. */
const FOLLOW_UP_RETURN_KEY = "find-candidates-followup-v2";

function readFollowUpReturn(reqNo) {
  if (typeof window === "undefined" || !reqNo) return "";
  try {
    const p = JSON.parse(
      sessionStorage.getItem(FOLLOW_UP_RETURN_KEY) || "null",
    );
    return p?.reqNo === reqNo ? String(p.returnTo || "") : "";
  } catch {
    return "";
  }
}

function writeFollowUpReturn(reqNo, returnTo) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      FOLLOW_UP_RETURN_KEY,
      JSON.stringify({ reqNo, returnTo }),
    );
  } catch {
    // ignore quota
  }
}

function clearFollowUpReturn() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(FOLLOW_UP_RETURN_KEY);
  } catch {
    // ignore
  }
}

/* Aliases of the app tokens on :root (see theme/ThemeContext). */
const surfaceTint = "var(--surface-tint)";
const surface2 = "var(--surface-2)";
const bg2 = "var(--bg-2)";
const lineSoft = "var(--line-soft)";
const lineStrong = "var(--line-strong)";
const inputBg = "var(--surface)";
const inkSoft = "var(--ink-soft)";
const inkFaint = "var(--ink-faint)";
const inkGradStart = "var(--ink-2)";
const inkGradEnd = "var(--ink-3)";
const accentColor = "var(--accent)";
const accent2Color = "var(--accent-2)";
const accentSoft = "var(--accent-soft)";
const accentSoftBorder = "var(--accent-soft)";
const accentInk = "var(--accent-ink)";
const successColor = "var(--success)";
const successSoft = "var(--success-soft)";
const warnColor = "var(--warn)";
const warnSoft = "var(--warn-soft)";
const dangerColor = "var(--danger)";
const dangerSoft = "var(--danger-soft)";
const shadowSm = "var(--shadow-sm)";
const shadowMd = "var(--shadow-md)";
const shadowLg = "var(--shadow-lg)";
const SERIF = "var(--font-display)";
/* Screens in order per start mode; the JD step holds its hiring company, a described person gets a role step. */
const SCREEN_ORDER = {
  board: ["source", "input", "country", "brief", "finding"],
  manual: ["source", "input", "country", "brief", "finding"],
  people: ["source", "input", "company", "country", "brief", "finding"],
};
const STEP_LABELS = {
  board: ["Start", "Pick a job", "Country", "Filters", "Find candidates"],
  manual: ["Start", "Job description", "Country", "Filters", "Find candidates"],
  people: ["Start", "Describe the person", "Hiring company", "Country", "Filters", "Find candidates"],
};

/* Below this a candidate is collapsed out of the results, ruled-out rows included.
   Mirrors RENIDLY_MIN_VISIBLE_SCORE in renidly_pipeline.py and the BFF (the bottom of "Medium"). */
const MIN_VISIBLE_SCORE = 40;

// Brief-level list fields the editor renders as chip rows.
const EMPTY_BRIEF_LISTS = ["locations", "must_haves", "nice_to_haves", "exclusions"];
// The recruiter sees a plan — positions × keywords, one search each; the run widens the thin ones.
const RUN_GOAL_PRESETS = [10, 20, 30, 50];
const RUN_GOAL_MAX = 500;
// Credits a run searches with before it wraps up with what it found. Mirrors RUN_SEARCH_CREDITS in renidly_pipeline.py.
const RUN_SEARCH_CREDITS = 1500;
// Extra credits the launch may add on top of the default; the total is capped at RUN_SEARCH_CREDITS_MAX in renidly_pipeline.py.
const RUN_EXTRA_PRESETS = [0, 1000, 5000];
const RUN_EXTRA_MAX = 10000;
// Ranked rows drawn per scroll step on the results page.
const RANKING_PAGE = 20;

// Mirrors RENIDLY_MAX_CRITERIA in renidly_pipeline.py, so this matches what was screened.
const MAX_CRITERIA = 10;

// One popover exists at a time, so its id is fixed (chips point at it via aria-describedby).
const CRITERIA_POP_ID = "criteria-checklist-pop";

/** True only when focus arrived by keyboard — :focus-visible, guarded for old engines. */
function isKeyboardFocus(el) {
  try {
    return el.matches(":focus-visible");
  } catch {
    return true;
  }
}


/** Defensive shape-fill so the filter editor never hits undefined lists/strings. */
/* The countries a saved search ran in: the brief's locations, else the older single country string. */
function itemCountries(item) {
  const fromBrief = Array.isArray(item?.brief?.locations) ? item.brief.locations.filter(Boolean) : [];
  if (fromBrief.length) return fromBrief;
  return String(item?.country || "").split(",").map((c) => c.trim()).filter(Boolean);
}

const isoListOf = (names) => names.map((n) => resolveCountryIso2(n).toLowerCase()).filter(Boolean);

function normalizeBriefForEdit(brief) {
  const b = { ...(brief || {}), ...planOf(brief) };
  EMPTY_BRIEF_LISTS.forEach((k) => {
    if (!Array.isArray(b[k])) b[k] = [];
  });
  return b;
}

/* Full-bleed app surface: negative margins cancel MainContent's responsive
   padding (App.js) so the top bar sits flush at the viewport top and the
   sticky wizard footer lands at the true bottom. Below 1024px that includes
   the strip MainContent reserves for the fixed menu button, so TopBar's own
   colour runs up behind it rather than leaving a band of page background;
   TopBar carries the button's clearance as padding-top instead. */
const Page = styled.div`
  margin: -2.5rem;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.ink};
  overflow: hidden;
  @media (min-width: 1025px) {
    border-radius: inherit;
  }
  /* Height stays the full 100dvh from the base rule — the page now starts at
     the viewport top again, so there is no reserved strip to subtract. */
  @media (max-width: 1024px) {
    margin: -4.75rem -1.5rem -1.5rem;
  }
  @media (max-width: 768px) {
    margin: -4.25rem -1rem -1rem;
  }
  @media (max-width: 450px) {
    margin: -4rem -0.75rem -0.75rem;
  }
  @media (max-width: 390px) {
    margin: -3.8rem -0.5rem -0.5rem;
  }
`;

/* Fixed height so the bar doesn't grow when the wizard step indicator mounts. */
const TopBar = styled.div`
  min-height: 64px;
  box-sizing: border-box;
  padding: 10px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-shrink: 0;
  /* Carries the fixed menu button's clearance, matching the strip Page now
     bleeds into. Shorthand first in each block, then padding-top, or the
     shorthand resets it. */
  @media (max-width: 1024px) {
    padding-top: calc(10px + 4.75rem);
  }
  @media (max-width: 768px) {
    min-height: 56px;
    padding: 8px 16px;
    padding-top: calc(8px + 4.25rem);
    gap: 12px;
  }
  @media (max-width: 450px) {
    padding-top: calc(8px + 4rem);
  }
  @media (max-width: 390px) {
    padding-top: calc(8px + 3.8rem);
  }
`;

const Crumbs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  min-width: 0;
  /* Phone: the trail costs more width than it gives. Keep where you are. */
  @media (max-width: 560px) {
    & > *:not(:last-child) {
      display: none;
    }
  }
`;

const Crumb = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
  transition: color 0.12s;
  &:hover {
    color: ${({ theme }) => theme.ink};
  }
  ${({ $current, theme }) =>
    $current &&
    `
    color: ${theme.ink};
    font-weight: 500;
    cursor: default;
  `}
`;

const CrumbSep = styled.span`
  color: ${lineStrong};
`;

const StepDotsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const StepDotsLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
  @media (max-width: 720px) {
    display: none;
  }
`;

const StepDotsNum = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

const StepDotsCurrent = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.ink};
  font-weight: 600;
  margin-top: 2px;
`;

const StepDotsBar = styled.div`
  display: flex;
  align-items: center;
`;

const StepDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 99px;
  @media (max-width: 560px) {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }
  background: ${({ $state }) => {
    if ($state === "done") return successColor;
    if ($state === "current") return "var(--accent)";
    return "var(--surface-tint)";
  }};
  border: 1.5px solid
    ${({ $state }) => {
      if ($state === "done") return successColor;
      if ($state === "current") return "var(--accent)";
      return lineStrong;
    }};
  color: ${({ theme, $state }) =>
    $state === "todo" ? theme.inkMuted || theme.ink : "var(--on-fill)"};
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  transition: all 0.2s;
  box-shadow: ${({ $state }) =>
    $state === "current" ? `0 0 0 4px ${"var(--accent-ring)"}` : "none"};
`;

const StepLine = styled.span`
  width: 26px;
  height: 2px;
  background: ${({ $done }) =>
    $done ? successColor : lineStrong};
  flex-shrink: 0;
  transition: background 0.18s;
  @media (max-width: 768px) {
    width: 14px;
  }
  @media (max-width: 560px) {
    width: 7px;
  }
`;

const Canvas = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const screenIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const Screen = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 36px 48px;
  position: relative;
  min-height: 100%;
  animation: ${screenIn} 0.28s ease both;
  @media (max-width: 768px) {
    padding: 20px 16px 32px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ScreenHead = styled.div`
  margin-bottom: 28px;
  max-width: 640px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  margin-bottom: 10px;
`;

const ScreenTitle = styled.h2`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: clamp(26px, 4.5vw, 36px);
  line-height: 1.1;
  color: ${({ theme }) => theme.ink};
  margin: 0;
  letter-spacing: -0.01em;
`;

const ScreenSub = styled.p`
  font-size: 14.5px;
  color: ${inkSoft};
  margin: 8px 0 0;
  line-height: 1.55;
  max-width: 580px;
`;

/* Setup steps share one layout: the question with Back / Continue on its right, the form below. */
const StepWrap = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  & ${ScreenTitle} {
    font-size: clamp(30px, 2.6vw, 42px);
  }
  & ${ScreenSub} {
    font-size: 15.5px;
  }
`;

const WizardHeadRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px 24px;
  margin-bottom: 32px;
  & > ${ScreenHead} {
    margin-bottom: 0;
  }
`;

const WizardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 560px) {
    width: 100%;
    & > button:last-child {
      flex: 1;
      justify-content: center;
    }
  }
`;

const AccentText = styled.span`
  color: ${accentColor};
  font-style: italic;
`;

/* ─── Brief working screen ───
   Full-viewport interstitial shown while the brief is extracted. The job title is
   the hero; under it a derivation ledger walks the real pipeline order, each line
   carrying a live datum about this job. When the response lands the remaining
   lines complete in a quick cascade and the sheet fades to reveal the review. */

const workingTitleIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

const workingRowIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: none; }
`;

const workingDotBreathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.55); opacity: 0.55; }
`;

const WorkingSheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: var(--bg);
  display: flex;
  justify-content: center;
  padding: 32px 24px;
  overflow-y: auto;
  opacity: ${({ $leaving }) => ($leaving ? 0 : 1)};
  transition: opacity 0.3s ease;
  @media (max-width: 560px) {
    padding: 28px 16px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* margin auto: centered while short, scrolls naturally once people arrive. */
const WorkingCol = styled.div`
  width: min(${({ $wide }) => ($wide ? 1080 : 600)}px, 100%);
  margin: auto 0;
`;

/* Corner escape hatch on the full-screen sheet: quiet text link, not a form control. */
const SheetBack = styled.button`
  position: absolute;
  top: 22px;
  left: 24px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: ${inkSoft};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;
  &:hover {
    color: ${({ theme }) => theme.ink};
    background: ${({ theme }) => theme.line};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
  /* Text lines up with the sheet's narrower phone gutter. */
  @media (max-width: 560px) {
    top: 16px;
    left: 10px;
  }
`;

const SheetAction = styled(SheetBack)`
  left: auto;
  right: 24px;
  padding: 6px 12px 6px 10px;
  border: 1px solid ${({ theme }) => theme.line};
  color: ${({ theme }) => theme.ink};
  @media (max-width: 560px) {
    left: auto;
    right: 16px;
  }
`;

const WorkingEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: ${inkSoft};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  margin-bottom: 14px;
`;

const WorkingTitle = styled.h2`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: clamp(32px, 6vw, 54px);
  line-height: 1.06;
  letter-spacing: -0.012em;
  color: ${({ theme }) => theme.ink};
  margin: 0 0 26px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  animation: ${workingTitleIn} 0.5s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const WorkingLedger = styled.div`
  border-top: 1px solid ${({ theme }) => theme.line};
  padding-top: 10px;
`;

/* The rail's middle: only this part scrolls, so the title stays put and the composer
   stays reachable however long the assistant's trail gets. */
const RailBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 6px;
  scrollbar-width: thin;
`;

/* The setup sheet: the assistant on the left, the recruiter's work on the right.
   Both columns scroll on their own so neither pushes the other off the screen. */
const SetupGrid = styled.div`
  width: min(1360px, 100%);
  /* Clears the Back link in the sheet's corner; the grid is full height below it. */
  margin: 36px 0 0;
  display: grid;
  grid-template-columns: minmax(320px, 400px) minmax(0, 1fr);
  gap: 56px;
  height: calc(100vh - 100px);
  height: calc(100dvh - 100px);
  min-height: 0;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    height: auto;
    gap: 28px;
  }
`;

const SetupRail = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  > *:not(${RailBody}) {
    flex-shrink: 0;
  }
`;

const SetupMain = styled.div`
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  padding: 6px 6px 32px 40px;
  border-left: 1px solid ${({ theme }) => theme.line};
  @media (max-width: 960px) {
    overflow: visible;
    border-left: 0;
    padding-left: 0;
  }
`;

const MainHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const MainTitle = styled.h3`
  margin: 0;
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.005em;
  color: ${({ theme }) => theme.ink};
`;

/* Before the assistant hands over, the work side says what it is waiting for. */
const MainEmptyActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const MainEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 240px;
  max-width: 40ch;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.5;
  color: ${inkSoft};
  svg {
    flex-shrink: 0;
    opacity: 0.6;
  }
`;

/* Each row keeps its slot from the start so the ledger never reflows; a row is
   invisible until reached, then cadences in. */
const WorkingRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  gap: 12px;
  align-items: baseline;
  padding: 9px 0;
  visibility: ${({ $state }) => ($state === "pending" ? "hidden" : "visible")};
  animation: ${({ $state }) => ($state === "pending" ? "none" : workingRowIn)}
    0.3s ease both;
  /* Phone: a 200px detail column left the label ~80px to wrap in; the detail goes under it. */
  @media (max-width: 560px) {
    grid-template-columns: 20px minmax(0, 1fr);
    row-gap: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* The thread: each dot cell draws a hairline down to the next row. */
const WorkingDotCell = styled.span`
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  ${({ $last, theme }) =>
    !$last &&
    `&::after {
      content: "";
      position: absolute;
      left: 50%;
      top: calc(50% + 9px);
      bottom: -11px;
      width: 1px;
      background: ${theme.line};
    }`}
  /* Phone: spans the label and the detail beneath it; the mark stays centred on the label line. */
  @media (max-width: 560px) {
    grid-row: span 2;
    display: grid;
    grid-template-rows: 20px;
    align-content: start;
    place-items: center;
    &::after {
      top: 19px;
    }
  }
`;

const WorkingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: ${({ theme, $state }) =>
    $state === "active" ? theme.ink : "transparent"};
  border: 1.5px solid
    ${({ theme, $state }) => ($state === "active" ? theme.ink : theme.line)};
  animation: ${({ $state }) =>
    $state === "active" ? workingDotBreathe : "none"}
    1.3s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const WorkingCheck = styled.span`
  color: ${({ theme }) => theme.ink};
  display: grid;
  place-items: center;
`;

const WorkingLabel = styled.span`
  font-size: 14.5px;
  color: ${({ theme, $state }) =>
    $state === "active" ? theme.ink : inkSoft};
  font-weight: ${({ $state }) => ($state === "active" ? 600 : 400)};
`;

const WorkingDetail = styled.span`
  font-size: 12.5px;
  color: ${inkSoft};
  font-variant-numeric: tabular-nums;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 560px) {
    max-width: none;
    white-space: normal;
  }
`;

const WorkingNote = styled.p`
  margin: 22px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${inkSoft};
`;

/* Assistant step lines carry long filter summaries: one line, ellipsized. */
const WorkingLineLabel = styled(WorkingLabel)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* ─── The assistant transcript on the setup sheet ─── */
const Transcript = styled.div`
  display: grid;
  gap: 14px;
  padding: 6px 0 4px;
  & + & {
    padding-top: 0;
  }
`;

const ChatUser = styled.div`
  justify-self: end;
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 14px 14px 4px 14px;
  background: ${accentSoft};
  color: ${({ theme }) => theme.ink};
  font-size: 13.5px;
  line-height: 1.45;
`;

const AgentBlock = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
`;

/* One live line for the whole flow: the current step while it runs, a fold once done. */
const FlowLine = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  border: 0;
  background: transparent;
  padding: 2px 0;
  font: inherit;
  font-size: 12.5px;
  color: ${inkSoft};
  text-align: left;
  cursor: ${({ $expandable }) => ($expandable ? "pointer" : "default")};
  .text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chev {
    flex-shrink: 0;
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }
  &:hover .text {
    color: ${({ theme, $expandable }) => ($expandable ? theme.ink : inkSoft)};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FlowTick = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  color: ${accentColor};
`;

const FlowSteps = styled.ol`
  list-style: none;
  margin: 0 0 0 5px;
  padding: 2px 0 2px 14px;
  border-left: 1px solid ${({ theme }) => theme.line};
  display: grid;
  gap: 5px;
`;

const FlowStep = styled.li`
  font-size: 12.5px;
  line-height: 1.4;
  color: ${({ theme, $state }) =>
    $state === "failed" ? dangerColor : $state === "running" ? theme.ink : inkSoft};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* One assistant turn: the flow line (expandable), then the reply. */
function AgentTurn({ turn }) {
  const [open, setOpen] = React.useState(false);
  const steps = turn.steps || [];
  const running = !turn.done;
  const last = steps[steps.length - 1];
  const line = running
    ? last
      ? `${last.label}${last.args ? ` · ${last.args}` : ""}`
      : "Reading your search"
    : steps.length
      ? `${steps.length} step${steps.length === 1 ? "" : "s"}${open ? "" : " · show"}`
      : "";
  const expandable = steps.length > 0;
  return (
    <AgentBlock>
      {(running || expandable) && (
        <FlowLine
          type="button"
          $expandable={expandable}
          $open={open}
          aria-expanded={expandable ? open : undefined}
          onClick={() => expandable && setOpen((v) => !v)}
        >
          {running ? (
            <FlowSpinner aria-hidden />
          ) : (
            <FlowTick aria-hidden>
              <FiCheck size={12} />
            </FlowTick>
          )}
          <span className="text">{line}</span>
          {expandable && <FiChevronDown size={13} className="chev" aria-hidden />}
        </FlowLine>
      )}
      {open && expandable && (
        <FlowSteps>
          {steps.map((st) => (
            <FlowStep key={st.id} $state={st.state} title={st.args || undefined}>
              {st.label}
              {st.args ? ` · ${st.args}` : ""}
              {st.state === "failed" ? " — didn't work" : ""}
            </FlowStep>
          ))}
        </FlowSteps>
      )}
      {turn.reply && (
        <WorkReplyCard>
          <WorkReply>{turn.reply}</WorkReply>
        </WorkReplyCard>
      )}
    </AgentBlock>
  );
}

/* The assistant's recommendation, people, and chat — the sheet's second act. */
const WorkReplyCard = styled.div`
  padding: 14px 16px;
  border: 1px solid ${lineSoft};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
`;

const WorkReply = styled.p`
  margin: 0;
  font-family: ${SERIF};
  font-size: 16.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.ink};
`;

/* The assistant's suggested edits, one bubble each; a click sends it as a message. */
const SuggestRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 4px 30px;
`;

const SuggestBubble = styled.button`
  border: 1px solid ${accentSoftBorder};
  background: ${accentSoft};
  color: ${accentInk};
  border-radius: 99px;
  padding: 5px 11px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  &:hover:not(:disabled) {
    border-color: ${accentColor};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const WorkComposer = styled.form`
  flex-shrink: 0;
  margin-top: 22px;
  display: flex;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.line};
  padding-top: 18px;
`;

const WorkInput = styled.input`
  flex: 1;
  min-width: 0;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  padding: 10px 13px;
  font: inherit;
  font-size: 13.5px;
  color: ${({ theme }) => theme.ink};
  &::placeholder {
    color: ${inkSoft};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.ink};
  }
  &:disabled {
    opacity: 0.55;
  }
`;

/* The editor's rows on the sheet: same chips, no panel chrome around them. */
const WorkFilters = styled.div`
  margin-top: 8px;
`;

const WorkFilterNote = styled.p`
  margin: 6px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${inkSoft};
`;

/* Cadence while the request is in flight; the last line holds until it lands. */
const WORKING_STEP_MS = 1250;
/* Cascade once the response is in: the ledger finishes fast, then hands off. */
const WORKING_SETTLE_MS = 130;

function BriefWorkingScreen({
  jobTitle,
  country,
  worldwide,
  descChars,
  briefSettled,
  agentDone,
  result,
  /* The conversation: user lines and assistant turns, each turn carrying its steps
     and its reply. Rendered like a chat, with the steps folded to one live line. */
  turns = [],
  busy = false,
  onChat,
  filters,
  launchError,
  launching,
  onLaunch,
  onBack,
  /* Opens the job posting this search is set up from, in a modal over the sheet. */
  onSeeJob,
  /* Re-run the brief extraction after it failed (timeout, no credits, bad read). */
  onRetry,
  /* A past search's filters arrive already drafted: no setup phases to play. */
  seeded = false,
}) {
  const phases = React.useMemo(
    () => [
      {
        label: "Reading the job post",
        detail: descChars > 0 ? `${descChars.toLocaleString()} characters` : "",
      },
      { label: "Naming the role", detail: jobTitle || "from your description" },
      {
        label: "Scoping the market",
        detail: worldwide ? "Worldwide" : country || "Worldwide",
      },
      { label: "Sorting must-haves from nice-to-haves", detail: "" },
      { label: "Drafting filters you can edit", detail: "" },
    ],
    [jobTitle, country, worldwide, descChars],
  );
  // Rows before `step` are done, `step` itself is active, the rest pending.
  const [step, setStep] = React.useState(seeded ? phases.length : 0);
  const [chatText, setChatText] = React.useState("");
  const bodyRef = React.useRef(null);

  // New lines land at the bottom of the scroll region, so keep it in view.
  React.useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [step, turns, agentDone, result]);

  // Cadence while the brief extracts; the last line holds until it lands.
  React.useEffect(() => {
    if (briefSettled) return undefined;
    const t = setInterval(
      () => setStep((s) => Math.min(s + 1, phases.length - 1)),
      WORKING_STEP_MS,
    );
    return () => clearInterval(t);
  }, [briefSettled, phases.length]);

  // Brief landed: finish its rows in a quick cascade, then the assistant takes over.
  React.useEffect(() => {
    if (!briefSettled || step >= phases.length) return undefined;
    const t = setTimeout(() => setStep((s) => s + 1), WORKING_SETTLE_MS);
    return () => clearTimeout(t);
  }, [briefSettled, step, phases.length]);

  const agentPhase = step >= phases.length;
  /* The brief extraction reads as the first assistant turn: the same folded flow line
     as every later one, cadenced through the phases while the request is in flight. */
  const setupTurn = React.useMemo(
    () => ({
      id: "setup",
      role: "assistant",
      kind: "setup",
      done: agentPhase,
      steps: phases
        .map((p, i) => ({
          id: p.label,
          label: p.label,
          args: p.detail,
          state: i < step ? "done" : "running",
        }))
        .slice(0, Math.min(step + 1, phases.length)),
    }),
    [phases, step, agentPhase],
  );
  const working = !briefSettled || !agentDone || busy;
  // The assistant has handed over and is idle: the last line says whose move it is.
  const waiting = agentDone && !!filters && !busy;
  const suggestions = (agentDone && result?.suggestions) || [];
  const submitChat = (e) => {
    e.preventDefault();
    const text = chatText.trim();
    if (!text || working) return;
    setChatText("");
    onChat?.(text);
  };

  return (
    <WorkingSheet>
      <PageScrollLock />
      {onBack && (
        <SheetBack type="button" onClick={onBack} data-tour="fc-brief-back">
          <FiArrowLeft size={15} />
          Back
        </SheetBack>
      )}
      {onSeeJob && (
        <SheetAction type="button" onClick={onSeeJob}>
          <FiFileText size={14} />
          See job posting
        </SheetAction>
      )}
      <SetupGrid>
        <SetupRail data-tour="fc-brief">
        <WorkingEyebrow>{seeded ? "Adjusting your search" : "Setting up your search"}</WorkingEyebrow>
        <WorkingTitle>{jobTitle || "Your ideal candidate"}</WorkingTitle>
        <RailBody ref={bodyRef}>
          <WorkingLedger role="status" aria-live="polite">
            {!seeded && (
              <Transcript>
                <AgentTurn turn={setupTurn} />
              </Transcript>
            )}
            {agentPhase && (
              <>
                <Transcript>
                  {turns.map((t) =>
                    t.role === "user" ? (
                      <ChatUser key={t.id}>{t.text}</ChatUser>
                    ) : (
                      <AgentTurn key={t.id} turn={t} />
                    ),
                  )}
                </Transcript>
                {waiting && (
                  <WorkingRow $state="active">
                    <WorkingDotCell $last>
                      <WorkingDot $state="active" />
                    </WorkingDotCell>
                    <WorkingLabel $state="active">
                      {launching ? "Checking the search" : "Your move"}
                    </WorkingLabel>
                    <WorkingDetail>
                      {launching
                        ? "one moment…"
                        : "edit the search, ask for a change, or find candidates"}
                    </WorkingDetail>
                  </WorkingRow>
                )}
                {waiting && suggestions.length > 0 && (
                  <SuggestRow>
                    {suggestions.map((text) => (
                      <SuggestBubble
                        key={text}
                        type="button"
                        disabled={working}
                        onClick={() => onChat?.(text)}
                      >
                        {text}
                      </SuggestBubble>
                    ))}
                  </SuggestRow>
                )}
              </>
            )}
          </WorkingLedger>
        </RailBody>

        <WorkComposer onSubmit={submitChat}>
          <WorkInput
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            placeholder={
              working
                ? "The assistant is working…"
                : "Ask for a change — e.g. more senior, add fintech experience"
            }
            disabled={working}
          />
          <Button type="submit" disabled={working || !chatText.trim()}>
            Send
          </Button>
        </WorkComposer>
        </SetupRail>

        <SetupMain>
          {agentDone && filters ? (
            <>
              <MainHead>
                <div>
                  <MainTitle>Your search</MainTitle>
                  <WorkFilterNote>
                    Edit anything here, or tell the assistant what to change.
                  </WorkFilterNote>
                </div>
                <Button
                  $accent
                  $lg
                  type="button"
                  onClick={onLaunch}
                  data-tour="fc-launch"
                  disabled={working || launching || !filters}
                >
                  {launching ? (
                    <>
                      <BtnSpinner /> Checking…
                    </>
                  ) : (
                    <>
                      <Spark size={14} /> Find candidates
                    </>
                  )}
                </Button>
              </MainHead>
              {launchError && (
                <StripeNotice $kind="danger" style={{ marginBottom: 14 }}>
                  {launchError}
                </StripeNotice>
              )}
              <WorkFilters data-tour="fc-filters">{filters}</WorkFilters>
            </>
          ) : briefSettled && launchError ? (
            <MainEmpty>
              <StripeNotice $kind="danger" style={{ textAlign: "left" }}>
                {launchError}
              </StripeNotice>
              <MainEmptyActions>
                {onRetry && (
                  <Button $accent type="button" onClick={onRetry}>
                    Try again
                  </Button>
                )}
                {onBack && (
                  <Button type="button" onClick={onBack}>
                    Back
                  </Button>
                )}
              </MainEmptyActions>
            </MainEmpty>
          ) : (
            <MainEmpty>
              <Spark size={18} />
              <span>
                Reading the job post…
              </span>
            </MainEmpty>
          )}
        </SetupMain>
      </SetupGrid>
    </WorkingSheet>
  );
}

/* ─── Buttons ─── */
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: ${({ $lg, $sm }) => ($lg ? "38px" : $sm ? "28px" : "32px")};
  padding: ${({ $lg, $sm }) => ($lg ? "0 16px" : $sm ? "0 10px" : "0 12px")};
  /* A 28px control is a poor thumb target; grow them where there's no cursor. */
  @media (pointer: coarse) {
    height: ${({ $lg, $sm }) => ($lg ? "44px" : $sm ? "34px" : "40px")};
    padding: ${({ $lg, $sm }) => ($lg ? "0 18px" : $sm ? "0 12px" : "0 14px")};
  }
  /* App.js's 44px phone min-height would undo the 34px small size: wrapped bars of them get slabby. */
  ${({ $sm }) => $sm && `@media (max-width: 450px) { min-height: 0; }`}
  border-radius: ${({ $lg, $sm }) => ($lg ? "10px" : $sm ? "7px" : "8px")};
  border: 1px solid
    ${({ $primary, $accent, $danger, $ghost }) => {
      if ($danger) return dangerColor;
      if ($primary || $accent) return "var(--accent)";
      if ($ghost) return "transparent";
      return "var(--line)";
    }};
  background: ${({ $primary, $accent, $danger, $ghost }) => {
    if ($danger) return dangerColor;
    if ($primary || $accent) return "var(--accent)";
    if ($ghost) return "transparent";
    return "var(--surface)";
  }};
  color: ${({ theme, $primary, $accent, $danger }) => {
    if ($primary || $accent || $danger) return "var(--on-fill)";
    return theme.ink;
  }};
  font: inherit;
  font-weight: 500;
  font-size: ${({ $lg, $sm }) => ($lg ? "14px" : $sm ? "12.5px" : "13px")};
  cursor: pointer;
  transition:
    background 0.12s ease,
    border-color 0.12s ease,
    color 0.12s ease,
    transform 0.06s ease;
  white-space: nowrap;
  &:hover {
    background: ${({ $primary, $accent, $danger, $ghost }) => {
      if ($danger) return "var(--danger-hover)";
      if ($primary || $accent) return "var(--accent-hover)";
      if ($ghost) return bg2;
      return surfaceTint;
    }};
    border-color: ${({ $primary, $accent, $danger, $ghost }) => {
      if ($danger) return "var(--danger-hover)";
      if ($primary || $accent) return "var(--accent-hover)";
      if ($ghost) return "transparent";
      return lineStrong;
    }};
  }
  &:active {
    transform: translateY(0.5px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const btnSpinKf = keyframes`
  to { transform: rotate(360deg); }
`;

// Small inline spinner for buttons that do async work before navigating/showing results.
const BtnSpinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 99px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  opacity: 0.85;
  display: inline-block;
  animation: ${btnSpinKf} 0.8s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FlowSpinner = styled.span`
  flex-shrink: 0;
  width: 11px;
  height: 11px;
  border-radius: 99px;
  border: 1.5px solid currentColor;
  border-top-color: transparent;
  animation: ${btnSpinKf} 0.8s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ─── Section heading with line ─── */
const SectionH = styled.div`
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  font-weight: 600;
  margin: 22px 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  /* Phone: the results heading carries Select, Filters and a filter summary; let it wrap. */
  @media (max-width: 560px) {
    flex-wrap: wrap;
    row-gap: 4px;
  }
`;

const SectionHLine = styled.span`
  flex: 1;
  height: 1px;
  background: ${({ theme }) => theme.line};
`;

/* ─── Landing hero ─── */
const LandingHero = styled.div`
  background: linear-gradient(180deg, var(--surface), var(--bg));
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 18px;
  padding: 36px 40px 28px;
  margin-bottom: 28px;
  position: relative;
  overflow: hidden;
  @media (max-width: 768px) {
    padding: 24px 20px 20px;
    margin-bottom: 20px;
  }
  &::before {
    content: "";
    position: absolute;
    top: -80px;
    right: -80px;
    width: 280px;
    height: 280px;
    background: radial-gradient(
      circle,
      var(--accent-ring),
      transparent 70%
    );
    pointer-events: none;
  }
  & > * {
    position: relative;
  }
`;

const LandingEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${accentSoft};
  color: ${accentInk};
  padding: 5px 12px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const LandingTitle = styled.h1`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: clamp(34px, 6vw, 60px);
  line-height: 1.04;
  color: ${({ theme }) => theme.ink};
  margin: 18px 0 14px;
  letter-spacing: -0.015em;
  max-width: 820px;
`;

const LandingSub = styled.p`
  font-size: 16px;
  color: ${inkSoft};
  line-height: 1.55;
  margin: 0;
  max-width: 580px;
`;

const LandingActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

// Beside a disabled "Search in progress…": why it's disabled, and the plan that lifts it.
const OneSearchNoteWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  font-size: 13px;
  ${({ $below }) => $below && "margin-top: 12px;"}
  color: var(--ink-muted);
  button {
    appearance: none;
    border: 0;
    padding: 0;
    background: none;
    font: inherit;
    font-weight: 600;
    color: var(--secondary);
    cursor: pointer;
  }
  button:hover {
    text-decoration: underline;
  }
`;
function OneSearchNote({ onUpgrade, $below }) {
  return (
    <OneSearchNoteWrap $below={$below}>
      <span>Your plan runs one search at a time.</span>{" "}
      <button type="button" onClick={onUpgrade}>
        Upgrade to Growth to run two
      </button>
    </OneSearchNoteWrap>
  );
}

const LandingGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 28px;
  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const LandingSection = styled.div`
  min-width: 0;
  & ${SectionH} {
    margin-top: 0;
  }
`;

/* ─── Past searches as cards ─── */
const PastGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const PastCard = styled.button`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  padding: 16px 18px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.12s,
    box-shadow 0.12s,
    transform 0.06s;
  font: inherit;
  color: inherit;
  position: relative;
  overflow: hidden;
  &:hover {
    border-color: ${lineStrong};
    box-shadow: ${shadowMd};
  }
  &:active {
    transform: translateY(0.5px);
  }
`;

const PastCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const PastCardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  line-height: 1.2;
`;

const PastCardCompany = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  margin-top: 3px;
`;

const PastCardStats = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${lineSoft};
`;

const PastStatBig = styled.span`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 22px;
  line-height: 1;
  color: ${({ theme }) => theme.ink};
  margin-right: 4px;
  font-variant-numeric: tabular-nums;
`;

const PastStatLbl = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.005em;
  background: ${({ $kind }) => {
    if ($kind === "complete") return successSoft;
    if ($kind === "draft") return warnSoft;
    if ($kind === "danger") return dangerSoft;
    return bg2;
  }};
  color: ${({ theme, $kind }) => {
    if ($kind === "complete") return successColor;
    if ($kind === "draft") return warnColor;
    if ($kind === "danger") return dangerColor;
    return theme.inkMuted || theme.ink;
  }};
  border: 1px solid transparent;
`;

/* ─── Quick start cards ─── */
const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const QuickCard = styled.button`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
  font: inherit;
  color: inherit;
  &:hover:not(:disabled) {
    border-color: ${lineStrong};
    box-shadow: ${shadowSm};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const QuickIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  margin-bottom: 10px;
  background: ${({ $bg }) => $bg || accentSoft};
  color: ${({ $fg }) => $fg || accentInk};
`;

const QuickTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  line-height: 1.25;
`;

const QuickSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  margin-top: 3px;
`;

/* Step 1 of a new search: one full-width row per way to start. */
const SOURCE_MODES = [
  {
    mode: "manual",
    Icon: FiFileText,
    name: "Job description",
    blurb: "Paste a JD from anywhere and we'll turn it into a search.",
  },
  {
    mode: "people",
    Icon: FiUser,
    name: "Describe a person",
    blurb: "No JD? Say who you need in plain English.",
  },
  {
    mode: "board",
    Icon: FiBriefcase,
    name: "Your job board",
    blurb: "Start from a role you're already tracking.",
  },
];

const ModeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ModeChevron = styled.span`
  color: var(--ink-faint);
  display: grid;
  place-items: center;
  transition: color 0.12s, transform 0.12s;
`;

const ModeRow = styled.button`
  display: grid;
  min-height: clamp(104px, 16vh, 156px);
  grid-template-columns: 60px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding: 24px 32px;
  border-radius: 14px;
  border: 1px solid ${({ $current }) => ($current ? "var(--line-strong)" : "var(--line)")};
  background: var(--surface);
  box-shadow: var(--shadow-sm);
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
  &:hover {
    border-color: var(--accent);
    background: var(--surface-tint);
    box-shadow: var(--shadow-md);
  }
  &:hover ${ModeChevron} {
    color: var(--accent);
    transform: translateX(2px);
  }
  ${({ $locked }) =>
    $locked &&
    css`
      box-shadow: none;
      background: var(--bg-2);
      & ${ModeIcon} {
        background: var(--line-soft);
        color: var(--ink-muted);
      }
      & ${ModeName} {
        color: var(--ink-soft);
      }
      &:hover ${ModeChevron} {
        transform: none;
      }
    `}
  &:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-ring);
  }
  @media (max-width: 560px) {
    min-height: 88px;
    grid-template-columns: 44px minmax(0, 1fr) 18px;
    gap: 14px;
    padding: 16px;
  }
`;

const ModeIcon = styled.span`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);
  & svg {
    width: 26px;
    height: 26px;
  }
  @media (max-width: 560px) {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    & svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ModeName = styled.span`
  display: block;
  font-size: 20px;
  font-weight: 600;
  color: var(--ink);
  @media (max-width: 560px) {
    font-size: 16px;
  }
`;

const ModeBlurb = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 14.5px;
  line-height: 1.45;
  color: var(--ink-soft);
  @media (max-width: 560px) {
    font-size: 13.5px;
  }
`;

/* ─── Form fields ─── */
const FormBlock = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 14px;
  padding: 22px 24px 24px;
  @media (max-width: 560px) {
    padding: 16px 14px 18px;
  }
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const FormLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.ink};
  font-weight: 600;
  margin-bottom: 8px;
  white-space: nowrap;
  & > svg {
    flex-shrink: 0;
  }
`;

/* iOS Safari zooms the viewport when a focused field is under 16px, and never zooms
   back out. Every text field on this page opts up to 16px on touch for that reason. */
const noZoomOnFocus = `
  @media (pointer: coarse) {
    font-size: 16px;
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${inputBg};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13.5px;
  ${noZoomOnFocus}
  @media (pointer: coarse) {
    height: 44px;
  }
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
  outline: none;
  &::placeholder {
    color: ${inkFaint};
  }
  &:focus {
    border-color: ${accentColor};
    box-shadow: 0 0 0 3px var(--accent-ring);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${inputBg};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13.5px;
  ${noZoomOnFocus}
  line-height: 1.5;
  resize: vertical;
  min-height: 200px;
  @media (max-width: 560px) {
    min-height: 150px;
  }
  outline: none;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
  &::placeholder {
    color: ${inkFaint};
  }
  &:focus {
    border-color: ${accentColor};
    box-shadow: 0 0 0 3px var(--accent-ring);
  }
`;

const AiCallout = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${accentSoft};
  border: 1px solid ${accentSoftBorder};
  color: ${accentInk};
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 12.5px;
  line-height: 1.5;
  & svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
  & b {
    font-weight: 600;
  }
`;

/* ─── Country dropdown (used by CountryDropdown component) ─── */
const CountryDropdownWrap = styled.div`
  position: relative;
`;

/* The picked countries sit in their own row above a one-line search bar that never
   moves, so adding the fifth country is as easy as the first. */
const FieldAnchor = styled.div`
  position: relative;
`;

const TokenField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 12px;
  box-sizing: border-box;
  background: ${inputBg};
  border: 1px solid ${({ theme, $open }) => ($open ? accentColor : theme.line)};
  border-radius: 12px;
  cursor: text;
  box-shadow: ${({ $open }) => ($open ? `0 0 0 3px ${accentSoft}` : "none")};
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
  & > svg:first-child {
    color: var(--ink-muted);
    flex-shrink: 0;
    margin-right: 2px;
  }
`;

const TokenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const FieldHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  & > ${FormLabel} {
    margin-bottom: 0;
  }
`;

const TokenClear = styled.button`
  margin-left: auto;
  padding: 0 6px;
  height: 30px;
  border: none;
  background: transparent;
  color: var(--ink-muted);
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    color: ${({ theme }) => theme.ink};
    text-decoration: underline;
  }
`;

const Token = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 4px 0 8px;
  border-radius: 8px;
  background: ${accentSoft};
  border: 1px solid ${accentColor};
  color: ${accentInk};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const TokenRemove = styled.button`
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  opacity: 0.65;
  cursor: pointer;
  &:hover {
    opacity: 1;
    background: ${inputBg};
  }
`;

const TokenInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.ink};
  ${noZoomOnFocus}
  &::placeholder {
    color: var(--ink-muted);
  }
`;

const CountryMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${shadowLg};
  z-index: 30;
  padding: 0.4rem;
`;

const titleSheenKf = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
`;

/* The person-search title while it's being suggested: a spinner and a sheen, read-only until it lands. */
const TitleField = styled.div`
  position: relative;
  & > ${BtnSpinner} {
    position: absolute;
    right: 12px;
    top: 50%;
    margin-top: -7px;
    color: var(--accent);
  }
  ${({ $loading }) =>
    $loading &&
    css`
    & > input {
      padding-right: 38px;
      cursor: progress;
      background-image: linear-gradient(
        90deg,
        transparent 0%,
        color-mix(in srgb, var(--accent) 9%, transparent) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: ${titleSheenKf} 1.4s linear infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      & > input { animation: none; }
    }
  `}
`;

const TextLink = styled.button`
  border: 0;
  background: none;
  padding: 0;
  min-height: 0;
  font: inherit;
  font-weight: 600;
  color: var(--accent);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

/* "Keep it confidential": a full-width checkbox row, orange once ticked. */
const ConfidentialRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${({ $on }) => ($on ? "var(--secondary)" : "var(--line)")};
  background: ${({ $on }) => ($on ? "var(--secondary-soft)" : "transparent")};
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
  &:hover {
    border-color: ${({ $on }) => ($on ? "var(--secondary)" : "var(--line-strong)")};
  }
  & input {
    width: 16px;
    height: 16px;
    min-height: 0;
    margin: 2px 0 0;
    accent-color: var(--secondary);
    cursor: pointer;
  }
  & b {
    display: block;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
  }
  & small {
    display: block;
    margin-top: 2px;
    font-size: 12.5px;
    color: var(--ink-soft);
  }
`;

const FieldHint = styled.div`
  margin: 8px 2px 0;
  font-size: 12.5px;
  color: var(--ink-muted);
`;

const QuickAddLabel = styled(FormLabel)`
  margin-top: 20px;
`;

/* Empty state for the country search. Was <Sub>, a component that has never existed
   in this file — it threw the moment a query matched nothing. */
const CountryEmpty = styled.div`
  margin: 0.3rem 0.4rem;
  font-size: 13px;
  color: var(--ink-muted);
`;

const CountryList = styled.div`
  max-height: 240px;
  overflow: auto;
  display: grid;
  gap: 0.1rem;
`;

const CountryItem = styled.button`
  width: 100%;
  border: none;
  border-radius: 6px;
  background: ${({ $selected }) =>
    $selected ? accentSoft : "transparent"};
  color: ${({ theme }) => theme.ink};
  text-align: left;
  padding: 7px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.1s;
  &:hover {
    background: ${bg2};
  }
`;

/* ─── Country step ─── */
const BriefStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 18px;
  min-width: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:hover:not(:disabled) {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-sm);
  }
  &:disabled {
    cursor: default;
  }
  @media (max-width: 560px) {
    padding: 12px 14px;
    gap: 12px;
  }
`;

const BriefCompany = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BriefMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-muted);
  ${BriefStrip}:hover & {
    color: var(--accent);
  }
`;

const BriefMain = styled.div`
  min-width: 0;
  flex: 1;
`;

const BriefTitle = styled.div`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 20px;
  line-height: 1.15;
  color: ${({ theme }) => theme.ink};
  letter-spacing: -0.005em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Phone: beside the logo and tag one line holds ~12 characters; give it two. */
  @media (max-width: 560px) {
    font-size: 17px;
    line-height: 1.2;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const CountryCard = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  padding: 22px 24px 24px;
  @media (max-width: 560px) {
    padding: 16px 14px 18px;
  }
`;

const PopularGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
`;

const CountryChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px 0 10px;
  border-radius: 10px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? accentColor : theme.line)};
  background: ${({ $active }) =>
    $active ? accentSoft : inputBg};
  color: ${({ theme, $active }) => ($active ? accentInk : theme.ink)};
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    border-color 0.12s,
    background 0.12s,
    box-shadow 0.12s,
    transform 0.06s;
  white-space: nowrap;
  box-shadow: ${({ $active }) =>
    $active ? `0 0 0 3px ${"var(--accent-ring)"}` : "none"};
  &:hover {
    border-color: ${({ $active }) =>
      $active ? accentColor : lineStrong};
    background: ${({ $active }) =>
      $active ? accentSoft : surfaceTint};
  }
  &:active {
    transform: translateY(0.5px);
  }
`;

/* The escape hatch from the country step: an explicit choice, styled as a selectable
   row rather than a link so it reads as a peer of the country picker above it. */
const WorldwideOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 12px;
  padding: 11px 13px;
  text-align: left;
  border-radius: 10px;
  font: inherit;
  cursor: pointer;
  transition:
    border-color 0.12s,
    background 0.12s;
  border: 1px solid
    ${({ $active, theme }) => ($active ? accentColor : theme.line)};
  background: ${({ $active }) =>
    $active ? accentSoft : "transparent"};
  &:hover {
    border-color: ${accentColor};
  }
  &:focus-visible {
    outline: 2px solid ${accentColor};
    outline-offset: 2px;
  }
`;

const WorldwideMark = styled.span`
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 99px;
  flex-shrink: 0;
  background: ${({ $active }) =>
    $active ? accentColor : surfaceTint};
  color: ${({ $active }) => ($active ? "var(--on-fill)" : "var(--ink-muted)")};
  border: 1px solid
    ${({ $active }) => ($active ? accentColor : lineStrong)};
`;

const WorldwideTitle = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
`;

const CountryItemCheck = styled.span`
  margin-left: auto;
  display: inline-flex;
  color: ${accentInk};
`;

/* ─── People-search example chips ─── */
const PEOPLE_EXAMPLES = [
  "Senior restructuring associate at a top law firm, 5+ years of insolvency experience",
  "Compliance officer who has worked at a major bank, strong AML background",
  "Software engineer who has led a small team at a fintech startup",
];

const ExampleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const ExampleLbl = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
`;

const ExampleChip = styled.button`
  border: 1px dashed ${lineStrong};
  background: ${surfaceTint};
  color: ${inkSoft};
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12.5px;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.12s,
    background 0.12s,
    color 0.12s;
  &:hover {
    border-color: ${accentColor};
    background: ${accentSoft};
    color: ${accentInk};
  }
`;

const POPULAR_COUNTRIES = [
  { name: "United States", label: "United States" },
  { name: "United Kingdom", label: "United Kingdom" },
  { name: "United Arab Emirates", label: "UAE" },
  { name: "Saudi Arabia", label: "Saudi Arabia" },
  { name: "Singapore", label: "Singapore" },
  { name: "Hong Kong", label: "Hong Kong" },
  { name: "Australia", label: "Australia" },
  { name: "Germany", label: "Germany" },
  { name: "France", label: "France" },
];

const PreviewEmpty = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px dashed ${lineStrong};
  border-radius: 14px;
  padding: 60px 24px;
  text-align: center;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  position: sticky;
  top: 0;
`;

/* Card-level size (54/15) of the shared empty-state design. */
const PreviewEmptyIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 15px;
  background: linear-gradient(155deg, ${accentSoft}, ${surfaceTint});
  border: 1px solid ${({ theme }) => theme.line};
  display: grid;
  place-items: center;
  margin: 0 auto 14px;
  color: ${({ theme }) => theme.accent};
  box-shadow: ${({ theme }) => `0 6px 16px -8px ${theme.accent}59`};
`;

const PreviewEmptyH = styled.div`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.005em;
  color: ${({ theme }) => theme.ink};
  margin-bottom: 4px;
`;

const PreviewEmptySub = styled.div`
  font-size: 13px;
  line-height: 1.5;
  max-width: 280px;
  margin: 0 auto;
`;

/* ─── Loading skeletons ─── */
const shimmerKf = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// Marked as a loading placeholder so a walkthrough waits for it to fill before it highlights anything.
const Skel = styled.div.attrs({ "data-skeleton": "" })`
  border-radius: ${({ $r }) => $r || "7px"};
  width: ${({ $w }) => $w || "100%"};
  height: ${({ $h }) => $h || "14px"};
  background: linear-gradient(90deg, ${bg2} 25%, ${surfaceTint} 50%, ${bg2} 75%);
  background-size: 200% 100%;
  animation: ${shimmerKf} 1.4s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkelCard = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  padding: 16px 18px;
`;

function PastCardSkeleton() {
  return (
    <SkelCard>
      <Skel $w="72px" $h="20px" $r="99px" style={{ marginLeft: "auto" }} />
      <Skel $w="70%" $h="16px" style={{ marginTop: 12 }} />
      <Skel $w="40%" $h="12px" style={{ marginTop: 8 }} />
      <Skel $w="55%" $h="22px" style={{ marginTop: 20 }} />
    </SkelCard>
  );
}

/* ─── Running screen ─── */
const PulseDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: ${accentColor};
  margin-right: 4px;
  box-shadow: 0 0 0 0 ${accentColor};
  animation: pulseDot 1.4s ease-in-out infinite;
  @keyframes pulseDot {
    0% {
      box-shadow: 0 0 0 0
        color-mix(in srgb, var(--accent) 45%, transparent);
    }
    70% {
      box-shadow: 0 0 0 8px transparent;
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }
`;

/* ─── History (past searches) index ─── */
const HistoryHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 22px;
  gap: 16px 24px;
  flex-wrap: wrap;
`;

// The header's button, with the one-search note under it when a search is running.
const HeadAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const HistoryToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const BmCount = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  font-variant-numeric: tabular-nums;
  margin-left: auto;
  & b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
`;

const HistoryTable = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  overflow: hidden;
`;

const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 100px 110px 1fr 40px;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  /* There is no global border-box reset, so width:100% below would otherwise
     add the padding on top and push the row past the card's clipped edge. */
  box-sizing: border-box;
  text-align: left;
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  cursor: ${({ $head }) => ($head ? "default" : "pointer")};
  border-bottom: 1px solid ${lineSoft};
  /* Children: Role · Matched · Status · spacer · arrow */
  @media (max-width: 820px) {
    grid-template-columns: minmax(0, 2fr) 70px 100px 28px;
    gap: 10px;
    & > *:nth-child(4) {
      display: none;
    }
  }
  /* Phone: the role gets a line of its own, count and status wrap beneath it.
     The arrow goes — the whole row is the tap target anyway. */
  @media (max-width: 560px) {
    display: ${({ $head }) => ($head ? "none" : "flex")};
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    padding: 14px;
    & > *:nth-child(1) {
      flex: 1 1 100%;
    }
    & > *:nth-child(5) {
      display: none;
    }
  }
  width: 100%;
  transition: background 0.12s;
  ${({ $head, theme }) =>
    $head &&
    `
    background: ${surfaceTint};
    font-size: 11px;
    color: ${theme.inkMuted || theme.ink};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
  `}
  &:hover {
    background: ${surfaceTint};
  }
  &:last-child {
    border-bottom: 0;
  }
`;

const HistRoleCell = styled.div`
  min-width: 0;
`;
const HistTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  line-height: 1.2;
  /* A long unbroken role title would otherwise run past the clipped card edge. */
  overflow-wrap: anywhere;
`;
const HistCompany = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  margin-top: 2px;
  overflow-wrap: anywhere;
`;
const HistWhen = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const HistNumCell = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.ink};
  font-variant-numeric: tabular-nums;
  text-align: right;
  & b {
    font-weight: 600;
  }
  /* The "Matched" column heading is gone on phones — say it inline instead. */
  @media (max-width: 560px) {
    text-align: left;
    &::after {
      content: " matched";
      color: var(--ink-muted);
    }
  }
`;
const HistEmpty = styled.div`
  padding: 60px 32px;
  text-align: center;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
`;
/* Accent-tinted tile at the shared 96/24 page-level size — matches the
   Dashboard empty state. */
const HistEmptyArt = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 24px;
  background: linear-gradient(155deg, ${accentSoft}, ${surfaceTint});
  border: 1px solid ${({ theme }) => theme.line};
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.accent};
  margin: 0 auto 18px;
  box-shadow: ${({ theme }) => `0 8px 22px -10px ${theme.accent}59`};
`;
const HistEmptyH = styled.h3`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 26px;
  margin: 0 0 6px;
  color: ${({ theme }) => theme.ink};
`;
const HistEmptyP = styled.p`
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  max-width: 360px;
  margin: 0 auto 18px;
  font-size: 13.5px;
`;
const HistLoadMore = styled.div`
  padding: 0.8rem;
  text-align: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
`;

const ResultsContext = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  @media (max-width: 560px) {
    padding: 16px;
    gap: 14px;
    border-radius: 14px;
    /* The actions block was holding a column, leaving the title ~150px to wrap in.
       Stack instead so the title gets the full width. */
    flex-direction: column;
    align-items: stretch;
  }
`;

const ResultsContextMain = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex: 1;
  min-width: 0;
`;

const ResultsLogo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${inkGradStart}, ${inkGradEnd});
  color: var(--on-fill);
  font-weight: 700;
  font-size: 26px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  @media (max-width: 560px) {
    width: 42px;
    height: 42px;
    font-size: 20px;
    border-radius: 11px;
  }
`;

const ResultsTitle = styled.h2`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: clamp(22px, 4.5vw, 32px);
  line-height: 1.1;
  color: ${({ theme }) => theme.ink};
  letter-spacing: -0.01em;
  margin: 0;
  @media (max-width: 560px) {
    font-size: 20px;
    line-height: 1.2;
  }
`;

const ResultsSub = styled.div`
  font-size: 13.5px;
  color: ${inkSoft};
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 0 6px;
  align-items: center;
  & .sep {
    color: ${inkFaint};
    margin: 0 6px;
  }
  @media (max-width: 560px) {
    font-size: 12.5px;
    gap: 2px 0;
    /* Each half takes its own line; a lone "·" between them reads as a stray bullet. */
    & > span {
      flex: 1 1 100%;
    }
    & .sep {
      display: none;
    }
  }
`;

const ResultsContextActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  @media (max-width: 560px) {
    /* Full-width action under the stacked header rather than a stranded pill. */
    & > * {
      flex: 1;
      justify-content: center;
    }
  }
`;

const AiSummaryCard = styled.div`
  background: ${({ theme }) =>
    `linear-gradient(135deg, ${accentSoft} 0%, ${theme.surface} 60%)`};
  border: 1px solid ${accentSoftBorder};
  border-radius: 16px;
  padding: 22px 24px 24px;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  @media (max-width: 560px) {
    padding: 16px 16px 18px;
    border-radius: 14px;
  }
  &::before {
    content: "";
    position: absolute;
    top: -80px;
    right: -80px;
    width: 260px;
    height: 260px;
    background: radial-gradient(
      circle,
      var(--accent-ring),
      transparent 70%
    );
    pointer-events: none;
  }
`;

const AiSummaryHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  position: relative;
`;

const AiSummaryIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${accentColor};
  color: var(--on-fill);
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const AiSummaryH = styled.div`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 22px;
  line-height: 1.15;
  color: ${({ theme }) => theme.ink};
`;

const AiSummarySub = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  margin-top: 1px;
`;

const AiSummaryBody = styled.div`
  position: relative;
  max-width: 760px;
  font-size: 14.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.ink};
  white-space: pre-wrap;
`;

const ReadMore = styled.button`
  appearance: none;
  border: 0;
  padding: 0;
  margin-top: 8px;
  background: none;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--accent);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

// The summary's first paragraph (the counts) up front; the why behind each level on Read more.
function ReasonText({ text }) {
  const [open, setOpen] = React.useState(false);
  const [lead, ...rest] = String(text || "").trim().split(/\n\s*\n/);
  return (
    <AiSummaryBody>
      {open ? String(text).trim() : lead}
      {rest.length > 0 && (
        <div>
          <ReadMore type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            {open ? "Show less" : "Read more"}
          </ReadMore>
        </div>
      )}
    </AiSummaryBody>
  );
}

/* ─── Ranking table ─── */
const RankingCard = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  overflow: hidden;
`;

const RankingRow = styled.div`
  display: grid;
  /* DOM order is #, person, match, actions, title, company, description; shown as name, company, title, description, match, actions. */
  grid-template-columns: 40px minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 2fr) 116px 196px;
  grid-template-areas: "num person company title desc score actions";
  & > *:nth-child(1) {
    grid-area: num;
  }
  & > *:nth-child(2) {
    grid-area: person;
  }
  & > *:nth-child(3) {
    grid-area: score;
  }
  & > *:nth-child(4) {
    grid-area: actions;
  }
  & > *:nth-child(5) {
    grid-area: title;
  }
  & > *:nth-child(6) {
    grid-area: company;
  }
  & > *:nth-child(7) {
    grid-area: desc;
  }
  /* Top-aligned so every column's first line sits level with the name, however long the description runs. */
  align-items: start;
  gap: 16px;
  padding: 16px 18px;
  & > *:nth-child(n + 5) {
    padding-top: ${({ $head }) => ($head ? 0 : "2px")};
  }
  /* Mid-width: no room for a description column, so it sits under the title instead. */
  @media (max-width: 1200px) {
    grid-template-columns: 40px minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.6fr) 116px 196px;
    grid-template-areas: "num person company title score actions";
    & > *:nth-child(7) {
      display: none;
    }
  }
  /* Narrow: the actions stay; title, company and description move under the name. */
  @media (max-width: 820px) {
    grid-template-columns: 30px minmax(0, 1fr) 96px 186px;
    grid-template-areas: "num person score actions";
    gap: 10px;
    & > *:nth-child(5),
    & > *:nth-child(6),
    & > *:nth-child(7) {
      display: none;
    }
  }
  /* Phone: four columns leave the name ~130px. Stack instead — identity and score
     on the first line, the actions on their own beneath. */
  @media (max-width: 560px) {
    grid-template-columns: 26px minmax(0, 1fr) auto;
    grid-template-areas:
      "num person score"
      "num actions actions";
    row-gap: 10px;
    column-gap: 10px;
    align-items: start;
    padding: 14px 14px;
    /* Column headings describe columns this layout no longer has. */
    display: ${({ $head }) => ($head ? "none" : "grid")};
    & > *:nth-child(1) {
      grid-area: num;
    }
    & > *:nth-child(2) {
      grid-area: person;
    }
    & > *:nth-child(3) {
      grid-area: score;
    }
    & > *:nth-child(4) {
      grid-area: actions;
      justify-content: flex-start;
      /* Line the actions up with the name, not the avatar: avatar (36px) + its gap
         (12px). Gives the stacked row one text edge instead of two. */
      padding-left: 48px;
      padding-top: 0;
    }
  }
  border-bottom: 1px solid ${lineSoft};
  transition: background 0.12s;
  /* Ruled-out rows read as de-emphasised without becoming unreadable. */
  opacity: ${({ $muted }) => ($muted ? 0.66 : 1)};
  cursor: ${({ $clickable, $head }) =>
    $head ? "default" : $clickable ? "pointer" : "default"};
  &:hover {
    background: ${surfaceTint};
  }
  &:last-child {
    border-bottom: 0;
  }
  /* Picked as a starting point for a "find people like these" round. */
  ${({ $picked }) =>
    $picked &&
    `
    background: ${accentSoft};
    box-shadow: inset 3px 0 0 ${accentInk};
    &:hover { background: ${accentSoft}; }
  `}
  ${({ $head, theme }) =>
    $head &&
    `
    background: ${surfaceTint};
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 11px;
    color: ${theme.inkMuted || theme.ink};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
  `}
`;

/* Empty slot where the rank number sits while picking — the row's "not picked" state. */
const PickDot = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1.5px solid ${lineSoft};
`;

const RankNum = styled.div`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 22px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  font-variant-numeric: tabular-nums;
  @media (max-width: 560px) {
    font-size: 15px;
    line-height: 1.6;
  }
`;

const RankPerson = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const RankAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 99px;
  background: ${bg2};
  color: ${inkSoft};
  font-size: 12px;
  font-weight: 600;
  display: grid;
  place-items: center;
  overflow: hidden;
  flex-shrink: 0;
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RankName = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Phone: there's a full line for it, so wrap rather than cut the name short. */
  @media (max-width: 560px) {
    white-space: normal;
    overflow: visible;
    overflow-wrap: anywhere;
    line-height: 1.3;
  }
`;

const RankActionCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Under the name once the title and company columns are hidden: "Senior Associate at Harrow & Lyle".
const RankRole = styled.div`
  display: none;
  @media (max-width: 820px) {
    display: block;
  }
  font-size: 13px;
  line-height: 1.4;
  color: ${inkSoft};
  margin-top: 1px;
  overflow-wrap: anywhere;
  & b {
    font-weight: 500;
    color: ${({ theme }) => theme.ink};
  }
`;

// A title or company in its own column.
const RankCell = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.ink};
  min-width: 0;
  overflow-wrap: anywhere;
`;

// Shows only at the widths given: the description column's text moves under the title, then under the name.
const Between = styled.div`
  display: none;
  margin-top: 4px;
  @media (max-width: ${({ $max }) => $max}px) and (min-width: ${({ $min }) => $min}px) {
    display: block;
  }
`;

// Why they fit; three lines, the rest on hover.
const RankWhy = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  max-width: 72ch;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// ─── Past-search "what we searched for" recap ───
// Reads as a receipt of the query that ran: the brief as written, then one
// hairline row per filter (label left, values right) instead of a grid of
// identically-weighted pills.
const RecapCard = styled.div`
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  border-radius: 14px;
  padding: 16px 20px 14px;
  margin-bottom: 18px;
  box-shadow: ${shadowSm};
  @media (max-width: 560px) {
    padding: 14px 14px 12px;
    margin-bottom: 14px;
  }
`;

// The whole header is the collapse control — a 11px chevron alone is a poor target.
const RecapHeadRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  margin-bottom: ${({ $open }) => ($open ? "12px" : "0")};
  /* Comfortable thumb target without adding chrome around the header. */
  @media (pointer: coarse) {
    min-height: 32px;
  }
  &:focus-visible {
    outline: 2px solid ${accentColor};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const RecapHeadRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const RecapChevron = styled.span`
  width: 8px;
  height: 8px;
  border-right: 1.5px solid var(--ink-muted);
  border-bottom: 1.5px solid var(--ink-muted);
  transform: rotate(${({ $open }) => ($open ? "-135deg" : "45deg")});
  transform-origin: 60% 60%;
  transition: transform 0.18s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const RecapHead = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${inkSoft};
`;

const RecapCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-muted);
  white-space: nowrap;
`;

const RecapRows = styled.div`
  display: flex;
  flex-direction: column;
`;

const RecapRow = styled.div`
  display: grid;
  /* The drawer is 440px regardless of viewport, so it asks for the stacked layout
     directly rather than waiting on a breakpoint that will never fire. */
  grid-template-columns: ${({ $stack }) =>
    $stack ? "minmax(0, 1fr)" : "116px minmax(0, 1fr)"};
  gap: 4px 16px;
  align-items: baseline;
  padding: 9px 0;
  border-top: 1px solid ${lineSoft};
  &:first-child {
    border-top: 0;
    padding-top: 2px;
  }
  &:last-child {
    padding-bottom: 2px;
  }
  @media (max-width: 620px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const RecapLabel = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: var(--ink-muted);
`;

const RecapChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

/* Chip tone carries meaning: accent = the core query terms, success = the
   must-haves candidates were gated on, neutral = everything else. Matches the
   tick chips further down the page so one colour means one thing. */
const RECAP_TONES = {
  accent: {
    bg: "color-mix(in srgb, var(--accent) 10%, transparent)",
    border: "color-mix(in srgb, var(--accent) 26%, transparent)",
    fg: "var(--accent-ink)",
  },
  success: {
    bg: "var(--success-soft)",
    border: "color-mix(in srgb, var(--success) 34%, transparent)",
    fg: "var(--success)",
  },
  danger: {
    bg: "var(--danger-soft)",
    border: "color-mix(in srgb, var(--danger) 34%, transparent)",
    fg: "var(--danger)",
  },
  neutral: {
    bg: "var(--surface-tint)",
    border: "var(--line-strong)",
    fg: "var(--ink-2)",
  },
};

const RecapChip = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.45;
  padding: 3px 10px;
  border-radius: 99px;
  background: ${({ $tone }) => (RECAP_TONES[$tone] || RECAP_TONES.neutral).bg};
  border: 1px solid
    ${({ $tone }) => (RECAP_TONES[$tone] || RECAP_TONES.neutral).border};
  color: ${({ $tone }) => (RECAP_TONES[$tone] || RECAP_TONES.neutral).fg};

  /* A filter the advisor just dropped: struck through and faded, kept in place beside
     whatever replaced it so the edit is legible as a before-and-after rather than a
     value that silently vanished. */
  ${({ $state }) =>
    $state === "removed"
      ? `
    background: transparent;
    border-style: dashed;
    border-color: var(--line-strong);
    color: var(--ink-muted);
    text-decoration: line-through;
    text-decoration-thickness: 1px;
  `
      : ""}
  ${({ $state }) =>
    $state === "added"
      ? `box-shadow: 0 0 0 2px color-mix(in srgb, var(--success) 22%, transparent);`
      : ""}
`;

const RecapMissing = styled.div`
  font-size: 12px;
  color: var(--ink-muted);
  line-height: 1.5;
`;

// Filters the search couldn't apply — flagged, not buried in body text.
const RecapNotApplied = styled(RecapMissing)`
  border-left: 2px solid ${warnColor};
  background: ${warnSoft};
  border-radius: 0 8px 8px 0;
  padding: 8px 12px;
  color: ${({ theme }) => theme.ink};
  strong {
    font-weight: 600;
  }
`;

// ─── Criteria ticks + disqualified presentation ───
const TickRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 5px;
`;

// Hovering (or tapping) a tick chip pops the checklist — the count alone never
// says which. A button so keyboard and touch reach it too.
const TickChip = styled.button`
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 9px;
  line-height: 1.3;
  border-radius: 99px;
  /* App.js sets a 44px min-height on every <button> under 450px for touch targets.
     This one is a label, not a control — that rule turns it into a circle. */
  min-height: 0;
  white-space: nowrap;
  /* Not the row's pointer: clicking the chip does nothing, hovering is the whole point. */
  cursor: default;
  border: 1px solid
    ${({ $kind }) =>
      $kind === "must" ? "var(--success)" : "var(--line-strong)"};
  background: ${({ $kind }) =>
    $kind === "must" ? successSoft : surfaceTint};
  color: ${({ $kind }) => ($kind === "must" ? successColor : inkSoft)};
  &:hover {
    filter: brightness(0.97);
  }
  &:focus-visible {
    outline: 2px solid ${accentColor};
    outline-offset: 2px;
  }
`;

const MatchBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 3px;
`;

const MatchBadge = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  padding: 0 5px;
  line-height: 1.2;
  border-radius: 99px;
  white-space: nowrap;
  background: ${successSoft};
  color: ${successColor};
  border: 1px solid ${successColor};
`;

/* Opacity only. `transform` carries the placement offset from the inline style, and
   an animated transform outranks it — the popover would render un-offset for the
   length of the animation, then jump into position. */
const popIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* Portalled to the body and fixed-positioned: the ranking card clips its overflow,
   so an absolutely-positioned popover would be cut off at the row edges. */
const CriteriaPop = styled.div`
  position: fixed;
  z-index: 60;
  pointer-events: none;
  width: max-content;
  max-width: min(380px, calc(100vw - 24px));
  padding: 12px 14px;
  @media (pointer: coarse) {
    padding: 13px 15px;
  }
  border-radius: 12px;
  border: 1px solid ${lineStrong};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${shadowLg};
  animation: ${popIn} 0.12s ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CriteriaGroupLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 7px;
  @media (pointer: coarse) {
    font-size: 11px;
    margin-bottom: 8px;
  }
`;

const CriteriaLine = styled.div`
  display: grid;
  grid-template-columns: 13px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ $met }) => ($met ? "var(--ink-2)" : "var(--ink-muted)")};
  & + & {
    margin-top: 5px;
  }
  /* Held at arm's length, not read at a cursor. */
  @media (pointer: coarse) {
    font-size: 14px;
    line-height: 1.45;
    & + & {
      margin-top: 6px;
    }
  }
`;

const CriteriaMark = styled.span`
  font-weight: 700;
  line-height: 1.3;
  color: ${({ $met }) => ($met ? "var(--success)" : "var(--ink-faint)")};
`;

// The ✓/✗ marks are decorative; screen readers get the word instead.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const GateReason = styled.div`
  font-size: 11px;
  color: ${dangerColor};
  margin-top: 3px;
  line-height: 1.35;
`;

/* The seniority verdict, shown against the name. Warning rather than danger: being at
   the wrong level is a mismatch, not a rule the person broke. */
const OffTierTag = styled.span`
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  padding: 0 5px;
  line-height: 1.4;
  border-radius: 99px;
  white-space: nowrap;
  margin-top: 3px;
  background: ${warnSoft};
  color: ${warnColor};
  border: 1px solid ${warnColor};
`;

/* The checklist filter sits on the "Full ranking" heading line, after its rule. */
const FilterAnchor = styled.div`
  position: relative;
  text-transform: none;
  letter-spacing: 0;
`;

const FilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid ${({ $on }) => ($on ? accentColor : lineStrong)};
  background: ${({ $on, theme }) => ($on ? accentSoft : theme.surface)};
  color: ${({ $on, theme }) => ($on ? accentInk : theme.ink)};
  border-radius: 9px;
  /* Fixed height so toggling a filter never shifts the heading line; the width
     follows the badge, which only shows while a filter is on. */
  height: 30px;
  padding: 0 11px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  /* App.js's 44px phone min-height made these heading-line chips taller than the cards. */
  @media (max-width: 450px) {
    min-height: 0;
    height: 34px;
  }
  &:hover {
    border-color: ${accentColor};
  }
`;

const FilterCount = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 9px;
  background: ${accentColor};
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  margin-right: -2px;
`;

const FilterSummary = styled.span`
  font-size: 12.5px;
  line-height: 30px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: ${inkSoft};
  b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
  /* Phone: drops below the heading's buttons (with Clear) instead of squeezing between them. */
  @media (max-width: 560px) {
    order: 1;
    flex: 1 1 60%;
    line-height: 1.4;
  }
`;

const FilterClear = styled.button`
  border: 0;
  background: transparent;
  height: 30px;
  padding: 0 6px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: ${inkFaint};
  cursor: pointer;
  @media (max-width: 560px) {
    order: 1;
  }
  @media (max-width: 450px) {
    min-height: 0;
    height: 34px;
  }
  &:hover {
    color: ${dangerColor};
  }
`;

const FilterPop = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  text-align: left;
  z-index: 50;
  width: min(420px, calc(100vw - 48px));
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  box-shadow: ${shadowLg};
  padding: 6px;
`;

const FilterGroupHead = styled.div`
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${inkFaint};
`;

const FilterOption = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
  &:hover {
    background: ${surfaceTint};
  }
`;

const FilterBox = styled.span`
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 4px;
  border: 1px solid ${({ $on }) => ($on ? accentColor : lineStrong)};
  background: ${({ $on }) => ($on ? accentColor : "transparent")};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const FilterEmpty = styled.div`
  padding: 22px 16px;
  font-size: 13px;
  color: ${inkSoft};
  text-align: center;
`;

const DisqualifiedBar = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 16px;
  border: 0;
  border-top: 1px solid ${lineSoft};
  background: ${surfaceTint};
  color: ${inkSoft};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition:
    color 0.12s,
    background 0.12s;
  &:hover {
    color: ${({ theme }) => theme.ink};
    background: ${bg2};
  }
`;

/* Filters nobody set collapse to one row of buttons, so an untouched field costs a
   line rather than a panel. */
const FilterHint = styled.div`
  font-size: 11px;
  color: ${inkFaint};
  margin-top: 8px;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
`;

/* A chip that states a choice rather than holding a value — "Worldwide" for a search
   the user scoped that way. Muted and not clickable, so it can't be mistaken for a
   filter chip you remove. */
const StaticChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* Solid, like every other filled element — dashed is this page's "type here"
     affordance (ChipAddInput), and this chip is a statement, not an input. */
  border: 1px solid ${lineStrong};
  background: ${surfaceTint};
  color: ${inkSoft};
  border-radius: 99px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
  @media (pointer: coarse) {
    padding: 8px 13px;
    font-size: 13px;
  }
`;

/** Best match level in a delivered ranking — "how good were they", not just "how many". */
function topScoreOf(ranking) {
  const levels = Object.values(ranking || {})
    .filter((e) => e?.relevant !== false)
    .map(matchLevelOf);
  if (!levels.length) return "none";
  return levels.reduce((a, b) => (b.rank > a.rank ? b : a)).label;
}

/* --- The search and the checklist, edited in place -----------------------------
   The same bubbles the sheet shows, now removable, with an inline field to add and a
   footer offering every filter not set yet. Edits apply at once; nothing is re-measured
   until the launch counts the search. */

const SearchCard = styled.div`
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 14px;
  background: ${({ theme }) => theme.surface};
  padding: 14px 18px 14px;
`;

const SearchCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  border-bottom: 1px solid ${lineSoft};
`;

const SearchCardName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchWhere = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${inkSoft};
  white-space: nowrap;
  /* Phone: several country chips on one nowrap line ran past the card. */
  @media (max-width: 560px) {
    flex-wrap: wrap;
  }
`;

const SearchRows = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px 28px;
  padding-top: 12px;
`;

const SearchRow = styled.div`
  display: grid;
  gap: 6px;
  align-content: start;
  min-width: 0;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};
`;

const SearchRowLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${inkSoft};
`;

/* Small round × beside a filter's label that drops the whole row — same shape as the
   checklist's remove, quiet until hovered. */
const RowClear = styled.button`
  border: 0;
  background: transparent;
  padding: 3px;
  margin: -3px -3px -3px 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${inkFaint};
  cursor: pointer;
  transition: color 0.12s ease, background 0.12s ease;
  &:hover,
  &:focus-visible {
    color: ${dangerColor};
    background: ${dangerSoft};
    outline: none;
  }
  /* App.js's 44px phone min-height stretched the label line; a bigger pad, cancelled by margin, keeps a tappable target. */
  @media (max-width: 450px) {
    min-height: 0;
    padding: 6px;
    margin: -6px -6px -6px 0;
  }
`;

/* A bubble that can be removed: click it, or its ×. */
const EditChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  border: 1px solid ${accentSoftBorder};
  background: ${accentSoft};
  color: ${accentInk};
  border-radius: 99px;
  padding: 5px 9px 5px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  span {
    opacity: 0.6;
    display: inline-flex;
  }
  &:hover span {
    opacity: 1;
  }
  @media (pointer: coarse) {
    padding: 8px 11px 8px 13px;
    font-size: 13px;
  }
  /* Phone: a long position was clipped at the row edge, × included; wrap it inside the chip. */
  @media (max-width: 560px) {
    white-space: normal;
    overflow-wrap: anywhere;
    text-align: left;
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

/* Stands where the add field was once a list is full, so the field doesn't just vanish. */
const ChipLimitNote = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 4px;
  font-size: 12px;
  color: var(--ink-muted);
`;

/* The dashed "type here" field at the end of a chip row. */
const ChipAddInput = styled.input`
  min-width: 140px;
  flex: 1 1 140px;
  border: 1px dashed ${lineStrong};
  background: ${inputBg};
  color: ${({ theme }) => theme.ink};
  border-radius: 99px;
  padding: 5px 12px;
  font-size: 12.5px;
  ${noZoomOnFocus}
  outline: none;
  &::placeholder {
    color: ${inkFaint};
  }
  &:focus {
    border-style: solid;
    border-color: ${accent2Color};
  }
  /* Same height as the chips beside it, not App.js's 44px. */
  @media (max-width: 450px) {
    min-height: 34px;
  }
`;

const SearchFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid ${lineSoft};
`;

/* "+ Employers": opens a filter row that is not set yet. */
const AddFilter = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px dashed ${lineStrong};
  background: transparent;
  color: ${inkSoft};
  border-radius: 99px;
  padding: 5px 12px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: ${accentColor};
    color: ${accentColor};
  }
  @media (max-width: 450px) {
    min-height: 34px;
  }
`;

/* Company-name autocomplete, the same Brandfetch proxy the Scout "track a company"
   field uses. Returns [{name, domain, icon}] — the domain is what makes an employer
   resolvable by website rather than by fuzzy name matching. */
async function searchCompanies(q) {
  try {
    const res = await fetchWithAuth(
      `/api/companies/search?q=${encodeURIComponent(q)}`,
    );
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/* A removable chip row with an inline add field. With `suggest` it also offers a
   typeahead: picking a row adds its plain name as the chip. Typing freely still works. */
function ChipListEditor({ values, onChange, placeholder, max, suggest }) {
  const [draft, setDraft] = React.useState("");
  const [hits, setHits] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const skipRef = React.useRef(false); // suppress the fetch right after a pick
  const atMax = typeof max === "number" && values.length >= max;

  const add = (name) => {
    const v = (name || "").trim();
    setDraft("");
    if (!v || values.includes(v) || atMax) return false;
    onChange([...values, v]);
    return true;
  };
  const pick = (row) => {
    skipRef.current = true;
    setOpen(false);
    setHits([]);
    add(row.name);
  };

  React.useEffect(() => {
    if (!suggest) return undefined;
    if (skipRef.current) {
      skipRef.current = false;
      return undefined;
    }
    const q = draft.trim();
    if (q.length < 2) {
      setHits([]);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const rows = await suggest(q);
      if (cancelled) return;
      setHits(rows);
      setOpen(rows.length > 0);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [draft, suggest]);

  React.useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <ChipsRow>
        {values.map((v) => (
          <EditChip
            key={v}
            type="button"
            title={`Remove ${v}`}
            onClick={() => onChange(values.filter((x) => x !== v))}
          >
            {v}
            <span aria-hidden>
              <FiX size={12} />
            </span>
          </EditChip>
        ))}
        {atMax ? (
          <ChipLimitNote>{max} max · remove one to add another</ChipLimitNote>
        ) : (
          <ChipAddInput
            value={draft}
            placeholder={placeholder || "Add…"}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => hits.length && setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                add(draft);
              }
            }}
            onBlur={() => add(draft)}
          />
        )}
      </ChipsRow>
      {open && hits.length > 0 && (
        <CountryMenu>
          <CountryList>
            {hits.map((row, i) => (
              <CountryItem
                key={`${row.domain}-${i}`}
                type="button"
                /* Keep focus on the input: a blur here would fire onBlur's add
                   and add the half-typed name alongside the picked one. */
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(row)}
              >
                {row.icon ? (
                  <img
                    src={row.icon}
                    alt=""
                    width={16}
                    height={16}
                    style={{ borderRadius: 3 }}
                  />
                ) : null}
                <span>{row.name}</span>
                <span style={{ marginLeft: "auto", color: "var(--ink-muted)" }}>
                  {row.domain}
                </span>
              </CountryItem>
            ))}
          </CountryList>
        </CountryMenu>
      )}
    </div>
  );
}

/* One company, picked from the same autocomplete the Scout "Find jobs" field uses; free text still counts.
   The menu opens on the first keystroke: earlier answers that still match are shown at once and a
   "Searching…" row stands in while the lookup for the new text runs. */
function CompanyPicker({ value, onChange, placeholder }) {
  const [hits, setHits] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const wrapRef = React.useRef(null);
  const skipRef = React.useRef(false); // no lookup right after a pick
  const seenRef = React.useRef(new Map()); // query -> rows, so a longer query reuses the shorter one's answer

  React.useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return undefined;
    }
    const q = (value || "").trim();
    if (q.length < 2) {
      setHits([]);
      setOpen(false);
      setSearching(false);
      return undefined;
    }
    const lower = q.toLowerCase();
    const cached = seenRef.current.get(lower);
    if (cached) {
      setHits(cached);
      setOpen(cached.length > 0);
      setSearching(false);
      return undefined;
    }
    // Instant guess: rows from the longest shorter query that still match what is typed now.
    let bestKey = "";
    for (const k of seenRef.current.keys()) {
      if (lower.startsWith(k) && k.length > bestKey.length) bestKey = k;
    }
    const guess = bestKey
      ? seenRef.current.get(bestKey).filter((r) => `${r.name} ${r.domain}`.toLowerCase().includes(lower))
      : [];
    setHits(guess);
    setSearching(true);
    setOpen(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      const rows = await searchCompanies(q);
      if (cancelled) return;
      seenRef.current.set(lower, rows);
      setHits(rows);
      setSearching(false);
      setOpen(true);
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value]);

  React.useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => (hits.length || searching) && setOpen(true)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
      />
      {open && (hits.length > 0 || searching) && (
        <CountryMenu>
          <CountryList>
            {hits.map((row, i) => (
              <CountryItem
                key={`${row.domain}-${i}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  skipRef.current = true;
                  setOpen(false);
                  setHits([]);
                  setSearching(false);
                  onChange(row.name);
                }}
              >
                {row.icon ? <img src={row.icon} alt="" width={16} height={16} style={{ borderRadius: 3 }} /> : null}
                <span>{row.name}</span>
                <span style={{ marginLeft: "auto", color: "var(--ink-muted)" }}>{row.domain}</span>
              </CountryItem>
            ))}
            {searching && (
              <CountryItem type="button" disabled onMouseDown={(e) => e.preventDefault()} style={{ color: "var(--ink-muted)" }}>
                <span>Searching…</span>
              </CountryItem>
            )}
          </CountryList>
        </CountryMenu>
      )}
    </div>
  );
}

/* Optional rows beyond positions and keywords; a row shows once it holds a value or is opened. Years, languages and schools are must-haves, not filters. */
const FILTER_ROWS = [
  { key: "employers", label: "Employers", placeholder: "Company name…", suggest: true, max: MAX_EMPLOYERS },
];

/* The plan's name on the card: the positions, then the keywords. */
function planName(view) {
  const pos = view.positions.slice(0, 3).join(", ") + (view.positions.length > 3 ? ` +${view.positions.length - 3}` : "");
  return [pos, view.keywords.join(", ")].filter(Boolean).join(" · ") || "New search";
}

/* The running sheet's two columns: progress on the left, the search it runs on the right. */
const FindingGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $wide }) =>
    $wide ? "minmax(0, 1fr) 360px" : "minmax(0, 1fr)"};
  gap: 64px;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }
  /* Phone: the progress runs taller than the screen, so it starts at the top, under the Back link. */
  @media (max-width: 560px) {
    margin-top: 44px;
  }
`;

const RunFiltersAside = styled.aside`
  border-left: 1px solid ${({ theme }) => theme.line};
  padding-left: 40px;
  animation: ${workingTitleIn} 0.5s ease 0.15s both;
  @media (max-width: 900px) {
    border-left: 0;
    border-top: 1px solid ${({ theme }) => theme.line};
    padding: 24px 0 0;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const RunFiltersBody = styled.div`
  max-height: calc(100vh - 140px);
  max-height: calc(100dvh - 140px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  @media (max-width: 900px) {
    max-height: none;
  }
`;

const FindingLedger = styled(WorkingLedger)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

/* The progress column never outgrows the viewport (clear of the Back link), so the
   sheet itself doesn't scroll: the assistant's feed is the one part that gives. */
const FindingMain = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: calc(100vh - 140px);
  max-height: calc(100dvh - 140px);
  /* The clamped title would otherwise take a share of the squeeze. */
  > *:not(${FindingLedger}) {
    flex-shrink: 0;
  }
  @media (max-width: 900px) {
    max-height: none;
  }
`;

/* Not scrollable by hand: it rides its newest line, and older ones fade out the top. */
const FeedScroll = styled.div`
  min-height: 0;
  overflow: hidden;
  scroll-behavior: smooth;
  ${({ $clipped }) =>
    $clipped &&
    `-webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 44px);
     mask-image: linear-gradient(to bottom, transparent 0, #000 44px);`}
  @media (max-width: 900px) {
    max-height: 260px;
  }
  @media (prefers-reduced-motion: reduce) {
    scroll-behavior: auto;
  }
`;

/* Label and value on one baseline, like the ledger's rows beside it. */
const RunFilterRow = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
  padding: 7px 0;
`;

const RunFilterLabel = styled.span`
  font-size: 12.5px;
  color: ${inkSoft};
`;

const RunFilterValue = styled.div`
  display: grid;
  gap: 3px;
  font-size: 14.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.ink};
  > span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
  }
`;

const RunChecklist = styled.div`
  border-top: 1px solid ${({ theme }) => theme.line};
  margin-top: 14px;
  padding-top: 16px;
`;

const RunChecklistLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${inkSoft};
  margin-bottom: 6px;
  span {
    font-weight: 500;
    color: ${inkFaint};
    margin-left: 4px;
  }
`;

const RunChecklistItem = styled.div`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  font-size: 13.5px;
  line-height: 1.5;
  color: ${({ theme, $must }) => ($must ? theme.ink : inkSoft)};
  padding: 4px 0;
  svg {
    margin-top: 4px;
    color: ${({ theme, $must }) => ($must ? theme.ink : inkFaint)};
  }
`;

/* The filters a run was launched with, as the recruiter confirmed them. */
function RunFiltersPanel({ brief, country, worldwide }) {
  const view = planOf(brief);
  const places = worldwide
    ? []
    : (brief.locations || []).filter(Boolean).length
      ? brief.locations.filter(Boolean)
      : String(country || "").split(",").map((c) => c.trim()).filter(Boolean);
  const rows = [
    { label: "Positions", values: view.positions },
    { label: "Keywords", values: view.keywords },
    { label: "Employers", values: view.employers },
  ].filter((r) => r.values.length > 0);
  const checklists = [
    { label: "Must-haves", must: true, values: brief.must_haves || [] },
    { label: "Nice-to-haves", must: false, values: brief.nice_to_haves || [] },
    { label: "Exclusions", exclude: true, values: brief.exclusions || [] },
  ].filter((c) => c.values.length > 0);
  return (
    <RunFiltersAside aria-label="Your filters">
      <RunFiltersBody>
        <WorkingEyebrow>Your filters</WorkingEyebrow>
        <RunFilterRow>
          <RunFilterLabel>Location</RunFilterLabel>
          <RunFilterValue>
            {places.length === 0 && <span>Worldwide</span>}
            {places.map((place) => {
              const iso = resolveCountryIso2(place);
              return (
                <span key={place}>
                  {iso && (
                    <CircleFlag countryCode={iso.toLowerCase()} height={14} width={14} />
                  )}
                  {place}
                </span>
              );
            })}
          </RunFilterValue>
        </RunFilterRow>
        {rows.map((r) => (
          <RunFilterRow key={r.label}>
            <RunFilterLabel>{r.label}</RunFilterLabel>
            <RunFilterValue>
              {r.values.map((v) => (
                <span key={v}>{v}</span>
              ))}
            </RunFilterValue>
          </RunFilterRow>
        ))}
        {checklists.map((c) => (
          <RunChecklist key={c.label}>
            <RunChecklistLabel>
              {c.label}
              <span>{c.values.length}</span>
            </RunChecklistLabel>
            {c.values.map((v) => (
              <RunChecklistItem key={v} $must={c.must}>
                {c.exclude ? (
                  <FiSlash size={13} aria-hidden />
                ) : c.must ? (
                  <FiCheck size={13} aria-hidden />
                ) : (
                  <FiPlus size={13} aria-hidden />
                )}
                <span>{v}</span>
              </RunChecklistItem>
            ))}
          </RunChecklist>
        ))}
      </RunFiltersBody>
    </RunFiltersAside>
  );
}

const ChecklistHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 28px 0 10px;
`;

const ChecklistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ChecklistCol = styled.div`
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 14px;
  background: ${({ theme }) => theme.surface};
  padding: 12px 14px 8px;
`;

const ChecklistColTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.ink};
  margin-bottom: 4px;
  span {
    font-weight: 500;
    color: ${inkFaint};
  }
`;

const ChecklistRow = styled.div`
  display: ${({ $editable }) => ($editable ? "grid" : "block")};
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.ink};
  padding: 7px 0;
  border-bottom: 1px solid ${lineSoft};
  &:last-child {
    border-bottom: 0;
  }
`;

const ChecklistEmpty = styled.div`
  font-size: 12.5px;
  color: ${inkFaint};
  padding: 6px 0;
`;

const ChecklistRemove = styled.button`
  border: 0;
  background: transparent;
  color: ${inkFaint};
  cursor: pointer;
  padding: 2px;
  margin-top: 1px;
  border-radius: 6px;
  display: inline-flex;
  &:hover {
    color: ${dangerColor};
    background: ${lineSoft};
  }
  /* App.js's 44px phone min-height made every checklist line 44px+; pad and cancel instead. */
  @media (max-width: 450px) {
    min-height: 0;
    padding: 6px;
    margin: -5px -6px -6px 0;
  }
`;

const ChecklistAdd = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  border: 1px dashed ${lineStrong};
  background: ${inputBg};
  color: ${({ theme }) => theme.ink};
  border-radius: 9px;
  padding: 7px 11px;
  font-size: 13px;
  ${noZoomOnFocus}
  outline: none;
  &::placeholder {
    color: ${inkFaint};
  }
  &:focus {
    border-style: solid;
    border-color: ${accent2Color};
  }
`;

/* A checklist column: one line per requirement, remove on the right, add at the foot.
   Requirements are sentences, so they read as a list, never as chips. */
function ChecklistEditor({ values, onChange, placeholder, max }) {
  const [draft, setDraft] = React.useState("");
  const atMax = typeof max === "number" && values.length >= max;
  const add = () => {
    const v = draft.trim();
    setDraft("");
    if (!v || values.includes(v) || atMax) return;
    onChange([...values, v]);
  };
  return (
    <>
      {values.map((v) => (
        <ChecklistRow key={v} $editable>
          <span>{v}</span>
          <ChecklistRemove
            type="button"
            aria-label={`Remove ${v}`}
            title="Remove"
            onClick={() => onChange(values.filter((x) => x !== v))}
          >
            <FiX size={13} />
          </ChecklistRemove>
        </ChecklistRow>
      ))}
      {values.length === 0 && <ChecklistEmpty>Nothing yet.</ChecklistEmpty>}
      {!atMax && (
        <ChecklistAdd
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      )}
    </>
  );
}

const RankScore = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
  min-width: 50px;
  white-space: nowrap;
  /* $rank is the 1-5 match level: High/Very High green, Medium accent, below warn. */
  background: ${({ $rank }) => {
    if ($rank >= 4) return successSoft;
    if ($rank === 3) return accentSoft;
    return warnSoft;
  }};
  color: ${({ $rank }) => {
    if ($rank >= 4) return successColor;
    if ($rank === 3) return accentInk;
    return warnColor;
  }};
`;

/* Two independent actions on a result row, joined into one segmented control:
   "Database" adds the person to your candidate pool (Outreach); "Shortlist"
   marks them chosen for the job this search was run from. Shortlist only
   renders when there IS a job — a description search has nothing to pick for. */
const ActionPills = styled.div`
  display: inline-flex;
  align-items: stretch;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid ${lineSoft};
  background: var(--surface-2);
`;
const ActionPill = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0;
  /* Separator only between pills, so the pair reads as one control. */
  border-left: 1px solid ${lineSoft};
  &:first-child {
    border-left: 0;
  }
  background: ${({ $on }) => ($on ? accentSoft : "transparent")};
  color: ${({ $on }) => ($on ? accentInk : inkSoft)};
  padding: 4px 10px;
  font-size: 10.5px;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: 0.01em;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  /* App.js gives every <button> a 44px min-height below 450px. These are secondary
     row actions inside a bordered group — that turns the pair into a slab. */
  min-height: 0;
  @media (max-width: 560px) {
    padding: 6px 11px;
    font-size: 11px;
  }
  transition:
    background 0.12s,
    color 0.12s;
  &:hover:not(:disabled) {
    color: ${accentInk};
    background: ${accentSoft};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  svg {
    flex-shrink: 0;
  }
`;
/* Labels stay: on phones the pills get their own row, so there is width for words.
   They only drop in the 560–820px band, where they share a line with everything else. */
const PillLabel = styled.span`
  @media (max-width: 820px) and (min-width: 561px) {
    display: none;
  }
`;

/* ─── Batches: earlier rounds of the same search, kept as folders ─── */
const BatchStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 2px 0 18px;
`;

const BatchPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  cursor: ${({ $current }) => ($current ? "default" : "pointer")};
  white-space: nowrap;
  min-height: 0;
  border: 1px solid
    ${({ $current }) =>
      $current ? accentSoftBorder : lineSoft};
  background: ${({ $current }) =>
    $current ? accentSoft : "var(--surface-2)"};
  color: ${({ $current }) =>
    $current ? accentInk : inkSoft};
  transition:
    background 0.12s,
    color 0.12s,
    border-color 0.12s;
  &:hover:not(:disabled) {
    color: ${accentInk};
    border-color: ${accentSoftBorder};
  }
`;

const BatchCount = styled.span`
  opacity: 0.75;
  font-weight: 500;
`;

/* Shared modal/overlay animations. Declared here because the first components to
   use them appear above the profile modal, and `const` is not hoisted. */
const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalCardIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/* ─── "Not seeing the right people?" — the choice modal ─── */
const ChoiceOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 40px;
  animation: ${overlayIn} 0.18s ease both;
  @media (max-width: 768px) {
    padding: 16px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ChoiceCard = styled.div`
  width: min(520px, 100%);
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${shadowLg};
  overflow: hidden;
  animation: ${modalCardIn} 0.22s ease both;
  /* A centred card taller than a phone (or one under the keyboard) had no way to scroll. */
  @media (max-width: 768px) {
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ChoiceHead = styled.div`
  padding: 20px 24px 14px;
  border-bottom: 1px solid ${lineSoft};
  @media (max-width: 560px) {
    padding: 18px 18px 14px;
  }
`;

const ChoiceTitle = styled.h3`
  margin: 0;
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 24px;
  line-height: 1.1;
  color: ${({ theme }) => theme.ink};
`;

const ChoiceSub = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
`;

const ConfirmFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px 18px;
  @media (max-width: 560px) {
    padding: 14px 18px 16px;
  }
`;

const ChoiceOptions = styled.div`
  display: grid;
  gap: 10px;
  padding: 16px 24px 18px;
  @media (max-width: 560px) {
    padding: 14px 18px 16px;
  }
`;

const ChoiceOption = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 13px;
  width: 100%;
  text-align: left;
  padding: 16px 18px;
  border-radius: 14px;
  cursor: pointer;
  font-family: inherit;
  @media (max-width: 560px) {
    padding: 14px;
    gap: 12px;
  }
  color: ${({ theme }) => theme.ink};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  transition:
    border-color 0.12s,
    background 0.12s;
  &:hover:not(:disabled) {
    border-color: ${accentColor};
    background: ${surfaceTint};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ChoiceOptionIcon = styled.div`
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex: none;
  background: ${accentSoft};
  color: ${accentInk};
`;

const ChoiceOptionH = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.ink};
`;

const ChoiceOptionP = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  margin-top: 3px;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
`;

const ChoiceFooter = styled.div`
  padding: 14px 24px;
  @media (max-width: 560px) {
    padding: 12px 18px;
  }
  border-top: 1px solid ${lineSoft};
  background: ${surfaceTint};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

/* ─── Picking mode: sticky bar while the user chooses who to match on ─── */
const PickBar = styled.div`
  position: sticky;
  top: 8px;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 11px 14px;
  border-radius: 12px;
  background: ${accentSoft};
  border: 1px solid ${accentSoftBorder};
  box-shadow: 0 6px 18px -12px rgb(0 0 0 / 0.5);
`;

const PickBarText = styled.div`
  flex: 1;
  min-width: 180px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: ${accentInk};
`;

const PickBarSub = styled.span`
  display: block;
  font-size: 12.5px;
  font-weight: 400;
  opacity: 0.82;
  margin-top: 3px;
`;

/* ─── Run dock ───────────────────────────────────────────────────────────
   A search takes minutes, so it can't own the screen. The dock keeps it present
   and clickable wherever the user wanders, and is the reason every "start a
   search" affordance can simply be disabled instead of explaining itself. */
const dockIn = keyframes`
  from { transform: translateY(130%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

/* The dock sits fixed at the bottom. One run is a single bar; several runs collapse into
   one bar that opens into a list of them. */
const RunDockStack = styled.div`
  position: fixed;
  left: 50%;
  bottom: 22px;
  transform: translateX(-50%);
  z-index: 60;
  width: min(560px, calc(100vw - 32px));
`;

const RunDockList = styled.div`
  margin-top: 8px;
  border-radius: 14px;
  border: 1px solid ${accentSoftBorder};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${shadowLg};
  overflow: hidden;
  animation: ${dockIn} 200ms cubic-bezier(0.2, 0.9, 0.3, 1) both;
`;

const RunDockRow = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
  & + & {
    border-top: 1px solid ${({ theme }) => theme.line};
  }
  &:hover {
    background: ${({ theme }) => theme.bg};
  }
`;

const RunDockBar = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border-radius: 14px;
  cursor: pointer;
  border: 1px solid ${accentSoftBorder};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${shadowLg};
  animation: ${dockIn} 260ms cubic-bezier(0.2, 0.9, 0.3, 1) both;
  transition:
    transform 140ms ease,
    border-color 140ms ease;
  &:hover {
    transform: translateY(-2px);
    border-color: ${accentColor};
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &:hover {
      transform: none;
    }
  }
`;

const RunDockMain = styled.span`
  flex: 1;
  min-width: 0;
`;

const RunDockTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 650;
  color: ${({ theme }) => theme.ink};
  span.name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RunDockSub = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 12px;
  color: ${inkSoft};
  font-variant-numeric: tabular-nums;
`;

const RunDockTrack = styled.span`
  display: block;
  margin-top: 8px;
  height: 3px;
  border-radius: 999px;
  overflow: hidden;
  background: ${lineSoft};
`;

const RunDockFill = styled.span`
  display: block;
  height: 100%;
  border-radius: 999px;
  width: ${({ $pct }) => `${Math.max(4, Math.min(100, $pct))}%`};
  background: linear-gradient(
    90deg,
    ${accentColor},
    ${accent2Color}
  );
  transition: width 400ms ease;
`;

const RunDockCta = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 650;
  color: ${accentColor};
`;

/* ─── Profile modal ─── */
const ProfileModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 40px;
  animation: ${overlayIn} 0.18s ease both;
  @media (max-width: 768px) {
    padding: 16px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProfileModalCard = styled.div`
  max-width: 760px;
  width: 100%;
  max-height: calc(100vh - 80px);
  max-height: calc(100dvh - 80px);
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  display: flex;
  flex-direction: column;
  box-shadow: ${shadowLg};
  animation: ${modalCardIn} 0.22s ease both;
  @media (max-width: 768px) {
    max-height: calc(100dvh - 32px);
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProfileModalHeader = styled.div`
  padding: 18px 22px 14px;
  border-bottom: 1px solid ${lineSoft};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  @media (max-width: 560px) {
    padding: 14px 16px 12px;
    gap: 10px;
  }
`;

const ProfileModalHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const ProfileModalAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 99px;
  flex-shrink: 0;
  overflow: hidden;
  background: ${bg2};
  color: ${inkSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProfileModalNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ProfileModalName = styled.div`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: 22px;
  line-height: 1.15;
  letter-spacing: -0.005em;
  color: ${({ theme }) => theme.ink};
  /* A long name must not push the close button off the sheet. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 560px) {
    font-size: 19px;
  }
`;

const ProfileModalLinkedInButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${PROVIDER_BRAND.linkedin};
  padding: 4px;
  border-radius: 6px;
  flex-shrink: 0;
  &:hover {
    background: color-mix(in srgb, ${PROVIDER_BRAND.linkedin} 12%, transparent);
  }
`;

const JobPreviewCard = styled(JobsVarsScope)`
  width: min(1100px, 100%);
  max-height: calc(100vh - 64px);
  max-height: calc(100dvh - 64px);
  overflow-y: auto;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.line};
  background: var(--bg);
  box-shadow: ${shadowLg};
  animation: ${modalCardIn} 0.22s ease both;
  @media (max-width: 768px) {
    max-height: calc(100dvh - 32px);
  }
`;

const ProfileModalBody = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
  /* Momentum scrolling inside the sheet instead of the page behind it. */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.ink};
  @media (max-width: 560px) {
    padding: 16px;
  }
`;

const ProfileSection = styled.div`
  margin-bottom: 16px;
`;

const ProfileSectionTitle = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  font-weight: 600;
  margin: 6px 0 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${lineSoft};
`;

const ProfileEntryCard = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  background: ${surface2};
`;

const ProfileMetaLine = styled.div`
  color: ${({ theme }) => theme.inkMuted || theme.ink};
  font-size: 12.5px;
`;

const ChoiceBody = styled.div`
  display: grid;
  gap: 16px;
  padding: 18px 24px 6px;
  @media (max-width: 560px) {
    padding: 16px 18px 6px;
  }
`;

const GoalRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  /* Fixed on the free trial: shown, greyed, and not clickable. */
  ${({ $locked }) => $locked && "opacity: .42; filter: grayscale(1); pointer-events: none; user-select: none;"}
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const TrialGoalNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 12.5px;
  color: var(--ink);
  background: var(--secondary-soft);
  svg {
    color: var(--secondary);
    flex-shrink: 0;
  }
`;

const GoalLabel = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
`;

const GoalHint = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: ${inkFaint};
`;

/* A typed value sits in the Custom segment's own slot, same height and about the same
   width as the label it replaces, so the control never grows when it appears. */
const SegmentedInput = styled.input`
  width: 76px;
  box-sizing: border-box;
  border: 0;
  border-radius: 7px;
  padding: 6px 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: ${({ theme }) => theme.ink};
  background: ${({ theme }) => theme.surface};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  ${noZoomOnFocus}
  outline: none;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
  /* Spinners would widen the slot. */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

/* One joined control per choice: the active segment reads as a raised tab. */
const Segmented = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border: 1px solid ${lineStrong};
  border-radius: 10px;
  background: ${surfaceTint};
`;

const SegmentedItem = styled.button`
  min-width: 46px;
  border: 0;
  border-radius: 7px;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.ink : inkSoft)};
  background: ${({ theme, $active }) => ($active ? theme.surface : "transparent")};
  box-shadow: ${({ $active }) => ($active ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none")};
  transition:
    background 0.12s,
    color 0.12s;
  &:hover {
    color: ${({ theme }) => theme.ink};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

const ClampedText = styled.div`
  margin-top: 0.35rem;
  line-height: 1.5;
  white-space: pre-line;
  ${({ $open }) =>
    $open
      ? ""
      : `display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;`}
`;

const ClampToggle = styled.button`
  border: 0;
  background: transparent;
  padding: 2px 0;
  margin-top: 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${accentColor};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

/* A role description, collapsed to three lines until asked for. The toggle only appears
   when the text actually overflows, measured after paint. */
function ExpandableText({ text }) {
  const [open, setOpen] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);
  if (!text) return null;
  return (
    <>
      <ClampedText ref={ref} $open={open}>
        {text}
      </ClampedText>
      {(overflows || open) && (
        <ClampToggle type="button" onClick={() => setOpen((v) => !v)}>
          {open ? "Show less" : "Show more"}
        </ClampToggle>
      )}
    </>
  );
}

const StripeNotice = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: ${({ $kind }) =>
    $kind === "danger" ? dangerSoft : accentSoft};
  border: 1px solid
    ${({ $kind }) =>
      $kind === "danger" ? dangerColor : accentSoftBorder};
  color: ${({ $kind }) =>
    $kind === "danger" ? dangerColor : accentInk};
  font-size: 13px;
  line-height: 1.5;
`;

function getActiveStageId(elapsedMs) {
  if (elapsedMs >= TOTAL_DURATION_MS) return STAGES[STAGES.length - 1].id;
  let current = STAGES[0].id;
  for (const stage of STAGES) {
    if (elapsedMs >= stage.startMs) current = stage.id;
  }
  return current;
}

function resolveCountryIso2(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  const match = countries.all.find(
    (country) =>
      country?.alpha2 === v ||
      country?.alpha3 === v ||
      country?.name?.toLowerCase() === v.toLowerCase(),
  );
  return match?.alpha2 || (v.length === 2 ? v.toUpperCase() : "");
}

/* Step 2 of a job-board search: the user's tracked jobs as Jobs-page rows, searched and filtered in place. */
const PICKER_PAGE = 15;
const NO_JOB_FILTERS = {
  industry: null,
  jobFunction: [],
  seniority: [],
  country: [],
  source: [],
  posted: null,
};

function BoardJobPicker({ selectedId, onPick, onView }) {
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState(NO_JOB_FILTERS);
  const [options, setOptions] = React.useState({
    industries: [],
    functions: [],
    seniorities: [],
    countries: [],
    sources: [],
  });
  const [jobs, setJobs] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  React.useEffect(() => {
    fetchWithAuth("/api/jobs/filter-options?scope=mine&mineType=tracked")
      .then((res) => res.json())
      .then((data) =>
        setOptions({
          industries: data.industries || [],
          functions: data.functions || [],
          seniorities: [...(data.seniorities || [])].sort((a, b) => seniorityRank(a) - seniorityRank(b)),
          countries: data.countries || [],
          sources: data.sources || [],
        }),
      )
      .catch(() => {});
  }, []);

  const fetchPage = React.useCallback(
    async (skip, signal) => {
      const params = new URLSearchParams({
        limit: String(PICKER_PAGE),
        skip: String(skip),
        includeTotal: "true",
        searchTitleOnly: "true",
        scope: "mine",
        mineType: "tracked",
      });
      if (search) params.set("search", search);
      if (filters.industry) params.set("industry", filters.industry);
      if (filters.jobFunction.length) params.set("function", filters.jobFunction.join("|"));
      if (filters.seniority.length) params.set("seniority", filters.seniority.join("|"));
      if (filters.country.length) params.set("country", filters.country.join("|"));
      if (filters.source.length) params.set("source", filters.source.join("|"));
      if (filters.posted) params.set("posted", filters.posted);
      const res = await fetchWithAuth(`/api/jobs?${params}`, { signal });
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    },
    [search, filters],
  );

  React.useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setFailed(false);
    fetchPage(0, ctrl.signal)
      .then((data) => {
        setJobs(data.jobs || []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setJobs([]);
        setTotal(0);
        setFailed(true);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(jobs.length);
      setJobs((prev) => [...prev, ...(data.jobs || [])]);
      setTotal(data.total || 0);
    } catch {
      // The rows already shown stay usable; the button simply stays for a retry.
    }
    setLoadingMore(false);
  };

  const patch = (next) => setFilters((f) => ({ ...f, ...next }));
  const activeCount =
    (filters.industry ? 1 : 0) +
    (filters.jobFunction.length ? 1 : 0) +
    (filters.seniority.length ? 1 : 0) +
    (filters.country.length ? 1 : 0) +
    (filters.source.length ? 1 : 0) +
    (filters.posted ? 1 : 0);
  const narrowed = Boolean(search) || activeCount > 0;

  return (
    <div>
      <SearchWrap style={{ marginBottom: 16, flexWrap: "nowrap" }}>
        <PickerSearch>
          <FiSearch className="lead" size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title…"
            aria-label="Search your jobs by title"
          />
        </PickerSearch>
        <FilterRow>
          <FiltersPopover activeCount={activeCount} alignEnd>
            <FilterField>
              <PanelFieldLabel>Industry</PanelFieldLabel>
              <FilterDropdown
                block
                label="Industry"
                values={filters.industry ? [filters.industry] : []}
                options={options.industries}
                single
                onChange={(arr) => patch({ industry: arr[0] || null })}
              />
            </FilterField>
            <FilterField>
              <PanelFieldLabel>Function</PanelFieldLabel>
              <FilterDropdown
                block
                label="Function"
                values={filters.jobFunction}
                options={options.functions}
                onChange={(arr) => patch({ jobFunction: arr })}
              />
            </FilterField>
            <FilterField>
              <PanelFieldLabel>Seniority</PanelFieldLabel>
              <FilterDropdown
                block
                label="Seniority"
                values={filters.seniority}
                options={options.seniorities}
                onChange={(arr) => patch({ seniority: arr })}
              />
            </FilterField>
            <FilterField>
              <PanelFieldLabel>Country</PanelFieldLabel>
              <FilterDropdown
                block
                label="Country"
                values={filters.country}
                options={options.countries}
                onChange={(arr) => patch({ country: arr })}
              />
            </FilterField>
            <FilterField>
              <PanelFieldLabel>Company</PanelFieldLabel>
              <FilterDropdown
                block
                label="Company"
                values={filters.source}
                options={options.sources}
                onChange={(arr) => patch({ source: arr })}
              />
            </FilterField>
            <FilterField>
              <PanelFieldLabel>Posted</PanelFieldLabel>
              <FilterDropdown
                block
                label="Posted"
                values={
                  filters.posted
                    ? [POSTED_OPTIONS.find((o) => o.value === filters.posted)?.label || filters.posted]
                    : []
                }
                options={POSTED_OPTIONS.map((o) => o.label)}
                single
                onChange={(arr) => {
                  const opt = POSTED_OPTIONS.find((o) => o.label === arr[0]);
                  patch({ posted: opt ? opt.value : null });
                }}
              />
            </FilterField>
            {activeCount > 0 && (
              <ClearLink onClick={() => setFilters(NO_JOB_FILTERS)}>
                <FiX size={12} /> Clear all filters
              </ClearLink>
            )}
          </FiltersPopover>
        </FilterRow>
      </SearchWrap>

      <PickerList>
        {loading ? (
          Array.from({ length: 6 }, (_, i) => <JobRowSkeleton key={i} />)
        ) : jobs.length === 0 ? (
          <PreviewEmpty style={{ position: "static" }}>
            <PreviewEmptyIcon>
              <FiBriefcase size={22} />
            </PreviewEmptyIcon>
            <PreviewEmptyH>
              {failed ? "Couldn't load your jobs" : narrowed ? "No jobs match" : "No tracked jobs yet"}
            </PreviewEmptyH>
            <PreviewEmptySub>
              {failed
                ? "Check your connection and try again."
                : narrowed
                  ? "Try a different title or loosen the filters."
                  : "Track companies on the Jobs page and their openings show up here."}
            </PreviewEmptySub>
          </PreviewEmpty>
        ) : (
          jobs.map((job) => {
            const id = jobIdOf(job);
            return (
              <JobRowCard
                key={id}
                job={job}
                picked={id === selectedId}
                pickerMode
                onOpen={onPick}
                onViewDetails={onView}
              />
            );
          })
        )}
      </PickerList>

      {!loading && jobs.length > 0 && (
        <PickerFoot>
          <span>
            Showing {formatNumber(jobs.length)} of {formatNumber(total)}
          </span>
          {jobs.length < total && (
            <Button onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          )}
        </PickerFoot>
      )}
    </div>
  );
}

/* The Jobs search box, stretched to the Filters button, with a brand-orange focus. */
const PickerSearch = styled(JobsSearch)`
  flex: 1 1 auto;
  max-width: none;
  input:focus {
    border-color: var(--secondary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--secondary) 18%, transparent);
  }
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PickerFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  font-size: 12.5px;
  color: var(--ink-muted);
`;

function CountryDropdown({
  values = [],
  onToggle,
  placeholder = "Type a country",
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const options = React.useMemo(() => {
    return countries.all
      .filter(
        (country) =>
          country?.name &&
          country?.alpha2 &&
          country?.status !== "deleted" &&
          country?.ioc !== "PRK",
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const selectedCountries = React.useMemo(() => {
    /* Nothing picked yet must mean NOTHING selected: a blank never matches (Canary
       Islands' alpha3 is itself "", so a loose match used to land there). */
    const picked = values.map((v) => String(v || "").trim()).filter(Boolean);
    return picked
      .map((v) => {
        const lower = v.toLowerCase();
        return options.find(
          (option) =>
            option.name === v ||
            option.alpha2 === v ||
            option.alpha3 === v ||
            option.alpha2.toLowerCase() === lower ||
            option.name.toLowerCase() === lower,
        );
      })
      .filter(Boolean);
  }, [options, values]);
  const selectedNames = React.useMemo(
    () => new Set(selectedCountries.map((c) => c.name)),
    [selectedCountries],
  );

  const filteredOptions = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(q) ||
        option.alpha2.toLowerCase().includes(q) ||
        option.alpha3.toLowerCase().includes(q),
    );
  }, [options, query]);

  React.useEffect(() => {
    const onPointerDown = (event) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  // Picking keeps the field focused and the list open: the next country is one keystroke away.
  const pick = (option) => {
    onToggle?.(option.name, option);
    setQuery("");
    inputRef.current?.focus();
  };

  const onKeyDown = (event) => {
    if (event.key === "Backspace" && !query && selectedCountries.length) {
      const last = selectedCountries[selectedCountries.length - 1];
      onToggle?.(last.name, last);
    } else if (event.key === "Enter" && query.trim() && filteredOptions.length) {
      event.preventDefault();
      pick(filteredOptions[0]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <CountryDropdownWrap ref={wrapRef}>
      {selectedCountries.length > 0 && (
        <TokenRow aria-label="Selected countries">
          {selectedCountries.map((c) => (
            <Token key={c.alpha2}>
              <CircleFlag countryCode={c.alpha2.toLowerCase()} height={16} width={16} />
              {c.name}
              <TokenRemove
                type="button"
                aria-label={`Remove ${c.name}`}
                onClick={() => onToggle?.(c.name, c)}
              >
                <FiX size={12} />
              </TokenRemove>
            </Token>
          ))}
        </TokenRow>
      )}
      <FieldAnchor>
        <TokenField
          $open={open}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
            setOpen(true);
          }}
        >
          <FiSearch size={15} />
          <TokenInput
            ref={inputRef}
            value={query}
            placeholder={placeholder}
            disabled={disabled}
            aria-label="Search countries"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
        </TokenField>
        {open && (
        <CountryMenu>
          <CountryList>
            {filteredOptions.map((option) => {
              const isSelected = selectedNames.has(option.name);
              return (
                <CountryItem
                  key={option.alpha2}
                  type="button"
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pick(option)}
                >
                  <CircleFlag
                    countryCode={option.alpha2.toLowerCase()}
                    height={18}
                    width={18}
                  />
                  <span>{option.name}</span>
                  {isSelected && (
                    <CountryItemCheck>
                      <FiCheck size={14} />
                    </CountryItemCheck>
                  )}
                </CountryItem>
              );
            })}
            {filteredOptions.length === 0 && (
              <CountryEmpty>No country found.</CountryEmpty>
            )}
          </CountryList>
        </CountryMenu>
        )}
      </FieldAnchor>
    </CountryDropdownWrap>
  );
}

function normalizeForReqNo(v) {
  return String(v || "")
    .toLowerCase()
    .replace(/[\W_]+/g, "");
}

function hashString(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function deriveReqNo({ sourceMode, selectedBoardJobData, title, description }) {
  if (sourceMode === "board") {
    return String(selectedBoardJobData?._id || "").trim();
  }
  const normalized = `${normalizeForReqNo(title)}${normalizeForReqNo(description)}`;
  return `MANUAL-${hashString(normalized || "empty")}`;
}

/* Red dot used to flag an unseen completed search on the landing's
   past-searches affordances. */
const PastNotifDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--danger);
  margin-left: 8px;
  vertical-align: middle;
  flex-shrink: 0;
`;

export default function FindCandidatesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading, features } = useAuth();
  const {
    hasUnseen: hasUnseenFind,
    activeRun,
    activeRuns,
    watchRun,
    flagFindCompleted,
    acknowledgeFindResults,
    reportRun,
    refreshRun,
  } = useFindNotification();
  /* One search at a time. `activeRun` is the server's answer, so this holds across
     remounts, routes and tabs — every "start a search" affordance reads it, rather
     than each one guessing from local state. `isSubmitting` covers the gap between
     posting a search and the server admitting it exists. */
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // The server took the search: until then the progress screen is provisional, so the walkthrough waits.
  const [launchAccepted, setLaunchAccepted] = React.useState(false);
  const isRunning = !!activeRun || isSubmitting;
  /* What the plan allows: Starter runs one search at a time, Growth two, Scale five. The
     client only ever tracks the newest run, so beyond one the server enforces the count. */
  const { features: planFeatures } = usePlan();
  const searchBlocked = isRunning && planFeatures.concurrentSearches <= 1;
  const similarLocked = !planFeatures.similarSearch;
  // Saving people (candidate database, a job's shortlist) needs Outreach, which the free trial doesn't have.
  const actionsLocked = planFeatures.outreach === false;
  // The free trial searches from a JD or a description only; the job board needs Jobs.
  const boardLocked = !planFeatures.boardSearch;
  const [screen, setScreen] = React.useState("home"); // home | source | input | company | country | brief | finding | rateLimit
  const [sourceMode, setSourceMode] = React.useState(""); // board | manual | people
  // Hiring company step (manual + people): the job record the search creates carries it; anonymous = "Confidential".
  const [hiringName, setHiringName] = React.useState("");
  const [anonymousHiring, setAnonymousHiring] = React.useState(false);
  // People mode: an LLM names the role from the description while the company step is open.
  const [titleLoading, setTitleLoading] = React.useState(false);
  const [titleError, setTitleError] = React.useState("");
  const [selectedBoardJobData, setSelectedBoardJobData] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  // The picked countries, in the order picked; one search covers all of them.
  const [countries, setCountries] = React.useState([]);
  /* "No location filter" as a deliberate choice. Kept apart from `countries` so an
     empty list still means "not chosen yet" and can't advance the wizard. The
     pipeline already runs unscoped on an empty list, so nothing else changes. */
  const [worldwide, setWorldwide] = React.useState(false);
  /* When the run began, per the server — not a client guess, so the timer is right
     after a reload or in a second tab. */
  const startedAt = activeRun?.startedAt ?? null;
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const [pastSearches, setPastSearches] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  // The first page of past searches has come back (or failed), so an empty list means none.
  const [historyLoaded, setHistoryLoaded] = React.useState(false);
  // An empty history loads as a screenful of placeholder rows, not a fixed few.
  const historySkelRef = React.useRef(null);
  const historyFillCount = useFillCount(historySkelRef, {
    min: 4,
    enabled: historyLoading && pastSearches.length === 0,
    rowSelector: "[data-skel-row]",
  });
  const [historyHasMore, setHistoryHasMore] = React.useState(false);
  const [historyTotal, setHistoryTotal] = React.useState(null); // every search, not just the loaded page
  const historyLoadMoreRef = React.useRef(null);
  const [candidateDetails, setCandidateDetails] = React.useState({});
  const [expandedCandidateObjectId, setExpandedCandidateObjectId] =
    React.useState(null);
  const expandedCandidateDetail = expandedCandidateObjectId
    ? candidateDetails[expandedCandidateObjectId]
    : null;
  const expandedProfileNormalized = React.useMemo(
    () => normalizePreviewProfile(expandedCandidateDetail),
    [expandedCandidateObjectId, expandedCandidateDetail],
  );
  /* The results page loads a lean profile for every ranked candidate; opening one fetches
     the whole block (skills, role descriptions, certifications) for that person only. */
  const fullProfileInFlightRef = React.useRef(new Set());
  const loadFullProfile = React.useCallback(async (objId) => {
    if (!objId || fullProfileInFlightRef.current.has(objId)) return;
    fullProfileInFlightRef.current.add(objId);
    try {
      const res = await fetchWithAuth(`/api/linkedin-candidates/${objId}`);
      if (!res.ok) return;
      const data = await res.json();
      setCandidateDetails((prev) => ({
        ...prev,
        [objId]: {
          ...(prev[objId] || {}),
          linkdapi_profile:
            data.linkdapi_profile || prev[objId]?.linkdapi_profile || null,
          _full: true,
        },
      }));
    } catch (err) {
      console.warn("Failed to fetch full profile:", err);
    } finally {
      fullProfileInFlightRef.current.delete(objId);
    }
  }, []);
  React.useEffect(() => {
    if (!expandedCandidateObjectId || !expandedCandidateDetail) return;
    if (expandedCandidateDetail._full) return;
    loadFullProfile(expandedCandidateObjectId);
  }, [expandedCandidateObjectId, expandedCandidateDetail, loadFullProfile]);
  // Interactive pre-search steps: editable filter brief, then a free sample preview.
  const [briefDraft, setBriefDraft] = React.useState(null);
  /* Minted when a new draft's brief is first extracted and sent with the brief, every
     filter-assistant turn and the run itself, so their credit charges group as one search. */
  const draftSearchIdRef = React.useRef("");
  const draftSearchId = () => {
    if (!draftSearchIdRef.current) {
      draftSearchIdRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
    }
    return draftSearchIdRef.current;
  };
  const [briefLoading, setBriefLoading] = React.useState(false);
  // The assistant on the working sheet: whether the sheet is released for editing,
  // its latest result (the people it found), and the conversation so far — user lines
  // and assistant turns, each turn holding the steps it streamed and its reply.
  const [agentIntro, setAgentIntro] = React.useState({
    done: false,
    turns: [],
    result: null,
  });
  /* A step event lands on the open assistant turn, or opens one (the first turn and a
     re-check start without a user line). */
  const handleAgentActivity = React.useCallback((ev) => {
    setAgentIntro((p) => {
      const turns = [...p.turns];
      let last = turns[turns.length - 1];
      if (!last || last.role !== "assistant" || last.done) {
        last = { id: `a-${Date.now()}`, role: "assistant", steps: [], reply: "", done: false };
        turns.push(last);
      }
      const steps = [...last.steps];
      const i = steps.findIndex((st) => st.id === ev.id);
      if (i >= 0) steps[i] = { ...steps[i], ...ev };
      else steps.push(ev);
      turns[turns.length - 1] = { ...last, steps };
      return { ...p, turns };
    });
  }, []);
  /* Close the open assistant turn, with its reply when there is one. */
  const finishAgentTurn = React.useCallback((reply) => {
    setAgentIntro((p) => {
      const turns = [...p.turns];
      const last = turns[turns.length - 1];
      if (!last || last.role !== "assistant" || last.done) return p;
      turns[turns.length - 1] = { ...last, done: true, reply: reply || last.reply };
      return { ...p, turns };
    });
  }, []);
  // A new search has no opening assistant turn: the extractor's plan is the plan, so
  // the sheet is released the moment the brief lands. The assistant only runs when
  // asked in chat, and the search itself measures and widens the plan at launch.
  React.useEffect(() => {
    if (screen !== "brief" || briefLoading || agentIntro.done) return;
    setAgentIntro((p) => ({ ...p, done: true }));
  }, [screen, briefLoading, agentIntro.done]);
  const [briefError, setBriefError] = React.useState("");
  /* The filter assistant on the Filters step: while a turn runs the editor is locked so
     its recommendation can't go stale under it. Its ref lets the page ask for a re-check. */
  const [agentBusy, setAgentBusy] = React.useState(false);
  const agentRef = React.useRef(null);
  // When the current pipeline stage began, for the in-stage progress bar.
  const liveStageStartRef = React.useRef({ stage: null, at: 0 });
  React.useEffect(() => {
    const st = activeRun?.stage || null;
    if (st && liveStageStartRef.current.stage !== st) {
      liveStageStartRef.current = { stage: st, at: Date.now() };
    }
  }, [activeRun?.stage]);
  const [runError, setRunError] = React.useState("");
  const [historyItemFromPath, setHistoryItemFromPath] = React.useState(null);
  const [historyItemLoading, setHistoryItemLoading] = React.useState(false);
  const [historyItemFetched, setHistoryItemFetched] = React.useState(false);
  // Two independent memberships. `databaseIds` = in the user's candidate pool
  // (Outreach); `shortlistedIds` = chosen for the job this search came from.
  // Shortlisting implies being in the database, but not the other way round.
  const [databaseIds, setDatabaseIds] = React.useState(() => new Set());
  const [shortlistedIds, setShortlistedIds] = React.useState(() => new Set());
  // Ruled-out candidates are collapsed behind a toggle in the results table.
  const [showDisqualified, setShowDisqualified] = React.useState(false);
  /* Checklist filter on the results: ids like "M2" / "N1"; a row must have met every one. */
  const [criteriaFilter, setCriteriaFilter] = React.useState(() => new Set());
  const [criteriaFilterOpen, setCriteriaFilterOpen] = React.useState(false);
  /* Bulk selection on the results: tick rows, then add them all to the database or the
     job's shortlist in one go. */
  const [bulkMode, setBulkMode] = React.useState(false);
  const [bulkIds, setBulkIds] = React.useState(() => new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);
  /* The ranking is drawn in pages of RANKING_PAGE rows: scrolling to the foot reveals the
     next page, the way the past-searches list loads. All rows are already in memory, so
     this is only about how much is drawn at once. */
  const [rankingVisible, setRankingVisible] = React.useState(RANKING_PAGE);
  const rankingMoreRef = React.useRef(null);
  React.useEffect(() => {
    setRankingVisible(RANKING_PAGE);
  }, [criteriaFilter]);
  /* The results walkthrough scrolls down to the weaker matches: draw every row as soon as it starts, so the
     list has finished growing by then and the scroll runs over a settled page. */
  const tourTarget = useTourTarget();
  const resultsTourOn = Boolean(tourTarget) && activeTourId() === "results";
  React.useEffect(() => {
    if (resultsTourOn) setRankingVisible(Number.MAX_SAFE_INTEGER);
  }, [resultsTourOn]);
  React.useEffect(() => {
    const el = rankingMoreRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setRankingVisible((v) => v + RANKING_PAGE);
      },
      { threshold: 0.1, rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  });
  const criteriaFilterRef = React.useRef(null);
  React.useEffect(() => {
    if (!criteriaFilterOpen) return undefined;
    const onDoc = (e) => {
      if (criteriaFilterRef.current && !criteriaFilterRef.current.contains(e.target))
        setCriteriaFilterOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setCriteriaFilterOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [criteriaFilterOpen]);
  // Whether the past-search recap card is expanded (open by default).
  const [searchRecapOpen, setSearchRecapOpen] = React.useState(true);
  // The tick chip currently hovered/focused, with the anchor rect its popover uses.
  const [criteriaPop, setCriteriaPop] = React.useState(null);
  const [shortlistBusyIds, setShortlistBusyIds] = React.useState(
    () => new Set(),
  );
  // Candidates picked as the starting point for "find people like these" (max 5).
  const [seedIds, setSeedIds] = React.useState(() => new Set());
  // Another round of the same search: which one is running, and why it failed.
  const [followUpMode, setFollowUpMode] = React.useState("");
  const [followUpError, setFollowUpError] = React.useState("");
  /* The wizard's Filters step re-entered from a past search: the confirmed filters are
     the draft, and the run goes out as the next round of that search rather than a
     new one. { searchId, reqNo, historyId, returnTo, stats } while set. */
  /* The req_no we just launched, for the moment between posting a search and the
     server reporting it. Once `activeRun` lands that is the truth. */
  const [submittedReqNo, setSubmittedReqNo] = React.useState("");
  const [dockOpen, setDockOpen] = React.useState(false);
  const runningReqNo = activeRun?.reqNo || submittedReqNo;
  // Readable from callbacks that fire when a run finishes.
  const runningReqNoRef = React.useRef("");
  runningReqNoRef.current = runningReqNo;
  /* Where a run started as another round of a past search should return to. Kept in
     sessionStorage as well, because a remount loses the state but not the run. */
  const followUpRunFrom = React.useMemo(
    () => readFollowUpReturn(runningReqNo),
    [runningReqNo],
  );
  // "Not seeing the right people?" — the choice modal, and the pick-people mode it starts.
  const [choiceOpen, setChoiceOpen] = React.useState(false);
  const [pickingMode, setPickingMode] = React.useState(false);
  // Start of the candidate list, so entering picking mode can jump straight to it.
  const pickAreaRef = React.useRef(null);
  /* The past search whose filters are open on the working sheet; its launch runs another round. */
  const [refineItem, setRefineItem] = React.useState(null);

  const closeCriteriaPop = React.useCallback(() => setCriteriaPop(null), []);

  /* Touch browsers synthesise mouseenter after a tap, which would re-open the popover
     the same tap just closed. Where there's no real hover, don't listen for it. */
  const canHover = React.useMemo(
    () =>
      typeof window === "undefined" ||
      !window.matchMedia ||
      window.matchMedia("(hover: hover)").matches,
    [],
  );

  const openCriteriaPop = React.useCallback((anchor, id, label, items) => {
    if (!items?.length || !anchor) return;
    const r = anchor.getBoundingClientRect();
    setCriteriaPop({
      id,
      label,
      items,
      x: r.left + r.width / 2,
      top: r.top,
      bottom: r.bottom,
    });
  }, []);

  // The anchor rect is a snapshot, so any scroll or resize would leave it stranded.
  // A tap anywhere else dismisses it — on touch there's no mouseleave to rely on.
  React.useEffect(() => {
    if (!criteriaPop) return undefined;
    const onDocPointerDown = (e) => {
      if (e.target?.closest?.("[data-criteria-chip]")) return;
      closeCriteriaPop();
    };
    window.addEventListener("scroll", closeCriteriaPop, true);
    window.addEventListener("resize", closeCriteriaPop);
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => {
      window.removeEventListener("scroll", closeCriteriaPop, true);
      window.removeEventListener("resize", closeCriteriaPop);
      document.removeEventListener("pointerdown", onDocPointerDown, true);
    };
  }, [criteriaPop, closeCriteriaPop]);

  const { id: historyId } = useParams();
  const latestStatusReqIdRef = React.useRef(0);
  // The run this page is already waiting on, so only one waiter exists per run.
  const adoptedReqNoRef = React.useRef("");

  /* The progress screen is only a valid view while something is running. When a run
     ends — here, in another tab, or by cancellation — anyone sitting on it goes back
     to the landing rather than watching a frozen pipeline. */
  React.useEffect(() => {
    if (screen === "finding" && !isRunning) setScreen("home");
  }, [screen, isRunning]);

  // Each start mode keeps its own title/description draft so a JD never leaks into "Describe a person".
  const modeDraftsRef = React.useRef({});

  const resetDraftForm = React.useCallback(() => {
    modeDraftsRef.current = {};
    draftSearchIdRef.current = "";
    setSourceMode("board");
    setSelectedBoardJobData(null);
    setTitle("");
    setDescription("");
    setHiringName("");
    setAnonymousHiring(false);
    setTitleError("");
    setCountries([]);
    setWorldwide(false);
    setBriefDraft(null);
    setBriefError("");
    setRefineItem(null);
  }, []);

  /* Country and worldwide are one choice with two shapes — setting either clears
     the other, so "worldwide + Singapore" can never be submitted. */
  const chooseCountry = React.useCallback((name) => {
    const n = String(name || "").trim();
    if (!n) return;
    setCountries((prev) => (prev.includes(n) ? prev.filter((c) => c !== n) : [...prev, n]));
    setWorldwide(false);
  }, []);

  const chooseWorldwide = React.useCallback(() => {
    setWorldwide((prev) => !prev);
    setCountries([]);
  }, []);

  // Stash the current mode's draft, then restore whatever the target mode had.
  const switchSourceMode = React.useCallback(
    (nextMode) => {
      if (sourceMode === nextMode) return;
      if (sourceMode) {
        modeDraftsRef.current[sourceMode] = { title, description };
      }
      const draft = modeDraftsRef.current[nextMode] || {
        title: "",
        description: "",
      };
      setTitle(draft.title);
      setDescription(draft.description);
      setSourceMode(nextMode);
      if (nextMode !== "board") {
        setSelectedBoardJobData(null);
      }
    },
    [sourceMode, title, description],
  );

  const waitForFindCandidatesByReqNo = React.useCallback(
    async (userSub, reqNo, onPoll) => {
      if (!userSub) {
        throw new Error("Sign in required to check search status.");
      }
      const maxMs = 1_800_000;
      const maxMisses = 12;
      const start = Date.now();
      let misses = 0;
      while (Date.now() - start < maxMs) {
        const payload = { sub: userSub };
        if (reqNo) payload.req_no = reqNo;
        const stRes = await fetchWithAuth(
          "/api/find-candidates/status-by-req-no",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        ).catch(() => null);
        // A status check that got no answer says nothing about the search, which keeps running: ask again.
        if (!stRes || stRes.status >= 500 || stRes.status === 429) {
          if (++misses > maxMisses) {
            throw new Error(
              "We lost contact with your search. It may still be running — check your recent searches in a few minutes.",
            );
          }
          await new Promise((r) => setTimeout(r, 5_000));
          continue;
        }
        misses = 0;
        const body = await stRes.json().catch(() => ({}));
        if (!stRes.ok) {
          throw new Error(
            body?.error ||
              body?.message ||
              `Status check failed (${stRes.status})`,
          );
        }
        if (typeof onPoll === "function") onPoll(body);
        const pollIntervalMs = body?.stage ? 3_000 : 20_000;
        if (!body?.found) {
          await new Promise((r) => setTimeout(r, pollIntervalMs));
          continue;
        }
        const st = body.status;
        if (st === "succeeded") return body;
        if (st === "failed") {
          throw new Error(body.error || "Find candidates job failed");
        }
        await new Promise((r) => setTimeout(r, pollIntervalMs));
      }
      throw new Error(
        "Search is taking longer than expected. Try again or check History in a few minutes.",
      );
    },
    [],
  );

  /* Every poll of a run this page is driving goes to the context too, so the dock,
     the progress screen and the "can I start a search" gate all read one value. */
  const handleFindPoll = reportRun;

  // What is on screen right now, readable from callbacks that fire when a run finishes.
  const screenRef = React.useRef(screen);
  React.useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  const applyFindCandidatesOutcome = React.useCallback(
    async (reqNo) => {
      clearFollowUpReturn();
      setSubmittedReqNo((cur) => (cur === reqNo ? "" : cur));
      // Waiting is over — let a future run be adopted.
      if (adoptedReqNoRef.current === reqNo) adoptedReqNoRef.current = "";
      // Search finished. Never pop the user into the results: the People nav badge and
      // the landing's past-searches list say it is ready, and they open it when they
      // choose. Only if they were watching THIS run's progress screen does it close,
      // back to the landing; anywhere else (another run's progress, a draft of the next
      // search, a past search) is left exactly as it is.
      flagFindCompleted(reqNo);
      const watchingThisRun = screenRef.current === "finding" && runningReqNoRef.current === reqNo;
      if (watchingThisRun) {
        setElapsedMs(0);
        setExpandedCandidateObjectId(null);
        resetDraftForm();
        setScreen("home");
      }
      // Refresh recent searches so the just-finished one appears on the landing.
      loadPastSearchesRef.current?.(false);
    },
    [resetDraftForm, flagFindCompleted],
  );

  const loadPastSearchesInFlightRef = React.useRef(false);

  const loadPastSearches = React.useCallback(
    async (append = false) => {
      if (!append && loadPastSearchesInFlightRef.current) return;
      loadPastSearchesInFlightRef.current = true;
      try {
        setHistoryLoading(true);
        const skip = append ? pastSearches.length : 0;
        const limit = 9;
        const res = await fetchWithAuth(
          `/api/candidates/history?limit=${limit}&skip=${skip}`,
        );
        if (!res.ok) throw new Error("Failed to load candidate history");
        const data = await res.json();
        const rows = data?.items || [];
        // Only what the list rows and the crumb read — the full search is
        // re-fetched by id when one is opened.
        const normalized = rows.map((item) => ({
          id: item.id || item._id,
          title: item.title || "Untitled role",
          requestedAt: item.requestedAt,
          candidateCount: item.candidateCount || 0,
          // How many rounds this search has run; the count above spans all of them.
          batchCount: item.batchCount || 1,
        }));
        setPastSearches((prev) =>
          append ? [...prev, ...normalized] : normalized,
        );
        setHistoryHasMore(!!data?.hasMore);
        if (Number.isFinite(Number(data?.total))) setHistoryTotal(Number(data.total));
      } catch (err) {
        console.warn("Failed to fetch candidate history:", err);
        if (!append) setPastSearches([]);
        setHistoryHasMore(false);
      } finally {
        setHistoryLoading(false);
        setHistoryLoaded(true);
        loadPastSearchesInFlightRef.current = false;
      }
    },
    [pastSearches.length],
  );

  React.useEffect(() => {
    if (historyLoadMoreRef.current && historyHasMore && !historyLoading) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadPastSearches(true);
        },
        { threshold: 0.1, rootMargin: "100px" },
      );
      io.observe(historyLoadMoreRef.current);
      return () => io.disconnect();
    }
  }, [historyHasMore, historyLoading, loadPastSearches]);

  const candidateFetchInFlightKeysRef = React.useRef(new Set());
  const candidateFetchCompletedKeysRef = React.useRef(new Set());
  const [candidateDetailsPending, setCandidateDetailsPending] =
    React.useState(false);

  /* Details (name, headline, photo) for a set of ranked rows — only the ones not loaded
     yet. Called for the rows being drawn, so a 300-person round fetches 20 at a time as
     the recruiter scrolls rather than everyone up front. */
  const candidateDetailsRef = React.useRef(candidateDetails);
  candidateDetailsRef.current = candidateDetails;
  const fetchCandidateDetailsFor = React.useCallback(async (wanted) => {
    const ids = Array.from(new Set((wanted || []).filter(Boolean))).filter(
      (id) => !candidateDetailsRef.current[id],
    );
    if (ids.length === 0) return;
    const reqKey = ids.slice().sort().join(",");
    if (!reqKey) return;
    if (candidateFetchInFlightKeysRef.current.has(reqKey)) return;
    if (candidateFetchCompletedKeysRef.current.has(reqKey)) return;

    candidateFetchInFlightKeysRef.current.add(reqKey);
    setCandidateDetailsPending(true);
    try {
      // Ids ride in the body: a few hundred of them would overflow a query string.
      const res = await fetchWithAuth("/api/linkedin-candidates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(new Set(ids)) }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setCandidateDetails((prev) => ({ ...prev, ...(data.candidates || {}) }));
      candidateFetchCompletedKeysRef.current.add(reqKey);
    } catch (err) {
      console.warn("Failed to fetch candidate details:", err);
    } finally {
      candidateFetchInFlightKeysRef.current.delete(reqKey);
      if (candidateFetchInFlightKeysRef.current.size === 0) {
        setCandidateDetailsPending(false);
      }
    }
  }, []);

  const fetchHistoryItem = React.useCallback(async (id) => {
    if (!id) return null;
    try {
      setHistoryItemLoading(true);
      const res = await fetchWithAuth(`/api/candidates/history/${id}`);
      if (!res.ok) return null;
      const data = await res.json();
      return {
        id: data.id || data._id,
        req_no: data.req_no || "",
        title: data.title || "Untitled role",
        jobId: data.jobId || null,
        requestedAt: data.requestedAt,
        candidateCount: data.candidateCount || 0,
        disqualifiedCount: data.disqualifiedCount || 0,
        ranking: data.ranking || {},
        reason: data.reason || "",
        jobDescription: data.jobDescription || "",
        brief: data.brief || null,
        country: data.country || "",
        targetCount: data.targetCount ?? null,
        unresolved: data.unresolved || [],
        // Batching: the search these rounds belong to, and its sibling rounds.
        searchId: data.searchId || "",
        batches: Array.isArray(data.batches) ? data.batches : [],
      };
    } catch (e) {
      console.warn("Failed to fetch history item:", e);
      return null;
    } finally {
      setHistoryItemLoading(false);
    }
  }, []);

  React.useEffect(() => {
    // Picks and the filter conversation belong to the round being viewed — carrying
    // them across would seed a search with people from a different one.
    setSeedIds(new Set());
    setFollowUpError("");
    setChoiceOpen(false);
    setPickingMode(false);
    setRefineItem(null);
    setCriteriaFilter(new Set());
    setBulkMode(false);
    setBulkIds(new Set());
    setRankingVisible(RANKING_PAGE);
    if (historyId) {
      setHistoryItemFetched(false);
      fetchHistoryItem(historyId).then((item) => {
        setHistoryItemFromPath(item);
        setHistoryItemFetched(true);
      });
    } else {
      setHistoryItemFromPath(null);
      setHistoryItemFetched(false);
    }
  }, [historyId, fetchHistoryItem]);

  /* First time a trial user opens a search's results, walk them through it once the first person has fully loaded
     (other plans start it from Get started). Marked as seen when it starts, so closing it early doesn't bring it back. */
  const getStarted = useGetStarted();
  const resultsTourSeen = !getStarted || Boolean(getStarted.done?.results);
  const onTrialPlan = planFeatures.plan === "trial";
  React.useEffect(() => {
    if (!historyId || resultsTourSeen || !onTrialPlan) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (isTourActive() || tries > 60) return clearInterval(timer);
      const row = document.querySelector('[data-tour="fc-results-row"]');
      if (!row || row.querySelector("[data-skeleton]")) return;
      clearInterval(timer);
      markGetStarted({ step: "results" });
      startTour("results");
    }, 500);
    return () => clearInterval(timer);
  }, [historyId, resultsTourSeen, onTrialPlan]);

  // The results walkthrough started from Get started lands on the past-search list: open the latest search that found people,
  // or, when none has yet, swap to the short version that says so.
  React.useEffect(() => {
    if (historyId || activeTourId() !== "results") return;
    const latest = pastSearches.find((s) => s.candidateCount > 0);
    if (latest) navigate(`/find-candidates/history/${latest.id}`);
    else if (historyLoaded && !historyLoading) startTour("resultsEmpty");
  }, [historyId, pastSearches, historyLoaded, historyLoading, navigate]);

  // Job the currently-open result set belongs to ("" for a description search).
  // Live results carry the picked job; a past search carries the job its req_no
  // was derived from.
  const activeSearchJobId = String(
    (historyId
      ? historyItemFromPath?.jobId
      : selectedBoardJobData?._id?.$oid || selectedBoardJobData?._id) || "",
  ).trim();

  React.useEffect(() => {
    if (!user?.sub) {
      setDatabaseIds(new Set());
      setShortlistedIds(new Set());
      return;
    }
    let cancelled = false;
    const toIdSet = (body) =>
      new Set(
        (Array.isArray(body?.ids) ? body.ids : [])
          .map((id) => String(id || "").trim())
          .filter((id) => /^[a-f0-9]{24}$/i.test(id)),
      );
    const load = async (url) => {
      const res = await fetchWithAuth(url);
      if (!res.ok) return null;
      return toIdSet(await res.json().catch(() => ({})));
    };
    (async () => {
      try {
        // Unscoped = who is in the candidate pool. Job-scoped = who is chosen
        // for this role. The two pills read from these independently.
        const [inDb, chosen] = await Promise.all([
          load("/api/outreach/external-shortlist/ids"),
          activeSearchJobId
            ? load(
                `/api/outreach/external-shortlist/ids?jobId=${encodeURIComponent(activeSearchJobId)}`,
              )
            : Promise.resolve(new Set()),
        ]);
        if (cancelled) return;
        if (inDb) setDatabaseIds(inDb);
        if (chosen) setShortlistedIds(chosen);
      } catch {
        // non-fatal
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.sub, activeSearchJobId]);

  const loadPastSearchesRef = React.useRef(loadPastSearches);
  loadPastSearchesRef.current = loadPastSearches;

  React.useEffect(() => {
    // Load past searches eagerly for both the history index AND the landing
    // page (so the "Recent searches" panel on /find-candidates is filled
    // on first paint, not only after visiting the history route).
    const onHistory =
      location.pathname === "/find-candidates/history" && !historyId;
    const onLanding = location.pathname === "/find-candidates" && !historyId;
    if (onHistory || onLanding) {
      loadPastSearchesRef.current(false);
    }
  }, [location.pathname, historyId]);

  // Opening past searches (the history index or a specific past search) means
  // the user has now seen their latest completed search — clear the badge on
  // the People nav and the landing's past-searches section.
  React.useEffect(() => {
    if (location.pathname.startsWith("/find-candidates/history")) {
      acknowledgeFindResults();
    }
  }, [location.pathname, acknowledgeFindResults]);

  /* The stale-screen guard near the top is the only thing that moves a user off the
     progress screen now. Returning to the base route while a search runs keeps it,
     rather than resetting to home and letting the resume probe put it back. */

  /* The rows on screen, recorded by the ranking as it renders; the fetch below follows. */
  const drawnIdsRef = React.useRef([]);
  React.useEffect(() => {
    if (historyItemFromPath?.ranking) fetchCandidateDetailsFor(drawnIdsRef.current);
  }, [historyId, historyItemFromPath, rankingVisible, criteriaFilter, showDisqualified, fetchCandidateDetailsFor]);

  React.useEffect(() => {
    if (location.pathname !== "/find-candidates") return;
    if (historyId) return;
    if (authLoading) return;
    if (!user?.sub) return;

    // Job board flow passes selectedJob in location.state; skip the resume probe for that arrival.
    if (location.state?.selectedJob) return;

    // Returning from the job picker without picking: straight to the source step, no probe.
    if (location.state?.resumeSource) return;

    /* Adopt a run this page isn't already following — one started before a reload,
       or in another tab. It only takes over the WAITING (so completion still routes
       and refreshes the landing); whether a run exists at all is `activeRun`'s job,
       and the dock is what offers the way back into the progress screen. */
    if (!activeRun?.reqNo) return;
    if (adoptedReqNoRef.current === activeRun.reqNo) return;
    adoptedReqNoRef.current = activeRun.reqNo;

    const reqId = Date.now();
    latestStatusReqIdRef.current = reqId;
    let cancelled = false;
    const reqNo = activeRun.reqNo;

    (async () => {
      try {
        await waitForFindCandidatesByReqNo(user.sub, reqNo, handleFindPoll);
        if (cancelled || latestStatusReqIdRef.current !== reqId) return;
        await applyFindCandidatesOutcome(reqNo);
      } catch {
        // The context poll still owns "is it running"; nothing to roll back here.
      } finally {
        if (adoptedReqNoRef.current === reqNo) adoptedReqNoRef.current = "";
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    user?.sub,
    authLoading,
    location.pathname,
    historyId,
    activeRun?.reqNo,
    handleFindPoll,
    waitForFindCandidatesByReqNo,
    applyFindCandidatesOutcome,
  ]);

  /** Make a board job the search's role: title, description and its country. */
  const pickBoardJob = React.useCallback((job) => {
    const picked = {
      ...job,
      id: jobIdOf(job),
      title: job.title || job.job_title || "",
      description: job.description || "",
    };
    const { country: jobCountry } = extractLocationCountry(picked);
    setSourceMode("board");
    setSelectedBoardJobData(picked);
    setTitle(picked.title);
    setDescription(picked.description);
    setCountries(jobCountry ? [jobCountry] : []);
  }, []);

  React.useEffect(() => {
    const selectedJob = location.state?.selectedJob;
    if (!selectedJob) return;
    pickBoardJob(selectedJob);
    setScreen("country");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, pickBoardJob]);

  // Returning from the job picker via its back button: reopen the source step.
  React.useEffect(() => {
    if (!location.state?.resumeSource) return;
    setSourceMode("board");
    setScreen("source");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // A follow-up round arriving from a past search opens straight on the progress screen.
  React.useEffect(() => {
    if (!location.state?.followUpStarted) return;
    setElapsedMs(0);
    setRunError("");
    setScreen("finding");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // A follow-up that failed after it had already moved to the running screen.
  React.useEffect(() => {
    const msg = location.state?.followUpError;
    if (!msg) return;
    setFollowUpError(msg);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Ticks for as long as something is running — the dock shows the clock too, not
  // just the progress screen.
  React.useEffect(() => {
    if (!startedAt) return undefined;
    const tick = () => setElapsedMs(Math.max(0, Date.now() - startedAt));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const getCandidateLink = React.useCallback((details) => {
    if (!details || typeof details !== "object") return "";
    const href = details.link;
    return typeof href === "string" && href.includes("linkedin.com")
      ? href
      : "";
  }, []);

  const handleCandidateClick = React.useCallback((objId) => {
    if (!objId) return;
    setExpandedCandidateObjectId((p) => (p === objId ? null : objId));
  }, []);

  const withBusy = React.useCallback(async (objId, fn) => {
    setShortlistBusyIds((prev) => new Set(prev).add(objId));
    try {
      await fn();
    } finally {
      setShortlistBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(objId);
        return next;
      });
    }
  }, []);

  const setMembership = (setter, objId, on) =>
    setter((prev) => {
      const next = new Set(prev);
      if (on) next.add(objId);
      else next.delete(objId);
      return next;
    });

  /** POST the shortlist row. `jobId` null = pool only; set = pool + job link. */
  const postShortlist = (objId, pct, searchCtx, jobId) =>
    fetchWithAuth("/api/outreach/external-shortlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        objectId: objId,
        percentage_match: Number.isFinite(Number(pct)) ? Number(pct) : null,
        // Records which search surfaced this person, so Outreach can filter
        // sourced candidates by search title.
        reqNo: String(searchCtx?.reqNo || "").trim(),
        searchTitle: String(searchCtx?.title || "").trim(),
        jobId: jobId || null,
      }),
    });

  /* "Database" — membership of the candidate pool (Outreach), independent of
     any job. Removing from the pool also drops the job links that shortlisting
     created (the server does that), so the Shortlist pill clears with it. */
  const toggleDatabaseCandidate = React.useCallback(
    async (objId, pct, searchCtx) => {
      if (!objId || shortlistBusyIds.has(objId)) return;
      const isOn = databaseIds.has(objId);
      setMembership(setDatabaseIds, objId, !isOn);
      if (isOn) setMembership(setShortlistedIds, objId, false);
      await withBusy(objId, async () => {
        try {
          const res = isOn
            ? await fetchWithAuth(
                `/api/outreach/external-shortlist/${encodeURIComponent(objId)}`,
                { method: "DELETE" },
              )
            : await postShortlist(objId, pct, searchCtx, null);
          if (!res.ok) throw new Error("Failed");
        } catch {
          setMembership(setDatabaseIds, objId, isOn); // revert
        }
      });
    },
    [databaseIds, shortlistBusyIds, withBusy],
  );

  /* "Shortlist" — chosen for the job this search came from. Turning it on also
     puts the person in the database (you cannot shortlist someone you don't
     have); turning it off only cuts the job link, leaving them in the pool. */
  const toggleShortlistCandidate = React.useCallback(
    async (objId, pct, searchCtx) => {
      const jobId = String(searchCtx?.jobId || "").trim();
      if (!objId || !jobId || shortlistBusyIds.has(objId)) return;
      const isOn = shortlistedIds.has(objId);
      setMembership(setShortlistedIds, objId, !isOn);
      if (!isOn) setMembership(setDatabaseIds, objId, true);
      await withBusy(objId, async () => {
        try {
          const res = isOn
            ? await fetchWithAuth(
                `/api/candidates/${encodeURIComponent(objId)}/jobs/${encodeURIComponent(jobId)}`,
                { method: "DELETE" },
              )
            : await postShortlist(objId, pct, searchCtx, jobId);
          if (!res.ok) throw new Error("Failed");
        } catch {
          setMembership(setShortlistedIds, objId, isOn); // revert
        }
      });
    },
    [shortlistedIds, shortlistBusyIds, withBusy],
  );

  const openJobPreview = React.useCallback(() => {
    setPreviewOpen(true);
  }, []);

  /* What "See job posting" shows: the board job as is, or the typed title and description
     shaped like one (plain text, so it is escaped and line breaks are kept). */
  const previewJob = React.useMemo(() => {
    if (selectedBoardJobData) return selectedBoardJobData;
    const text = (description || "").trim();
    if (!title.trim() && !text) return null;
    const esc = (t) =>
      t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return {
      title: title.trim() || "Your search",
      description: text ? `<p>${esc(text).replace(/\n/g, "<br/>")}</p>` : "",
      source: anonymousHiring ? "Confidential" : hiringName.trim(),
    };
  }, [selectedBoardJobData, title, description]);

  // The company a board job is posted by, or the one named on the Hiring company step; anonymous names none.
  const hiringCompany = () =>
    sourceMode === "board"
      ? String(selectedBoardJobData?.source || "").trim()
      : anonymousHiring
        ? ""
        : hiringName.trim();

  // People mode: the user types the title; "Generate with AI" fills it from the description on request.
  const suggestTitle = async () => {
    const text = description.trim();
    if (!text || !user?.sub || titleLoading) return;
    setTitleLoading(true);
    setTitleError("");
    try {
      const res = await fetchWithAuth("/api/people-search/title", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sub: user.sub, search_id: draftSearchId(), description: text }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.title) throw new Error(body?.error || "Couldn't name this role.");
      setTitle((prev) => (prev.trim() ? prev : String(body.title)));
    } catch (err) {
      setTitleError(err?.message || "Couldn't name this role.");
    } finally {
      setTitleLoading(false);
    }
  };

  // The Hiring company step always opens blank: coming back to it (from either side) clears the
  // company pick and, in people mode, the job title too.
  React.useEffect(() => {
    if (screen !== "company") return;
    setHiringName("");
    setAnonymousHiring(false);
    setTitleError("");
    if (sourceMode === "people") {
      setTitle("");
      suggestTitle();
    }
  }, [screen, sourceMode]);

  // Manual + people searches run against a job of their own: create it (queued import, id minted now)
  // and use its id as the search's req_no, exactly as a board job does.
  const createJobForSearch = async () => {
    const res = await fetchWithAuth("/api/jobs/from-search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: title.trim(),
        description: description.trim(),
        company: hiringName.trim(),
        anonymous: anonymousHiring,
        countries: worldwide ? [] : countries,
      }),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body?.jobId) throw new Error(body?.error || "Could not save this role.");
    return String(body.jobId);
  };

  // Step: country → extract the filter brief (sync LLM call, no Apollo usage).
  const goToBriefScreen = async () => {
    if (!user?.sub) {
      setRunError("Sign in required before starting a search.");
      return;
    }
    setBriefError("");
    setBriefLoading(true);
    setAgentIntro({ done: false, turns: [], result: null });
    setScreen("brief");
    try {
      const res = await fetchWithAuth("/api/people-search/brief", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sub: user.sub,
          search_id: draftSearchId(),
          title,
          description,
          // The posting's own company: the backend never puts its current staff forward.
          hiring_company: hiringCompany(),
          countries,
          /* The picker already knows each country's ISO-2, so send them rather than make
             the backend re-derive them from the display names — the two country lists
             spell a fair few of them differently ("Turkey" vs "Türkiye"). */
          country_codes: isoListOf(countries),
          /* An explicit "no location filter", which an empty country alone can't say
             (that also means "not chosen yet"). The extractor must not infer a location
             from the description when this is set. */
          worldwide,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          body?.error || "Could not read the description. Please try again.",
        );
      }
      /* The backend is told `worldwide` and drops inferred locations itself; this is
         the belt-and-braces half, and also covers a brief extracted before the user
         switched to worldwide. The filter step can still add locations back by hand —
         that's a deliberate choice, unlike an inferred one. */
      const brief = normalizeBriefForEdit(body.brief);
      setBriefDraft(worldwide ? { ...brief, locations: [] } : brief);
    } catch (err) {
      setBriefError(
        errorText(err, "Could not read the description. Please try again."),
      );
    } finally {
      setBriefLoading(false);
    }
  };

  /* The assistant's result: the reply closes the turn on the rail, and an edited plan
     becomes the draft on the sheet. */
  const handleAgentDone = React.useCallback(
    (done) => {
      if (!done || !done.brief) return;
      setAgentIntro((p) => ({ ...p, result: done }));
      finishAgentTurn(done.reply || "");
      if (done.changed)
        setBriefDraft(
          normalizeBriefForEdit({
            ...done.brief,
            locations: worldwide ? [] : done.brief.locations,
          }),
        );
    },
    [worldwide, finishAgentTurn],
  );

  /* Every launch spends credits and cannot be stopped once it starts, so each one is
     confirmed first. Holds { kind, run } while the confirm dialog is up. */
  const [launchConfirm, setLaunchConfirm] = React.useState(null);
  // Strong candidates wanted; the run stops searching at that or at RUN_SEARCH_CREDITS, whichever comes first.
  const [runGoal, setRunGoal] = React.useState(10);
  const [goalCustom, setGoalCustom] = React.useState(false);
  /* The confirm dialog holds the launch function from the render it opened on, so the
     launch reads the goal through a ref — what was chosen, not what was there. */
  // The free trial searches for a fixed number of strong candidates; the server holds it there too.
  const trialGoal = planFeatures.fixedRunGoal || null;
  React.useEffect(() => {
    if (!trialGoal) return;
    setGoalCustom(false);
    setRunGoal(trialGoal);
  }, [trialGoal]);
  const runGoalRef = React.useRef(runGoal);
  runGoalRef.current = trialGoal || runGoal;
  // Extra credits on top of the default budget; the trial stays on the default (the server holds it there too).
  const [runExtra, setRunExtra] = React.useState(0);
  const [extraCustom, setExtraCustom] = React.useState(false);
  const runCreditsRef = React.useRef(RUN_SEARCH_CREDITS);
  runCreditsRef.current = RUN_SEARCH_CREDITS + (trialGoal ? 0 : runExtra);
  const briefDraftRef = React.useRef(briefDraft);
  briefDraftRef.current = briefDraft;
  const startFinding = async () => {
    if (!user?.sub) {
      setRunError("Sign in required before starting a search.");
      return;
    }
    // Belt and braces: the button is disabled while a run is live, and the server
    // 409s anyway, but never fire a second search from a stale click.
    if (searchBlocked) {
      setBriefError(
        "A search is already running. Wait for it to finish, or cancel it first.",
      );
      return;
    }
    clearFollowUpReturn();
    let derivedReqNo = deriveReqNo({
      sourceMode,
      selectedBoardJobData,
      title,
      description,
    });
    if (!derivedReqNo) {
      setRunError("Selected job is missing object id.");
      return;
    }
    if (sourceMode !== "board") {
      try {
        derivedReqNo = await createJobForSearch();
      } catch (err) {
        setRunError(err?.message || "Could not save this role.");
        return;
      }
    }

    setElapsedMs(0);
    setRunError("");

    try {
      setIsSubmitting(true);
      setLaunchAccepted(false);
      setSubmittedReqNo(derivedReqNo);
      // This call owns the waiting; stop the adopt effect starting a second waiter.
      adoptedReqNoRef.current = derivedReqNo;
      // No countries is the worldwide search; every picked one must resolve.
      if (countries.some((c) => !resolveCountryIso2(c))) {
        throw new Error("Please select a valid country.");
      }
      setRunError("");
      setScreen("finding");
      const res = await fetchWithAuth("/api/find-candidates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sub: user.sub,
          countries,
          country_codes: isoListOf(countries),
          worldwide,
          title,
          description,
          hiring_company: hiringCompany(),
          req_no: derivedReqNo,
          // The id the brief and filter turns were charged under; the run keeps it.
          search_id: draftSearchId(),
          // The plan and checklist the recruiter confirmed on the sheet.
          ...(briefDraft ? { brief: briefDraft } : {}),
          goal: runGoalRef.current,
          credits: runCreditsRef.current,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        /* Out of credits: nothing ran, so keep the filters and say so in place. */
        if (res.status === 402 || payload?.code === "insufficient_credits") {
          setBriefError(
            payload?.error ||
              "You're out of credits — add more to keep searching.",
          );
          setRunError("");
          setSubmittedReqNo("");
          setElapsedMs(0);
          setScreen("brief");
          return;
        }
        /* Not on their plan (a similar-candidate run on Starter): say which plan has it. */
        if (res.status === 403 && payload?.code === "plan_required") {
          setBriefError(payload?.error || "This is part of the Growth plan and up.");
          setRunError("");
          setSubmittedReqNo("");
          setElapsedMs(0);
          setScreen("brief");
          return;
        }
        /* Another search of theirs is still running — not a failure, just not now.
           Stay on the filters so nothing they typed is lost. */
        if (res.status === 409) {
          setBriefError(
            payload?.error ||
              "A search is already running. Wait for it to finish, or cancel it first.",
          );
          setRunError("");
          setSubmittedReqNo("");
          setElapsedMs(0);
          setScreen("brief");
          // Whatever is actually running, pick it up so the dock appears.
          refreshRun();
          return;
        }
        if (res.status === 429) {
          setRunError(
            payload?.error ||
              payload?.message ||
              "You have reached the rate limit. Please try again in 10 minutes.",
          );
          setSubmittedReqNo("");
          setElapsedMs(0);
          setScreen("rateLimit");
          return;
        }
        throw new Error(
          payload?.error || payload?.message || "Failed to find candidates.",
        );
      }

      setLaunchAccepted(true);
      if (payload?.jobId) {
        await waitForFindCandidatesByReqNo(user.sub, derivedReqNo, handleFindPoll);
        creditsChanged();
      }
      await applyFindCandidatesOutcome(derivedReqNo);
    } catch (err) {
      setRunError(errorText(err, "Failed to find candidates."));
      setSubmittedReqNo("");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Picking happens down in the list, so jump there instead of leaving the user at the
     top wondering what changed. Waits a frame for the pick bar to mount, and honours
     the reduced-motion preference rather than always animating. */
  React.useEffect(() => {
    if (!pickingMode) return undefined;
    const frame = requestAnimationFrame(() => {
      const reduced = window.matchMedia?.(
        "(prefers-reduced-motion: reduce)",
      )?.matches;
      pickAreaRef.current?.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "start",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [pickingMode]);

  /* ─── Another round of an existing search ─────────────────────────────
     Both follow-ups (more like these / adjusted filters) run as the next batch of the
     same search: the backend dedupes them against everyone already delivered, so the
     user only ever sees people they have not seen. */

  const MAX_SEEDS = 5;

  /* "Continue" from a past search runs the next round straight away with the confirmed
     filters — the agent adjusts them as it goes, so there is no filter step to revisit.
     Confirmed first, like every launch. */
  const startFollowUpRef = React.useRef(null);

  const toggleSeed = React.useCallback((objId) => {
    if (!objId) return;
    setSeedIds((prev) => {
      const next = new Set(prev);
      if (next.has(objId)) next.delete(objId);
      else if (next.size < MAX_SEEDS) next.add(objId);
      return next;
    });
  }, []);

  const startFollowUpSearch = React.useCallback(
    async (item, mode, briefOverride = null) => {
      if (!user?.sub || !item?.searchId || !item?.req_no) {
        setFollowUpError("This search can't be extended.");
        return;
      }
      if (searchBlocked) {
        setFollowUpError(
          "A search is already running. Wait for it to finish, or cancel it first.",
        );
        return;
      }
      setFollowUpError("");
      setFollowUpMode(mode);
      /* Counts as running from the moment we ask, not from when the server first
         reports it — otherwise the gap would read as idle and the progress screen
         would be bounced straight back to the landing. */
      setIsSubmitting(true);
      // A follow-up runs the same pipeline as a first search, so it gets the same
      // running screen rather than a spinner on a button. Back here when it lands.
      const returnTo = `/find-candidates/history/${item.id}`;
      let onRunningScreen = false;
      const clearRun = () => {
        clearFollowUpReturn();
        setSubmittedReqNo("");
        setElapsedMs(0);
        setScreen("home");
        if (adoptedReqNoRef.current === item.req_no)
          adoptedReqNoRef.current = "";
      };
      try {
        const res = await fetchWithAuth("/api/find-candidates", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sub: user.sub,
            title: item.title || "",
            description: item.jobDescription || "",
            req_no: item.req_no,
            countries: itemCountries(item),
            country_codes: isoListOf(itemCountries(item)),
            search_id: item.searchId,
            mode,
            goal: runGoalRef.current,
            credits: runCreditsRef.current,
            ...(mode === "lookalike" ? { seed_ids: Array.from(seedIds) } : {}),
            // Same agent-driven run as a first search, minus the filter step.
            ...(mode !== "lookalike" ? { agent: true, locked: [] } : {}),
            ...(briefOverride || item.brief
              ? { brief: briefOverride || item.brief }
              : {}),
          }),
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            payload?.error ||
              payload?.message ||
              "Couldn't start another round of this search.",
          );
        }
        setSeedIds(new Set());
        setPickingMode(false);
        if (payload?.jobId) {
          /* The running screen lives on the base route, so the round to come back to
             is recorded rather than held in state — a remount mid-run would lose it. */
          onRunningScreen = true;
          setSubmittedReqNo(item.req_no);
          // This call owns the waiting; stop the adopt effect starting a second waiter.
          adoptedReqNoRef.current = item.req_no;
          writeFollowUpReturn(item.req_no, returnTo);
          navigate("/find-candidates", { state: { followUpStarted: true } });
          await waitForFindCandidatesByReqNo(
            user.sub,
            item.req_no,
            handleFindPoll,
          );
        }
        // Reload the search so the new round appears, then leave the running screen
        // straight onto it — clearing it any earlier would flash the landing page.
        const refreshed = await fetchHistoryItem(item.id);
        const newest = (refreshed?.batches || [])
          .slice()
          .sort((a, b) => (b.batchNo || 0) - (a.batchNo || 0))[0];
        loadPastSearchesRef.current?.(false);
        clearRun();
        // Only someone still watching this round's progress is taken to it; anyone who
        // moved on finds it in their history.
        if (newest?.id && newest.id !== item.id) {
          if (onRunningScreen) navigate(`/find-candidates/history/${newest.id}`);
        } else {
          if (onRunningScreen) navigate(returnTo);
          if (refreshed) setHistoryItemFromPath(refreshed);
        }
      } catch (err) {
        const msg = errorText(err, "Couldn't start another round of this search.");
        clearRun();
        // Don't strand the user on the running screen. The round's own effects wipe
        // followUpError on arrival, so the message rides along in route state.
        if (onRunningScreen)
          navigate(returnTo, { state: { followUpError: msg } });
        else setFollowUpError(msg);
      } finally {
        setFollowUpMode("");
        setIsSubmitting(false);
      }
    },
    [
      user?.sub,
      seedIds,
      searchBlocked,
      waitForFindCandidatesByReqNo,
      handleFindPoll,
      fetchHistoryItem,
      navigate,
    ],
  );

  startFollowUpRef.current = startFollowUpSearch;

  const activeStageId = getActiveStageId(elapsedMs);
  // Drive the pipeline UI from the job doc's real stage/funnel.
  const liveStageIndex = activeRun?.stage
    ? REAL_STAGES.findIndex((s) => s.key === activeRun.stage)
    : -1;
  const usingRealStages = liveStageIndex >= 0;
  const liveFunnel = activeRun?.funnel || null;
  /* The feed always rides its newest line; what has passed slides out of view. */
  const feedRef = React.useRef(null);
  const [feedClipped, setFeedClipped] = React.useState(false);
  const feedTail = (liveFunnel?.feed || []).slice(-1)[0];
  const feedKey = `${liveFunnel?.feed?.length || 0}:${feedTail?.id || ""}:${feedTail?.state || ""}`;
  React.useEffect(() => {
    const pin = () => {
      const el = feedRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      setFeedClipped(el.scrollHeight > el.clientHeight + 1);
    };
    pin();
    window.addEventListener("resize", pin);
    return () => window.removeEventListener("resize", pin);
  }, [feedKey, screen]);

  /* The filters the run was launched with: the server's copy once it answers (so a
     reload or another tab shows them too), the sheet's draft in the moment before. */
  const runFilters = React.useMemo(() => {
    const src = activeRun || { brief: briefDraft, country: countries.join(", "), worldwide };
    return src.brief
      ? { brief: src.brief, country: src.country, worldwide: src.worldwide }
      : null;
  }, [activeRun, briefDraft, countries, worldwide]);
  /* An agent-driven run has no fixed stage order: it searches, opens and scores in
     whatever order the scores call for, so the sheet shows its own ledger plus a feed
     of what it is doing right now. */
  const liveAgent = !!liveFunnel?.agent;
  // The agent reports `ranked` only once, after its last batch: from then on it is ranking.
  const agentRanking = liveAgent && activeRun?.stage === "rank" && liveFunnel.ranked != null;
  const displayStages = usingRealStages
    ? REAL_STAGES.map((s, i) => ({ id: i + 1, label: s.label }))
    : STAGES;
  const displayActiveId = usingRealStages ? liveStageIndex + 1 : activeStageId;
  // Live funnel figure for a pipeline row on the running sheet ("" when unknown).
  const runStageDetail = (stageId) => {
    if (!usingRealStages || !liveFunnel) return "";
    const key = REAL_STAGES[stageId - 1]?.key;
    if (key === "search")
      return liveFunnel.screened != null
        ? `${formatNumber(liveFunnel.screened)} profiles screened`
        : "";
    if (key === "prerank")
      return liveFunnel.matched != null
        ? `${formatNumber(liveFunnel.matched)} match your filters`
        : "";
    if (key === "enrich")
      return liveFunnel.shortlisted != null
        ? `${formatNumber(liveFunnel.shortlisted)} shortlisted`
        : "";
    if (key === "rank")
      return liveFunnel.ranked != null
        ? `${formatNumber(liveFunnel.ranked)} ranked`
        : "";
    return "";
  };
  const isPeopleMode = sourceMode === "people";
  const screenOrder = SCREEN_ORDER[sourceMode] || SCREEN_ORDER.board;
  const stepLabels = STEP_LABELS[sourceMode] || STEP_LABELS.board;
  const setupStep = screenOrder.indexOf(screen) + 1; // 0 when not in the wizard

  const briefHasSearchables = Boolean(briefDraft && planOf(briefDraft).positions.length);
  /* A run that starts mid-wizard (another tab, or a follow-up) stops it advancing:
     the country step spends an LLM call on the brief, which should not be paid for a
     search the server is going to refuse. */
  const canGoNext =
    searchBlocked && screen !== "finding"
      ? false
      : screen === "input"
        ? isPeopleMode
          ? Boolean(description.trim())
          : sourceMode === "manual"
            ? Boolean(title.trim() && description.trim()) && (anonymousHiring || Boolean(hiringName.trim()))
            : Boolean(selectedBoardJobData)
        : screen === "company"
          ? (anonymousHiring || Boolean(hiringName.trim())) && Boolean(title.trim()) && !titleLoading
          : screen === "country"
            ? countries.length > 0 || worldwide
            : false;

  // Back discards what was entered on the step being left, so stepping forward again starts blank.
  const handleSetupPrev = () => {
    if (screen === "source") {
      resetDraftForm();
      setSourceMode("");
      setScreen("home");
      return;
    }
    if (screen === "input") {
      modeDraftsRef.current = {};
      setSelectedBoardJobData(null);
      setDescription("");
    }
    // The JD step and the person search's role step hold the title and hiring company.
    if (screen === "input" || screen === "company") {
      setTitle("");
      setTitleError("");
      setHiringName("");
      setAnonymousHiring(false);
    }
    // A picked job also filled in its country.
    if (screen === "input" || screen === "country") {
      setCountries([]);
      setWorldwide(false);
    }
    const i = screenOrder.indexOf(screen);
    if (i > 0) setScreen(screenOrder[i - 1]);
  };

  const handleSetupNext = () => {
    if (screen === "country") {
      goToBriefScreen();
      return;
    }
    const i = screenOrder.indexOf(screen);
    if (i >= 0 && i + 1 < screenOrder.length) setScreen(screenOrder[i + 1]);
  };

  const onBaseFindCandidates =
    location.pathname === "/find-candidates" && !historyId;
  // Only block first paint until auth is known. The in-flight-job resume probe
  // (status-by-req-no → BFF → Python → Mongo) runs in the BACKGROUND; if it finds
  // a running job it flips to the "finding" screen, so we don't gate the landing
  // on that network round-trip.
  const showResumeBootstrap =
    onBaseFindCandidates &&
    !location.state?.selectedJob &&
    !location.state?.resumeSource &&
    authLoading;

  /* The editable filters: the same chips wherever they show — on the working sheet for a
     new search, and on the review screen if the sheet ever has to drop (a brief error). */
  /* Filter rows the recruiter opened that hold nothing yet, so a row does not vanish
     the moment it is opened. */
  const [openRows, setOpenRows] = React.useState([]);
  /* Every plan edit lands on the draft's own positions / keywords / employers, so the run starts from exactly what the rows show. */
  const updatePlan = React.useCallback((fn) => {
    setBriefDraft((p) => ({ ...p, ...planOf(fn(planOf(p))) }));
  }, []);

  if (showResumeBootstrap) {
    // Skeleton of the landing layout: no spinner-to-content jump when the
    // resume probe and history calls return.
    return (
      <Page>
        <TopBar>
          <Crumbs>
            <Crumb as="span" style={{ cursor: "default" }}>
              Sourcing
            </Crumb>
            <CrumbSep>/</CrumbSep>
            <Crumb $current>People</Crumb>
          </Crumbs>
        </TopBar>
        <Canvas>
          <Screen>
            <LandingHero>
              <Skel $w="118px" $h="24px" $r="99px" />
              <Skel $w="min(640px, 85%)" $h="48px" style={{ marginTop: 20 }} />
              <Skel $w="min(420px, 60%)" $h="15px" style={{ marginTop: 16 }} />
              <Skel $w="min(330px, 48%)" $h="15px" style={{ marginTop: 8 }} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 26 }}>
                <Skel $w="168px" $h="38px" $r="10px" />
                <Skel $w="168px" $h="38px" $r="10px" />
              </div>
            </LandingHero>
            <LandingGrid>
              <LandingSection>
                <Skel $w="150px" $h="12px" style={{ marginBottom: 14 }} />
                <PastGrid>
                  {[0, 1, 2, 3].map((i) => (
                    <PastCardSkeleton key={i} />
                  ))}
                </PastGrid>
              </LandingSection>
              <LandingSection>
                <Skel $w="110px" $h="12px" style={{ marginBottom: 14 }} />
                <QuickGrid>
                  {[0, 1, 2, 3].map((i) => (
                    <SkelCard key={i}>
                      <Skel $w="32px" $h="32px" $r="8px" />
                      <Skel $w="80%" $h="13px" style={{ marginTop: 12 }} />
                      <Skel $w="60%" $h="11px" style={{ marginTop: 7 }} />
                    </SkelCard>
                  ))}
                </QuickGrid>
              </LandingSection>
            </LandingGrid>
          </Screen>
        </Canvas>
      </Page>
    );
  }
  // ─── Step indicator (scoutm8-style) ───────────────────────────
  const renderStepDots = () => (
    <StepDotsWrap>
      <StepDotsLabel>
        <StepDotsNum>
          Step {setupStep} of {stepLabels.length}
        </StepDotsNum>
        <StepDotsCurrent>{stepLabels[setupStep - 1]}</StepDotsCurrent>
      </StepDotsLabel>
      <StepDotsBar>
        {stepLabels.map((_, idx) => {
          const n = idx + 1;
          const state =
            n < setupStep ? "done" : n === setupStep ? "current" : "todo";
          return (
            <React.Fragment key={n}>
              <StepDot $state={state}>
                {state === "done" ? <FiCheck size={12} /> : n}
              </StepDot>
              {n < stepLabels.length && <StepLine $done={state === "done"} />}
            </React.Fragment>
          );
        })}
      </StepDotsBar>
    </StepDotsWrap>
  );

  const inWizard =
    screen === "source" ||
    screen === "input" ||
    screen === "company" ||
    screen === "country" ||
    screen === "brief" ||
    screen === "finding";
  const onHistoryRoute = location.pathname.startsWith(
    "/find-candidates/history",
  );
  // A follow-up round runs the same pipeline but has no wizard behind it.
  const showStepDots = inWizard && !followUpRunFrom;

  const renderTopBar = () => {
    let current = "";
    if (screen === "source") current = "New search";
    else if (screen === "input")
      current = isPeopleMode ? "Describe the person" : sourceMode === "manual" ? "Job description" : "Pick a job";
    else if (screen === "country") current = "Country";
    else if (screen === "brief") current = "Filters";
    else if (screen === "finding")
      current = followUpRunFrom ? "Another round" : "Finding candidates";
    else if (screen === "rateLimit") current = "Rate limit";
    if (onHistoryRoute && !historyId) current = "Past searches";
    if (onHistoryRoute && historyId) {
      const item =
        historyItemFromPath || pastSearches.find((s) => s.id === historyId);
      current = item?.title || "Search results";
    }

    return (
      <TopBar>
        <Crumbs>
          <Crumb as="span" style={{ cursor: "default" }}>
            Sourcing
          </Crumb>
          <CrumbSep>/</CrumbSep>
          <Crumb
            onClick={() => {
              if (screen === "home" && !onHistoryRoute) return;
              resetDraftForm();
              setElapsedMs(0);
              setExpandedCandidateObjectId(null);
              setScreen("home");
              navigate("/find-candidates");
            }}
            $current={screen === "home" && !onHistoryRoute}
          >
            People
          </Crumb>
          {current && (
            <>
              <CrumbSep>/</CrumbSep>
              <Crumb $current>{current}</Crumb>
            </>
          )}
        </Crumbs>
        {showStepDots && renderStepDots()}
      </TopBar>
    );
  };

  /* One search at a time. Every entry point routes through this so a blocked start
     is impossible to reach rather than rejected four steps later — by which point
     the brief extraction and the preview have both been paid for. */
  const blockedReason = searchBlocked
    ? "A search is already running. Running two at once is part of the Growth plan; wait for this one to finish, or cancel it."
    : "";
  const startNewSearch = (mode) => {
    if (searchBlocked) return;
    resetDraftForm();
    if (mode) setSourceMode(mode);
    setScreen(mode ? "input" : "source");
  };

  /* Shown wherever the progress screen itself isn't — including while sitting on a
     past search, which is the whole point of it. */
  /* The run on the progress screen has no entry of its own. One other run is a bar; several
     collapse into one bar that opens into a list, each row opening that run's progress. */
  const openRun = (run) => {
    watchRun(run.reqNo);
    setRunError("");
    setScreen("finding");
    setDockOpen(false);
    if (onHistoryRoute) navigate("/find-candidates");
  };
  const stageOf = (run) => {
    const stageIdx = run.stage ? REAL_STAGES.findIndex((s) => s.key === run.stage) : -1;
    return {
      label: stageIdx >= 0 ? REAL_STAGES[stageIdx].label : "Getting started",
      pct: stageIdx >= 0 ? ((stageIdx + 1) / REAL_STAGES.length) * 100 : 4,
    };
  };
  const renderRunDock = () => {
    // Only on the People landing: not over a past search, the setup steps or a progress screen.
    if (screen !== "home" || onHistoryRoute) return null;
    const runs = activeRuns.length ? activeRuns : activeRun ? [activeRun] : [];
    if (!runs.length) return null;
    if (runs.length === 1) {
      const run = runs[0];
      const { label, pct } = stageOf(run);
      return (
        <RunDockStack data-tour="fc-dock">
          <RunDockBar type="button" onClick={() => openRun(run)} aria-label="View search progress">
            <RunDockMain>
              <RunDockTitle>
                <PulseDot />
                <span className="name">{run.title ? `Searching · ${run.title}` : "Search running"}</span>
              </RunDockTitle>
              <RunDockSub>{label}</RunDockSub>
              <RunDockTrack>
                <RunDockFill $pct={pct} />
              </RunDockTrack>
            </RunDockMain>
            <RunDockCta>View progress →</RunDockCta>
          </RunDockBar>
        </RunDockStack>
      );
    }
    const overall = runs.reduce((n, r) => n + stageOf(r).pct, 0) / runs.length;
    return (
      <RunDockStack data-tour="fc-dock">
        <RunDockBar type="button" onClick={() => setDockOpen((v) => !v)} aria-expanded={dockOpen} aria-label="Searches running">
          <RunDockMain>
            <RunDockTitle>
              <PulseDot />
              <span className="name">{runs.length} searches running</span>
            </RunDockTitle>
            <RunDockSub>{runs.map((r) => r.title || "Untitled").join(" · ")}</RunDockSub>
            <RunDockTrack>
              <RunDockFill $pct={overall} />
            </RunDockTrack>
          </RunDockMain>
          <RunDockCta>{dockOpen ? "Hide" : "See all →"}</RunDockCta>
        </RunDockBar>
        {dockOpen && (
          <RunDockList>
            {runs.map((run) => {
              const { label, pct } = stageOf(run);
              return (
                <RunDockRow key={run.reqNo || run.jobId} type="button" onClick={() => openRun(run)}>
                  <RunDockMain>
                    <RunDockTitle>
                      <PulseDot />
                      <span className="name">{run.title || "Search running"}</span>
                    </RunDockTitle>
                    <RunDockSub>{label}</RunDockSub>
                    <RunDockTrack>
                      <RunDockFill $pct={pct} />
                    </RunDockTrack>
                  </RunDockMain>
                  <RunDockCta>View →</RunDockCta>
                </RunDockRow>
              );
            })}
          </RunDockList>
        )}
      </RunDockStack>
    );
  };

  /** Hiring company + "Keep it confidential": on the JD step, or the person search's role step. */
  const renderHiringField = () => (
    <Field data-tour="fc-hiring" style={{ marginBottom: 0 }}>
      <FormLabel as="span">Hiring company</FormLabel>
      {!anonymousHiring && (
        <CompanyPicker
          value={hiringName}
          onChange={setHiringName}
          placeholder="Start typing a company name…"
        />
      )}
      <ConfidentialRow $on={anonymousHiring}>
        <input
          type="checkbox"
          checked={anonymousHiring}
          onChange={(e) => {
            setAnonymousHiring(e.target.checked);
            if (e.target.checked) setHiringName("");
          }}
        />
        <span>
          <b>Keep it confidential</b>
          <small>The role shows as Confidential and no company is ruled out.</small>
        </span>
      </ConfidentialRow>
    </Field>
  );

  /** A setup step's question with Back / Continue beside it. */
  const renderStepHead = (eyebrow, heading, sub) => (
    <WizardHeadRow>
      <ScreenHead>
        <Eyebrow>{eyebrow}</Eyebrow>
        <ScreenTitle>{heading}</ScreenTitle>
        <ScreenSub>{sub}</ScreenSub>
      </ScreenHead>
      <WizardActions>
        <Button onClick={handleSetupPrev} data-tour="fc-back">
          ← Back
        </Button>
        {screen !== "source" && (
          <Button
            $accent
            $lg
            disabled={!canGoNext}
            title={blockedReason || undefined}
            onClick={handleSetupNext}
            data-tour="fc-continue"
          >
            <ButtonContent>
              {searchBlocked && !isSubmitting ? <>Search in progress…</> : <>Continue →</>}
            </ButtonContent>
          </Button>
        )}
      </WizardActions>
    </WizardHeadRow>
  );

  // ─── Ranking list renderer ───
  /** Green "✓ Google" chips showing which of the user's employer filters a
   *  candidate actually meets (current or past). */
  const renderMatchBadges = (fm) => {
    if (!fm) return null;
    const badges = (fm.employers || []).map((c) => `✓ ${c}`);
    if (badges.length === 0) return null;
    return (
      <MatchBadgeRow>
        {badges.map((b, i) => (
          <MatchBadge key={i}>{b}</MatchBadge>
        ))}
      </MatchBadgeRow>
    );
  };

  // What the search actually ran with. Only searches run after the brief started being
  // persisted carry `brief` — older ones show a short note instead of a misleading blank.
  const renderSearchRecap = (item) => {
    const brief = item?.brief;
    const jd = (item?.jobDescription || "").trim();
    if (!brief && !jd) return null;

    const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
    const filters = briefFilterRows(brief, item.country);
    // Only employers the recruiter set themselves are worth a note; companies the run thought of on its own are not theirs to fix.
    const ownEmployers = new Set(planOf(brief).employers.map((e) => e.toLowerCase()));
    const notApplied = list(item?.unresolved).filter((n) => ownEmployers.has(String(n).toLowerCase()));
    const chipCount = filters.reduce((n, f) => n + f.chips.length, 0);

    return (
      <RecapCard>
        <RecapHeadRow
          type="button"
          $open={searchRecapOpen}
          aria-expanded={searchRecapOpen}
          onClick={() => setSearchRecapOpen((v) => !v)}
        >
          <RecapHead>What we searched for</RecapHead>
          <RecapHeadRight>
            {chipCount > 0 && (
              <RecapCount>
                {chipCount} filter{chipCount !== 1 ? "s" : ""} applied
              </RecapCount>
            )}
            <RecapChevron $open={searchRecapOpen} aria-hidden="true" />
          </RecapHeadRight>
        </RecapHeadRow>

        {searchRecapOpen && (
          <>
            {filters.length > 0 && (
              <RecapRows>
                {filters.map((f) => (
                  <RecapRow key={f.label}>
                    <RecapLabel>{f.label}</RecapLabel>
                    <RecapChips>
                      {f.chips.map((c, i) => (
                        <RecapChip
                          key={`${f.label}-${i}`}
                          $tone={f.tone}
                          $state={c.state}
                        >
                          {c.text}
                        </RecapChip>
                      ))}
                    </RecapChips>
                  </RecapRow>
                ))}
              </RecapRows>
            )}

            {!brief && (
              <RecapMissing>
                Filters weren't recorded for this search — only searches run
                from now on store them.
              </RecapMissing>
            )}

            {notApplied.length > 0 && (
              <RecapNotApplied style={{ marginTop: 14 }}>
                Not applied: <strong>{notApplied.join(", ")}</strong> —
                couldn't be matched to a known company.
              </RecapNotApplied>
            )}
          </>
        )}
      </RecapCard>
    );
  };

  /* Every checklist item with its tick state: the screen returns only the ones met, so
     misses come from the stored brief, on the same M1/N1 ids renidly_pipeline assigns. */
  const buildChecklist = (c, brief) => {
    const group = (briefList, met, cap) => {
      const metById = new Map(
        (met || []).map((m) => [m.id, (m.text || "").trim()]),
      );
      const fromBrief = (briefList || [])
        .filter(Boolean)
        .slice(0, MAX_CRITERIA)
        .map((text, i) => ({
          text,
          met: metById.has(`${cap}${i + 1}`),
        }));
      if (fromBrief.length) return fromBrief;
      return [...metById.values()]
        .filter(Boolean)
        .map((text) => ({ text, met: true }));
    };
    return {
      musts: group(brief?.must_haves, c.must_haves_met, "M"),
      nices: group(brief?.nice_to_haves, c.nice_to_haves_met, "N"),
    };
  };

  // Must/nice-have tick counts under the candidate's name. Hover (or tap, on touch)
  // either chip for the full checklist, since the counts alone don't say which.
  const renderCriteriaBadges = (entry, rowKey, brief, tourMark = false) => {
    const c = entry?.criteria;
    if (!c) return null;
    const chips = [];
    if (c.must_haves_total > 0) {
      chips.push({
        kind: "must",
        label: `${c.must_haves_met_count}/${c.must_haves_total} must`,
      });
    }
    if (c.nice_to_haves_total > 0) {
      chips.push({
        kind: "nice",
        label: `${c.nice_to_haves_met_count}/${c.nice_to_haves_total} nice`,
      });
    }
    if (!chips.length) return null;

    const { musts, nices } = buildChecklist(c, brief);
    const itemsFor = (kind) => (kind === "must" ? musts : nices);
    const groupLabel = (kind) =>
      kind === "must" ? "Must-haves" : "Nice-to-haves";

    return (
      <TickRow data-tour={tourMark ? "fc-results-criteria" : undefined}>
        {chips.map((chip) => {
          const popId = `${rowKey}-${chip.kind}`;
          const items = itemsFor(chip.kind);
          const open = criteriaPop?.id === popId;
          const show = (e) =>
            openCriteriaPop(
              e.currentTarget,
              popId,
              groupLabel(chip.kind),
              items,
            );
          return (
            <TickChip
              key={chip.kind}
              type="button"
              data-criteria-chip=""
              $kind={chip.kind}
              aria-describedby={open ? CRITERIA_POP_ID : undefined}
              aria-expanded={open}
              // The row opens the candidate's profile; the chip must not.
              onClick={(e) => e.stopPropagation()}
              // Touch has no hover: a tap toggles the same popover.
              onPointerDown={(e) => {
                if (e.pointerType !== "touch") return;
                e.stopPropagation();
                if (open) closeCriteriaPop();
                else show(e);
              }}
              onMouseEnter={canHover ? show : undefined}
              onMouseLeave={canHover ? closeCriteriaPop : undefined}
              // Only keyboard focus opens it — a tap already focuses the chip, and
              // that would fight the toggle above.
              onFocus={(e) => {
                if (isKeyboardFocus(e.currentTarget)) show(e);
              }}
              onBlur={closeCriteriaPop}
            >
              {chip.label}
            </TickChip>
          );
        })}
      </TickRow>
    );
  };

  /* One popover at a time, portalled out of the ranking card so it can't be clipped.
     Placed above the chip when there's room, below when there isn't. */
  const renderCriteriaPop = () => {
    if (!criteriaPop || typeof document === "undefined") return null;
    const { label, items, x, top, bottom } = criteriaPop;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Anchored by whichever edge faces the chip, so the popover's own height never
    // enters the maths — a wrapping criterion can't shift it off its anchor.
    const above = top > vh - bottom;
    // Clamp against the width the popover can actually reach, which on a narrow
    // screen is the viewport, not the 380px cap.
    const half = Math.min(380, vw - 24) / 2;
    return createPortal(
      <CriteriaPop
        id={CRITERIA_POP_ID}
        role="tooltip"
        style={{
          left: Math.min(
            Math.max(x, 12 + half),
            Math.max(12 + half, vw - 12 - half),
          ),
          ...(above ? { bottom: vh - top + 8 } : { top: bottom + 8 }),
          transform: "translateX(-50%)",
        }}
      >
        <CriteriaGroupLabel>{label}</CriteriaGroupLabel>
        {items.map((it, i) => (
          <CriteriaLine key={i} $met={it.met}>
            <CriteriaMark $met={it.met} aria-hidden="true">
              {it.met ? "✓" : "✗"}
            </CriteriaMark>
            <span>
              <VisuallyHidden>{it.met ? "Met: " : "Not met: "}</VisuallyHidden>
              {it.text}
            </span>
          </CriteriaLine>
        ))}
      </CriteriaPop>,
      document.body,
    );
  };

  /* Checklist filter on the results, shared by the heading control and the list. Ids
     follow the badge convention: M1.. for must-haves, N1.. for nice-to-haves, in brief
     order, matched against what each row met when it was scored. */
  const criteriaFilterItems = (brief) => [
    ...((brief?.must_haves || []).slice(0, MAX_CRITERIA).map((text, i) => ({ id: `M${i + 1}`, text, kind: "must" }))),
    ...((brief?.nice_to_haves || []).slice(0, MAX_CRITERIA).map((text, i) => ({ id: `N${i + 1}`, text, kind: "nice" }))),
  ];
  const entryMetIds = (entry) => {
    const c = entry?.criteria || {};
    return new Set(
      [...(c.must_haves_met || []), ...(c.nice_to_haves_met || [])]
        .map((m) => (typeof m === "string" ? m : m?.id))
        .filter(Boolean),
    );
  };
  const passesCriteriaFilter = (entry) => {
    if (criteriaFilter.size === 0) return true;
    const met = entryMetIds(entry);
    for (const id of criteriaFilter) if (!met.has(id)) return false;
    return true;
  };
  const toggleCriterion = (id) =>
    setCriteriaFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleBulk = (objId) =>
    setBulkIds((prev) => {
      const next = new Set(prev);
      if (next.has(objId)) next.delete(objId);
      else next.add(objId);
      return next;
    });
  /* Everyone currently on screen: passes the checklist filter, and is not tucked behind
     the weaker-matches toggle unless that is open. */
  const shownRankingIds = (ranking) =>
    Object.values(ranking || {})
      .filter((e) => e?.object_id && passesCriteriaFilter(e))
      .filter((e) => showDisqualified || (e.relevant !== false && (Number(e.percentage_match) || 0) >= MIN_VISIBLE_SCORE))
      .map((e) => e.object_id);
  /* Add the selection to the database (jobId null) or the job's shortlist (jobId set),
     a few at a time; a failed row is put back the way it was. */
  const bulkAdd = async (ranking, searchCtx, jobId) => {
    const pctOf = new Map(Object.values(ranking || {}).map((e) => [e?.object_id, e?.percentage_match]));
    const targets = [...bulkIds].filter((id) => (jobId ? !shortlistedIds.has(id) : !databaseIds.has(id)));
    if (!targets.length) return;
    setBulkBusy(true);
    targets.forEach((id) => {
      setMembership(setDatabaseIds, id, true);
      if (jobId) setMembership(setShortlistedIds, id, true);
    });
    const queue = [...targets];
    let failed = 0;
    await Promise.all(
      Array.from({ length: Math.min(4, queue.length) }, async () => {
        while (queue.length) {
          const id = queue.shift();
          try {
            const res = await postShortlist(id, pctOf.get(id), searchCtx, jobId);
            if (!res.ok) throw new Error("Failed");
          } catch {
            failed += 1;
            if (jobId) setMembership(setShortlistedIds, id, false);
            else setMembership(setDatabaseIds, id, false);
          }
        }
      }),
    );
    setBulkBusy(false);
    if (failed) setFollowUpError(`${failed} of ${targets.length} could not be added. Try them again.`);
  };
  /* Download the selection as a CSV, in list order. The list only holds a lean profile per person, so the
     full one (skills, every role, education) is fetched for each selected person first, a few at a time. */
  const exportCsv = async (ranking, item) => {
    const entries = Object.values(ranking || {})
      .filter((e) => e?.object_id && bulkIds.has(e.object_id))
      .sort((a, b) => (Number(b.percentage_match) || 0) - (Number(a.percentage_match) || 0) || parseInt(a.rank, 10) - parseInt(b.rank, 10));
    if (!entries.length) return;
    setBulkBusy(true);
    const profiles = {};
    const queue = entries.map((e) => e.object_id).filter((id) => !candidateDetails[id]?._full);
    await Promise.all(
      Array.from({ length: Math.min(5, queue.length) }, async () => {
        while (queue.length) {
          const id = queue.shift();
          try {
            const res = await fetchWithAuth(`/api/linkedin-candidates/${id}`);
            if (!res.ok) continue;
            const data = await res.json();
            if (data?.linkdapi_profile) profiles[id] = data.linkdapi_profile;
          } catch {
            // The lean block already on the page stands in for this person.
          }
        }
      }),
    );
    // Keep the full blocks so opening these people later is instant.
    if (Object.keys(profiles).length) {
      setCandidateDetails((prev) => {
        const next = { ...prev };
        Object.entries(profiles).forEach(([id, pp]) => {
          next[id] = { ...(prev[id] || {}), linkdapi_profile: pp, _full: true };
        });
        return next;
      });
    }
    const rows = entries.map((e, i) => {
      const d = candidateDetails[e.object_id] || {};
      const { place, country } = locationCells(d.location);
      const pc = profileCells(profiles[e.object_id] || d.linkdapi_profile, formatLanguageProficiency);
      return [
        i + 1,
        getCandidateDisplayName(d),
        getCandidateSubtitle(d),
        (d.company || "").trim(),
        place,
        country,
        getCandidateLink(d),
        e.relevant === false ? (e.excluded ? "Excluded" : "Wrong level") : matchLevelOf(e).label,
        (e.why || "").trim(),
        pc.experience,
        pc.education,
        pc.skills,
        pc.languages,
        pc.certifications,
        pc.summary,
      ];
    });
    setBulkBusy(false);
    downloadCsv(
      csvFileName(item.title || "candidates"),
      toCsv(
        ["#", "Name", "Title", "Company", "Location", "Country", "LinkedIn", "Match", "Description", "Experience", "Education", "Skills", "Languages", "Certifications", "Summary"],
        rows,
      ),
    );
  };
  const renderBulkBar = (item, ranking) => {
    if (!bulkMode) return null;
    const n = bulkIds.size;
    const shown = shownRankingIds(ranking);
    const jobId = String(item.jobId || "").trim();
    const searchCtx = { reqNo: item.req_no || "", title: item.title || "", jobId };
    const toDb = [...bulkIds].filter((id) => !databaseIds.has(id)).length;
    const toJob = jobId ? [...bulkIds].filter((id) => !shortlistedIds.has(id)).length : 0;
    return (
      <PickBar>
        <PickBarText>
          {n === 0 ? "Click people to select them" : `${n} selected`}
          <PickBarSub>
            {shown.length > n
              ? `${shown.length} in this list — select them all, or pick one by one.`
              : "Add them to your database, shortlist them for this job, or export them as a CSV."}
          </PickBarSub>
        </PickBarText>
        <Button $sm disabled={bulkBusy || shown.length === n} onClick={() => setBulkIds(new Set(shown))}>
          Select all {shown.length}
        </Button>
        <Button $sm disabled={bulkBusy || n === 0} onClick={() => setBulkIds(new Set())}>
          Clear
        </Button>
        <Button $sm $accent disabled={bulkBusy || toDb === 0} onClick={() => bulkAdd(ranking, searchCtx, null)}>
          <FiDatabase size={12} /> {toDb === 0 && n > 0 ? "All in database" : `Add ${toDb || ""} to database`}
        </Button>
        {jobId && (
          <Button $sm $accent disabled={bulkBusy || toJob === 0} onClick={() => bulkAdd(ranking, searchCtx, jobId)}>
            <FiStar size={12} /> {toJob === 0 && n > 0 ? "All shortlisted" : `Shortlist ${toJob || ""}`}
          </Button>
        )}
        <Button
          $sm
          disabled={bulkBusy || n === 0 || candidateDetailsPending}
          onClick={() => exportCsv(ranking, item)}
          title={candidateDetailsPending ? "Profiles are still loading" : "Download the selected people as a CSV file, with their experience, education, skills and languages"}
        >
          <FiDownload size={12} /> {bulkBusy ? "Preparing…" : "Export CSV"}
        </Button>
        <Button $sm $ghost disabled={bulkBusy} onClick={() => { setBulkMode(false); setBulkIds(new Set()); }}>
          Done
        </Button>
      </PickBar>
    );
  };

  /* The control on the "Full ranking" heading line: a button that opens the checklist,
     and, while a filter is on, how many people pass and a way to clear it. */
  const renderChecklistFilter = (ranking, brief) => {
    const items = criteriaFilterItems(brief);
    if (!items.length) return null;
    const rows = Object.values(ranking || {});
    const passing = criteriaFilter.size ? rows.filter(passesCriteriaFilter).length : rows.length;
    return (
      <>
        {criteriaFilter.size > 0 && (
          <>
            <FilterSummary>
              <b>{passing}</b> of {rows.length} met every selected item
            </FilterSummary>
            <FilterClear type="button" onClick={() => setCriteriaFilter(new Set())}>
              Clear
            </FilterClear>
          </>
        )}
        <FilterAnchor ref={criteriaFilterRef}>
          <FilterButton
            type="button"
            $on={criteriaFilter.size > 0}
            aria-expanded={criteriaFilterOpen}
            onClick={() => setCriteriaFilterOpen((v) => !v)}
          >
            <FiSliders size={13} />
            Filters
            {criteriaFilter.size > 0 && (
              <FilterCount>{criteriaFilter.size > 9 ? "9+" : criteriaFilter.size}</FilterCount>
            )}
          </FilterButton>
          {criteriaFilterOpen && (
            <FilterPop role="group" aria-label="Filter the results">
              {["must", "nice"].map((kind) => {
                const group = items.filter((it) => it.kind === kind);
                if (!group.length) return null;
                return (
                  <React.Fragment key={kind}>
                    <FilterGroupHead>{kind === "must" ? "Must-haves" : "Nice-to-haves"}</FilterGroupHead>
                    {group.map((it) => (
                      <FilterOption
                        key={it.id}
                        type="button"
                        role="checkbox"
                        aria-checked={criteriaFilter.has(it.id)}
                        onClick={() => toggleCriterion(it.id)}
                      >
                        <FilterBox $on={criteriaFilter.has(it.id)} aria-hidden>
                          {criteriaFilter.has(it.id) && <FiCheck size={11} />}
                        </FilterBox>
                        <span>{it.text}</span>
                      </FilterOption>
                    ))}
                  </React.Fragment>
                );
              })}
            </FilterPop>
          )}
        </FilterAnchor>
      </>
    );
  };

  const renderRanking = (ranking, candidateDetailsMap, searchCtx) => {
    const entries = Object.entries(ranking).sort(
      (a, b) => parseInt(a[0], 10) - parseInt(b[0], 10),
    );
    if (entries.length === 0) return null;
    /* One rule for what is worth the recruiter's attention: a weak match and a
       disqualified one are both noise, so both collapse behind the same toggle rather
       than padding the list. Disqualified rows already score 0, so the threshold catches
       them anyway — the explicit checks are for legacy rows scored before that was true. */
    const minScore = MIN_VISIBLE_SCORE;
    const scoreOf = (entry) => Number(entry?.percentage_match) || 0;
    /* Ordered here, not just by the backend, because the stored ranking carries its order
       in its keys — a search saved before the ordering was fixed would otherwise replay a
       list whose numbers run out of order. Sorting on the score the row actually shows
       makes position and percentage agree for past searches too. `pos` is the display
       number, so it stays 1..n down the visible list whatever the stored keys say. */
    const scored = entries
      .map(([rank, entry]) => ({ rank, entry, score: scoreOf(entry) }))
      .sort(
        (a, b) =>
          b.score - a.score || parseInt(a.rank, 10) - parseInt(b.rank, 10),
      )
      .map((r, i) => ({ ...r, pos: i + 1 }));
    const isHidden = ({ entry, score }) =>
      entry?.relevant === false || score < minScore;
    const filtered = scored.filter(({ entry }) => passesCriteriaFilter(entry));
    const qualified = filtered.filter((r) => !isHidden(r));
    const disqualified = filtered.filter(isHidden);
    drawnIdsRef.current = [
      ...qualified.slice(0, rankingVisible),
      ...(showDisqualified ? disqualified : []),
    ].map((r) => r.entry?.object_id);

    // The first listed person carries the results walkthrough's targets.
    const tourId = qualified[0]?.entry?.object_id;
    const renderRow = ({ pos, rank, entry }) => {
      const objId = entry?.object_id;
      const tourRow = !!objId && objId === tourId;
      const details = candidateDetailsMap[objId];
      const detailsLoading = !details && candidateDetailsPending;
      const name = getCandidateDisplayName(details) || "—";
      const subtitle = getCandidateSubtitle(details);
      const company = (details?.company || "").trim();
      const initials = name
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
      const picUrl = details?.photo_url || null;
      const why = (entry?.why || "").trim();
      const whyText = why
        ? entry?.relevant === false
          ? <GateReason title={why}>{why}</GateReason>
          : <RankWhy title={why}>{why}</RankWhy>
        : null;
      // Ruled out once the full profile was read (wrong level, outside a years must-have, or an exclusion): muted, unpickable as a seed.
      const isOut = entry?.relevant === false;
      // Ruled out by one of the recruiter's own exclusions rather than on level.
      const isExcluded = isOut && entry?.excluded === true;
      // Derived from the checklist ticks, never scored by a model.
      const score = Math.round(
        Math.min(100, Number(entry?.percentage_match) || 0),
      );
      const level = matchLevelOf(entry);
      const rowKey = String(objId || rank);
      /* While picking seeds the whole row is the target: clicking chooses this
             person instead of opening their profile. Ruled-out rows stay unpickable. */
      const picking = !!searchCtx?.picking && !!objId && !isOut;
      const picked = picking && seedIds.has(objId);
      const atSeedCap = picking && !picked && seedIds.size >= MAX_SEEDS;
      const selecting = bulkMode && !picking && !!objId;
      const selected = selecting && bulkIds.has(objId);
      return (
        <RankingRow
          key={entry?.object_id || rank}
          data-tour={tourRow ? "fc-results-row" : undefined}
          $clickable={!!objId && !atSeedCap}
          $muted={isOut || atSeedCap}
          $picked={picked || selected}
          aria-pressed={picking ? picked : selecting ? selected : undefined}
          title={atSeedCap ? `Pick at most ${MAX_SEEDS} people` : undefined}
          onClick={
            picking
              ? () => !atSeedCap && toggleSeed(objId)
              : selecting
                ? () => toggleBulk(objId)
                : objId
                  ? () => handleCandidateClick(objId)
                  : undefined
          }
        >
          <RankNum>
            {picking ? (
              picked ? (
                <FiCheck size={14} aria-label="Picked" />
              ) : (
                <PickDot aria-hidden="true" />
              )
            ) : selecting ? (
              <FilterBox $on={selected} aria-hidden>
                {selected && <FiCheck size={11} />}
              </FilterBox>
            ) : (
              `#${pos}`
            )}
          </RankNum>
          <RankPerson>
            {detailsLoading ? (
              <>
                <Skel $w="36px" $h="36px" $r="99px" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Skel $w="40%" $h="14px" />
                  <Skel $w="55%" $h="12px" style={{ marginTop: 6 }} />
                  <Skel $w="80%" $h="12px" style={{ marginTop: 10 }} />
                </div>
              </>
            ) : (
              <>
                <RankAvatar>
                  <CandidatePhoto
                    src={picUrl}
                    fallback={<span>{initials || "?"}</span>}
                  />
                </RankAvatar>
                <div style={{ minWidth: 0 }}>
                  <RankName>{name}</RankName>
                  {(subtitle || company) && (
                    <RankRole>
                      {subtitle}
                      {subtitle && company && " at "}
                      {company && <b>{company}</b>}
                    </RankRole>
                  )}
                  {whyText && <Between $min={0} $max={820}>{whyText}</Between>}
                  {renderMatchBadges(details?.filter_matches)}
                  {isOut && (
                    <OffTierTag title={why}>
                      {isExcluded ? "Excluded" : "Wrong level"}
                    </OffTierTag>
                  )}
                  {!isOut &&
                    renderCriteriaBadges(entry, rowKey, searchCtx?.brief, tourRow)}
                </div>
              </>
            )}
          </RankPerson>
          <div style={{ textAlign: "right" }}>
            {isOut ? (
              <RankScore
                $rank={0}
                title={
                  why ||
                  (isExcluded
                    ? "Ruled out by one of your exclusions"
                    : "Too far from the role's seniority level")
                }
              >
                <FiSlash size={12} />
              </RankScore>
            ) : (
              <RankScore $rank={level.rank}>{level.label}</RankScore>
            )}
          </div>
          <RankActionCell>
            {objId && actionsLocked && (
              <ActionPills>
                {["Database", ...(searchCtx?.jobId ? ["Shortlist"] : [])].map((label) => (
                  <ActionPill
                    key={label}
                    type="button"
                    style={{ opacity: 0.6 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/plans");
                    }}
                    title="Saving people to your database or a job's shortlist is part of the Starter plan and up"
                  >
                    <FiLock size={12} style={{ color: "var(--secondary)" }} />
                    <PillLabel>{label}</PillLabel>
                  </ActionPill>
                ))}
              </ActionPills>
            )}
            {objId && !actionsLocked && (
              <ActionPills>
                <ActionPill
                  type="button"
                  $on={databaseIds.has(objId)}
                  disabled={shortlistBusyIds.has(objId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDatabaseCandidate(objId, score, searchCtx);
                  }}
                  title={
                    databaseIds.has(objId)
                      ? "In your candidate database — click to remove"
                      : "Add this person to your candidate database"
                  }
                >
                  {databaseIds.has(objId) ? (
                    <FiCheck size={12} />
                  ) : (
                    <FiDatabase size={12} />
                  )}
                  <PillLabel>Database</PillLabel>
                </ActionPill>
                {searchCtx?.jobId && (
                  <ActionPill
                    type="button"
                    $on={shortlistedIds.has(objId)}
                    disabled={shortlistBusyIds.has(objId)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleShortlistCandidate(objId, score, searchCtx);
                    }}
                    title={
                      shortlistedIds.has(objId)
                        ? "Shortlisted for this job — click to remove from its shortlist (stays in your database)"
                        : "Shortlist for this job — adds them to its shortlist"
                    }
                  >
                    {shortlistedIds.has(objId) ? (
                      <StarFilled size={12} />
                    ) : (
                      <FiStar size={12} />
                    )}
                    <PillLabel>Shortlist</PillLabel>
                  </ActionPill>
                )}
              </ActionPills>
            )}
          </RankActionCell>
          {detailsLoading ? (
            <Skel $w="70%" $h="12px" />
          ) : (
            <div style={{ minWidth: 0 }}>
              <RankCell>{subtitle || "—"}</RankCell>
              {whyText && <Between $min={821} $max={1200}>{whyText}</Between>}
            </div>
          )}
          {detailsLoading ? <Skel $w="60%" $h="12px" /> : <RankCell>{company || "—"}</RankCell>}
          {detailsLoading ? <Skel $w="85%" $h="12px" /> : <div style={{ minWidth: 0 }}>{whyText}</div>}
        </RankingRow>
      );
    };

    return (
      <RankingCard>
        {disqualified.length === 0 && <span hidden data-tour="fc-no-weaker" />}
        <RankingRow $head>
          <div>#</div>
          <div>Candidate</div>
          <div style={{ textAlign: "right" }}>Match</div>
          <div style={{ textAlign: "center" }}>Actions</div>
          <div>Title</div>
          <div>Company</div>
          <div>Description</div>
        </RankingRow>
        {qualified.slice(0, rankingVisible).map(renderRow)}
        {qualified.length > rankingVisible && (
          <>
            <div ref={rankingMoreRef} style={{ height: 1 }} />
            <HistLoadMore>
              Scroll for more · {qualified.length - rankingVisible} left
            </HistLoadMore>
          </>
        )}
        {filtered.length === 0 && (
          <FilterEmpty>Nobody in this round met every selected item.</FilterEmpty>
        )}
        {disqualified.length > 0 && (
          <>
            <DisqualifiedBar
              type="button"
              data-tour="fc-results-weaker"
              onClick={() => setShowDisqualified((v) => !v)}
            >
              {showDisqualified ? (
                <FiChevronUp size={14} />
              ) : (
                <FiChevronDown size={14} />
              )}
              {showDisqualified ? "Hide" : "Show"} {disqualified.length} weaker
              match{disqualified.length === 1 ? "" : "es"} (under {minScore}%)
            </DisqualifiedBar>
            {showDisqualified && disqualified.map(renderRow)}
          </>
        )}
      </RankingCard>
    );
  };

  // ─── Past searches table ───
  const renderHistoryTable = () => {
    const showHistorySkeleton = historyLoading;
    const historySkeletonCount = pastSearches.length === 0 ? historyFillCount : 2;
    if (pastSearches.length === 0 && !historyLoading) {
      return (
        <HistEmpty>
          <HistEmptyArt>
            <Spark size={32} />
          </HistEmptyArt>
          <HistEmptyH>No past searches yet</HistEmptyH>
          <HistEmptyP>
            Start your first AI sourcing run and we'll show all of your past
            searches here.
          </HistEmptyP>
          <Button
            $primary
            $lg
            disabled={searchBlocked}
            title={blockedReason || undefined}
            onClick={() => {
              if (searchBlocked) return;
              startNewSearch();
              navigate("/find-candidates");
            }}
          >
            <ButtonContent>
              <Spark size={14} /> Start a new search
            </ButtonContent>
          </Button>
        </HistEmpty>
      );
    }
    return (
      <HistoryTable ref={historySkelRef}>
        <HistoryRow $head>
          <div>Role</div>
          <div style={{ textAlign: "right" }}>Matched</div>
          <div>Status</div>
          <div />
          <div />
        </HistoryRow>
        {pastSearches.map((item) => (
          <HistoryRow
            key={item.id}
            onClick={() => {
              setExpandedCandidateObjectId(null);
              navigate(`/find-candidates/history/${item.id}`);
            }}
          >
            <HistRoleCell>
              <HistTitle>{item.title}</HistTitle>
              <HistCompany>
                {new Date(item.requestedAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
                {item.batchCount > 1 && ` · ${item.batchCount} rounds`}
              </HistCompany>
            </HistRoleCell>
            <HistNumCell>
              <b>{formatNumber(item.candidateCount)}</b>
            </HistNumCell>
            <div>
              <StatusBadge $kind="complete">Complete</StatusBadge>
            </div>
            <div />
            <HistWhen>→</HistWhen>
          </HistoryRow>
        ))}
        {showHistorySkeleton &&
          Array.from({ length: historySkeletonCount }).map((_, i) => (
            <HistoryRow key={`history-skel-${i}`} data-skel-row="">
              <HistRoleCell>
                <Skel $w="62%" $h="14px" />
                <Skel $w="38%" $h="11px" style={{ marginTop: 8 }} />
              </HistRoleCell>
              <HistNumCell>
                <Skel $w="34px" $h="14px" />
              </HistNumCell>
              <div>
                <Skel $w="72px" $h="20px" />
              </div>
              <div />
              <HistWhen>
                <Skel $w="10px" $h="12px" />
              </HistWhen>
            </HistoryRow>
          ))}
        <div ref={historyLoadMoreRef} style={{ height: 1 }} />
        {historyHasMore && !historyLoading && pastSearches.length > 0 && (
          <HistLoadMore>Scroll for more</HistLoadMore>
        )}
      </HistoryTable>
    );
  };

  /* Earlier rounds of this search, as folders. Only shown once there is more than one —
     a single-round search has nothing to switch between. */
  const renderBatchStrip = (item) => {
    const batches = item?.batches || [];
    if (batches.length < 2) return null;
    const ordered = batches
      .slice()
      .sort((a, b) => (a.batchNo || 0) - (b.batchNo || 0));
    return (
      <BatchStrip>
        {ordered.map((b, i) => {
          const current = b.id === item.id;
          return (
            <BatchPill
              key={b.id}
              type="button"
              $current={current}
              aria-current={current ? "true" : undefined}
              disabled={current}
              onClick={() =>
                !current && navigate(`/find-candidates/history/${b.id}`)
              }
              title={
                b.origin === "lookalike"
                  ? "People found from candidates you picked"
                  : b.origin === "refine"
                    ? "Found after you adjusted the filters"
                    : "The first round of this search"
              }
            >
              {b.origin === "lookalike" ? (
                <FiUsers size={12} />
              ) : (
                <FiFolder size={12} />
              )}
              {/* Counted by position: searches stored before 2026-10-06 start at batch 2. */}
              Round {i + 1}
              <BatchCount>{formatNumber(b.candidateCount)}</BatchCount>
            </BatchPill>
          );
        })}
      </BatchStrip>
    );
  };

  /* "Not seeing the right people?" lives at the TOP of a result set, as one button.
     Everything it can do is behind the choice modal, so the results themselves stay
     uncluttered. Only offered for searches the backend can actually extend. */
  const canRefine = (item) => !!(item?.searchId && item?.req_no);

  /* "Adjust the filters" opens the same working sheet as a new search, seeded from the
     past search — same editable chips, checklist and assistant. Find candidates then
     runs another round of that search with the edited filters. */
  const openRefineSheet = (item) => {
    if (!canRefine(item) || !item.brief) return;
    setRefineItem(item);
    setTitle(item.title || "");
    setDescription(item.jobDescription || "");
    setCountries(itemCountries(item));
    setWorldwide(itemCountries(item).length === 0);
    setBriefDraft(normalizeBriefForEdit(item.brief));
    setBriefError("");
    // The assistant opens with an intro: what the search is, and a few edits to click.
    setAgentIntro({ done: false, turns: [], result: null });
  };

  const renderRefineTrigger = (item) =>
    !canRefine(item) ? null : (
      <Button
        $primary
        data-tour="fc-results-refine"
        // Another round is a search like any other — one at a time.
        disabled={!!followUpMode || searchBlocked}
        title={blockedReason || undefined}
        onClick={() => {
          if (searchBlocked) return;
          setFollowUpError("");
          setChoiceOpen(true);
        }}
      >
        <Spark size={13} />
        {followUpMode
          ? "Working…"
          : searchBlocked
            ? "Search in progress…"
            : "Not seeing the right people?"}
      </Button>
    );

  const renderChoiceModal = (item) => {
    if (!choiceOpen || !canRefine(item)) return null;
    const close = () => setChoiceOpen(false);
    const brief = item.brief || null;
    // Nothing to pick from on a round that returned nobody.
    const hasPeople = Object.keys(item.ranking || {}).length > 0;
    return createPortal(
      <ChoiceOverlay
        role="dialog"
        aria-modal="true"
        aria-label="Get different candidates"
      >
        <ChoiceCard onMouseDown={(e) => e.stopPropagation()}>
          <ChoiceHead>
            <ChoiceTitle>Not seeing the right people?</ChoiceTitle>
            <ChoiceSub>
              Get another round of names for this search. Anyone you've already
              been shown is left out.
            </ChoiceSub>
          </ChoiceHead>

          <ChoiceOptions>
            <ChoiceOption
              type="button"
              disabled={!hasPeople || similarLocked}
              onClick={() => {
                setChoiceOpen(false);
                setBulkMode(false);
                setPickingMode(true);
              }}
            >
              <ChoiceOptionIcon>
                <FiUsers size={16} />
              </ChoiceOptionIcon>
              <div style={{ minWidth: 0 }}>
                <ChoiceOptionH>Find more people like these</ChoiceOptionH>
                <ChoiceOptionP>
                  {similarLocked
                    ? "Similar-candidate search is part of the Growth plan and up."
                    : hasPeople
                      ? `Pick up to ${MAX_SEEDS} candidates you like and we'll go and find people similar to them.`
                      : "This round didn't return anyone to pick from."}
                </ChoiceOptionP>
                {similarLocked && <PlanLock plan="Growth" label="Similar-candidate search" style={{ marginTop: 8 }} />}
              </div>
            </ChoiceOption>

            <ChoiceOption
              type="button"
              disabled={!brief}
              onClick={() => {
                setChoiceOpen(false);
                openRefineSheet(item);
              }}
            >
              <ChoiceOptionIcon>
                <FiSliders size={16} />
              </ChoiceOptionIcon>
              <div style={{ minWidth: 0 }}>
                <ChoiceOptionH>Adjust the filters</ChoiceOptionH>
                <ChoiceOptionP>
                  {brief
                    ? "Talk through what to change, then run the search again."
                    : "This search didn't record its filters, so they can't be edited."}
                </ChoiceOptionP>
              </div>
            </ChoiceOption>
          </ChoiceOptions>

          <ChoiceFooter>
            <Button $sm onClick={close}>
              Cancel
            </Button>
          </ChoiceFooter>
        </ChoiceCard>
      </ChoiceOverlay>,
      document.body,
    );
  };

  /* While picking, the whole row is the target — click people to add and remove them. */
  const renderPickBar = (item) => {
    if (!pickingMode || !canRefine(item)) return null;
    const n = seedIds.size;
    const running = !!followUpMode;
    return (
      <PickBar>
        <PickBarText>
          {n === 0
            ? "Click the people you like"
            : `${n} of ${MAX_SEEDS} picked`}
          <PickBarSub>
            {n >= MAX_SEEDS
              ? "That's the maximum — unpick someone to swap."
              : "We'll find more people similar to the ones you pick."}
          </PickBarSub>
        </PickBarText>
        <Button
          $sm
          disabled={running}
          onClick={() => {
            setPickingMode(false);
            setSeedIds(new Set());
          }}
        >
          Cancel
        </Button>
        {similarLocked && <PlanLock plan="Growth" label="Similar-candidate search" />}
        <Button
          $primary
          $sm
          disabled={running || n === 0 || similarLocked}
          onClick={() =>
            setLaunchConfirm({
              kind: "lookalike",
              run: () => startFollowUpSearch(item, "lookalike"),
            })
          }
        >
          <FiUsers size={12} />
          {followUpMode === "lookalike"
            ? "Finding similar people…"
            : `Find people like ${n === 1 ? "this one" : `these ${n}`}`}
        </Button>
      </PickBar>
    );
  };


  const renderSearchDetail = (item, opts = {}) => {
    const { backLabel, onBack, jobLink, jobId } = opts;
    const ranking = item.ranking || {};
    const rankingObjectIds = Object.values(ranking)
      .map((entry) => String(entry?.object_id || "").trim())
      .filter(Boolean);
    const loadedDetailsCount = rankingObjectIds.filter(
      (id) => !!candidateDetails[id],
    ).length;
    const showRankingSkeleton =
      rankingObjectIds.length > 0 &&
      candidateDetailsPending &&
      loadedDetailsCount === 0;
    return (
      <Screen>
        <Button $ghost $sm onClick={onBack} style={{ marginBottom: 10 }}>
          ← {backLabel}
        </Button>
        <ResultsContext>
          <ResultsContextMain>
            <ResultsLogo>
              {(item.title || "?").trim().charAt(0).toUpperCase()}
            </ResultsLogo>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ResultsTitle>{item.title}</ResultsTitle>
              <ResultsSub>
                <span>
                  {new Date(item.requestedAt).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
                <span className="sep">·</span>
                <span>
                  {formatNumber(item.candidateCount)} candidate
                  {item.candidateCount !== 1 ? "s" : ""} matched
                </span>
              </ResultsSub>
            </div>
          </ResultsContextMain>
          <ResultsContextActions>
            {jobId && (
              <Button
                as={Link}
                to={`/jobs?jobId=${jobId}`}
                state={{
                  fromFindCandidates: true,
                  returnTo: jobLink || location.pathname,
                }}
              >
                View job posting →
              </Button>
            )}
            {renderRefineTrigger(item)}
          </ResultsContextActions>
        </ResultsContext>

        {renderChoiceModal(item)}

        {/* A follow-up can fail after leaving this screen, so its error lands here. */}
        {followUpError && (
          <StripeNotice $kind="danger" style={{ marginBottom: 18 }}>
            {followUpError}
          </StripeNotice>
        )}

        {renderBatchStrip(item)}

        {renderSearchRecap(item)}

        {item.candidateCount === 0 &&
          Object.keys(ranking).length > 0 &&
          item.reason && (
            <StripeNotice style={{ marginBottom: 18 }}>
              {item.reason}
            </StripeNotice>
          )}

        {item.reason && item.candidateCount > 0 && (
          <AiSummaryCard>
            <AiSummaryHead>
              <AiSummaryIcon>
                <Spark size={16} />
              </AiSummaryIcon>
              <div>
                <AiSummaryH>Why these candidates</AiSummaryH>
                <AiSummarySub>
                  AI rationale for how the ranking was built
                </AiSummarySub>
              </div>
            </AiSummaryHead>
            <ReasonText key={item.searchId || item.req_no || ""} text={item.reason} />
          </AiSummaryCard>
        )}

        {Object.keys(ranking).length === 0 ? (
          <PreviewEmpty style={{ position: "static" }}>
            <PreviewEmptyIcon>
              <Spark size={24} />
            </PreviewEmptyIcon>
            <PreviewEmptyH>{item.failed ? "This search didn't finish" : "No results for this search"}</PreviewEmptyH>
            <PreviewEmptySub>
              {item.failed
                ? "Something went wrong on our side part-way through, so there's nothing to show. Your filters are saved: run it again and it will pick up from scratch."
                : "We couldn't find any matching candidates this time. Try widening the criteria or running the search again in a few moments."}
            </PreviewEmptySub>
          </PreviewEmpty>
        ) : showRankingSkeleton ? (
          <RankingCard>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <RankingRow key={i}>
                <Skel $w="28px" $h="18px" />
                <RankPerson>
                  <Skel $w="32px" $h="32px" $r="99px" />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Skel $w="55%" $h="13px" />
                    <Skel $w="75%" $h="11px" style={{ marginTop: 6 }} />
                  </div>
                </RankPerson>
                <Skel $w="50px" $h="22px" style={{ marginLeft: "auto" }} />
                <Skel $w="112px" $h="22px" />
                <Skel $w="70%" $h="11px" />
                <Skel $w="60%" $h="12px" />
              </RankingRow>
            ))}
          </RankingCard>
        ) : (
          <>
            {similarLocked && <span hidden data-tour="fc-similar-locked" />}
            <SectionH ref={pickAreaRef} style={{ scrollMarginTop: 12 }}>
              Full ranking
              <SectionHLine />
              {!pickingMode && (
                <FilterAnchor>
                  {actionsLocked ? (
                    <FilterButton
                      type="button"
                      style={{ opacity: 0.6 }}
                      onClick={() => navigate("/plans")}
                      title="Saving people to your database or a job's shortlist is part of the Starter plan and up"
                    >
                      <FiLock size={13} style={{ color: "var(--secondary)" }} />
                      Select
                    </FilterButton>
                  ) : (
                    <FilterButton
                      type="button"
                      $on={bulkMode}
                      aria-pressed={bulkMode}
                      onClick={() => {
                        setBulkMode((v) => !v);
                        setBulkIds(new Set());
                      }}
                    >
                      <FiCheckSquare size={13} />
                      Select
                    </FilterButton>
                  )}
                </FilterAnchor>
              )}
              {renderChecklistFilter(ranking, item.brief || null)}
            </SectionH>
            {renderPickBar(item)}
            {renderBulkBar(item, ranking)}
            {renderRanking(ranking, candidateDetails, {
              reqNo: item.req_no || "",
              title: item.title || "",
              jobId: item.jobId || "",
              brief: item.brief || null,
              picking: pickingMode && canRefine(item),
            })}
          </>
        )}
      </Screen>
    );
  };

  const renderFilterEditor = () => {
    if (!briefDraft) return null;
    const q = planOf(briefDraft);
    const places = worldwide ? [] : (briefDraft.locations || []).filter(Boolean);
    const visible = FILTER_ROWS.filter((r) => q[r.key].length > 0 || openRows.includes(r.key));
    const addable = FILTER_ROWS.filter((r) => !visible.includes(r));
    const clearRow = (row) => {
      setOpenRows((prev) => prev.filter((k) => k !== row.key));
      updatePlan((cur) => ({ ...cur, [row.key]: [] }));
    };
    const name = planName(q);
    return (
      <>
        <SearchCard>
          <SearchCardHead>
            <SearchCardName title={name}>{name}</SearchCardName>
            <SearchWhere>
              <span>in</span>
              {places.length === 0 && (
                <StaticChip>{worldwide ? "Worldwide" : "no country yet"}</StaticChip>
              )}
              {places.map((place) => {
                const iso = resolveCountryIso2(place);
                return (
                  <StaticChip key={place}>
                    {iso && (
                      <CircleFlag countryCode={iso.toLowerCase()} height={14} width={14} />
                    )}
                    {place}
                  </StaticChip>
                );
              })}
            </SearchWhere>
          </SearchCardHead>
          <SearchRows>
            <SearchRow $full>
              <SearchRowLabel>Positions</SearchRowLabel>
              <ChipListEditor
                values={q.positions}
                max={MAX_POSITIONS}
                onChange={(values) => updatePlan((v) => ({ ...v, positions: values }))}
                placeholder={q.positions.length ? "Add a position…" : "e.g. Product Development Executive"}
              />
              <FilterHint style={{ marginTop: 0 }}>
                The full job title as people write it, department included. The search starts from these.
              </FilterHint>
            </SearchRow>
            <SearchRow $full>
              <SearchRowLabel>Keywords</SearchRowLabel>
              <ChipListEditor
                values={q.keywords}
                max={MAX_KEYWORDS}
                onChange={(values) => updatePlan((v) => ({ ...v, keywords: values }))}
                placeholder={q.keywords.length ? "Add a keyword…" : "e.g. life insurance"}
              />
              <FilterHint style={{ marginTop: 0 }}>
                The sector or domain, added to every position. Years, languages and degrees belong in must-haves.
              </FilterHint>
            </SearchRow>
            {visible.map((row) => (
              <SearchRow key={row.key}>
                <SearchRowLabel>
                  {row.label}
                  <RowClear
                    type="button"
                    aria-label={`Remove ${row.label}`}
                    title="Remove this filter"
                    onClick={() => clearRow(row)}
                  >
                    <FiX size={13} />
                  </RowClear>
                </SearchRowLabel>
                <ChipListEditor
                  values={q[row.key]}
                  onChange={(v) => updatePlan((cur) => ({ ...cur, [row.key]: v }))}
                  placeholder={row.placeholder}
                  max={row.max}
                  suggest={row.suggest ? searchCompanies : undefined}
                />
              </SearchRow>
            ))}
          </SearchRows>
          {addable.length > 0 && (
            <SearchFoot>
              {addable.map((row) => (
                <AddFilter
                  key={row.key}
                  type="button"
                  onClick={() => setOpenRows((prev) => [...prev, row.key])}
                >
                  <span aria-hidden>+</span>
                  {row.label}
                </AddFilter>
              ))}
            </SearchFoot>
          )}
        </SearchCard>

        <ChecklistHead>
          <MainTitle as="h4">Checklist</MainTitle>
          <FilterHint style={{ margin: 0 }}>
            How each person found is scored, and who is ruled out.
          </FilterHint>
        </ChecklistHead>
        <ChecklistGrid>
          <ChecklistCol>
            <ChecklistColTitle>
              Must-haves <span>{briefDraft.must_haves.length}</span>
            </ChecklistColTitle>
            <ChecklistEditor
              values={briefDraft.must_haves}
              max={MAX_CRITERIA}
              onChange={(v) => setBriefDraft((p) => ({ ...p, must_haves: v }))}
              placeholder="Add a requirement…"
            />
          </ChecklistCol>
          <ChecklistCol>
            <ChecklistColTitle>
              Nice-to-haves <span>{briefDraft.nice_to_haves.length}</span>
            </ChecklistColTitle>
            <ChecklistEditor
              values={briefDraft.nice_to_haves}
              max={MAX_CRITERIA}
              onChange={(v) => setBriefDraft((p) => ({ ...p, nice_to_haves: v }))}
              placeholder="Add a preference…"
            />
          </ChecklistCol>
          <ChecklistCol>
            <ChecklistColTitle>
              Exclusions <span>{briefDraft.exclusions.length}</span>
            </ChecklistColTitle>
            <ChecklistEditor
              values={briefDraft.exclusions}
              max={MAX_CRITERIA}
              onChange={(v) => setBriefDraft((p) => ({ ...p, exclusions: v }))}
              placeholder="Rule someone out, in your own words…"
            />
          </ChecklistCol>
        </ChecklistGrid>
      </>
    );
  };

  /* Launch from the sheet: the numbers must be current first, and a count of nobody
     blocks the run rather than spending credits to confirm it. */
  const launchFromSheet = async () => {
    if (searchBlocked) {
      setBriefError(
        "A search is already running. Wait for it to finish, or cancel it first.",
      );
      return;
    }
    if (!briefHasSearchables) {
      setBriefError(
        "Add at least one search with a job title, a phrase, an employer or a school.",
      );
      return;
    }
    setBriefError("");
    // Hand edits are not counted first: the run starts at once and widens an empty search itself.
    if (refineItem) {
      const item = refineItem;
      setLaunchConfirm({
        kind: "round",
        run: () => {
          setRefineItem(null);
          startFollowUpRef.current?.(item, "refine", briefDraftRef.current);
        },
      });
      return;
    }
    setLaunchConfirm({ kind: "search", run: startFinding });
  };

  return (
    <Page>
      {renderCriteriaPop()}
      {renderTopBar()}
      <Canvas>
        {/* ── Landing / home ─────────────────────────── */}
        {screen === "home" && !onHistoryRoute && (
          <Screen>
            {/* The two ways to find people: described to the AI, or filtered by hand on its own tab. */}
            <Tabs>
              <Tab $active type="button">
                <Spark size={12} /> AI search
              </Tab>
              {features?.manualSearch && (
                <Tab type="button" onClick={() => navigate("/find-candidates/manual")}>
                  <FiSliders size={12} /> Manual search
                </Tab>
              )}
            </Tabs>
            <LandingHero>
              <LandingEyebrow>
                <Spark size={12} /> AI sourcing
              </LandingEyebrow>
              <LandingTitle>
                Find people from <AccentText>any description</AccentText>, in
                minutes.
              </LandingTitle>
              <LandingSub>
                Pick a job from your board, paste a JD, or just type a line.
                We'll find them.
              </LandingSub>
              <LandingActions>
                <Button
                  $accent
                  $lg
                  disabled={searchBlocked}
                  title={blockedReason || undefined}
                  onClick={() => startNewSearch()}
                  data-tour="fc-start"
                >
                  <ButtonContent>
                    <Spark size={14} />{" "}
                    {searchBlocked ? "Search in progress…" : "Start a new search"}
                  </ButtonContent>
                </Button>
                <Button
                  $lg
                  onClick={() => navigate("/find-candidates/history")}
                >
                  See all past searches
                  {hasUnseenFind && <PastNotifDot aria-hidden />}
                </Button>
              </LandingActions>
              {searchBlocked && <OneSearchNote $below onUpgrade={() => navigate("/plans")} />}
            </LandingHero>

            <LandingGrid>
              <LandingSection>
                <SectionH>
                  Recent searches
                  {hasUnseenFind && <PastNotifDot aria-hidden />}
                  <SectionHLine />
                  <Button
                    $ghost
                    $sm
                    onClick={() => navigate("/find-candidates/history")}
                    style={{ textTransform: "none", letterSpacing: 0 }}
                  >
                    See all →
                  </Button>
                </SectionH>
                {historyLoading && pastSearches.length === 0 ? (
                  <PastGrid>
                    {[0, 1, 2, 3].map((i) => (
                      <PastCardSkeleton key={i} />
                    ))}
                  </PastGrid>
                ) : pastSearches.length === 0 ? (
                  <PreviewEmpty style={{ position: "static" }}>
                    <PreviewEmptyIcon>
                      <Spark size={24} />
                    </PreviewEmptyIcon>
                    <PreviewEmptyH>No searches yet</PreviewEmptyH>
                    <PreviewEmptySub>
                      Once you run a search, your most recent ones will show up
                      here.
                    </PreviewEmptySub>
                  </PreviewEmpty>
                ) : (
                  <PastGrid>
                    {pastSearches.slice(0, 4).map((s) => (
                      <PastCard
                        key={s.id}
                        onClick={() =>
                          navigate(`/find-candidates/history/${s.id}`)
                        }
                      >
                        <PastCardTop>
                          <div />
                          <StatusBadge $kind="complete">Complete</StatusBadge>
                        </PastCardTop>
                        <PastCardTitle>{s.title}</PastCardTitle>
                        <PastCardCompany>
                          {new Date(s.requestedAt).toLocaleDateString(
                            undefined,
                            { month: "short", day: "numeric", year: "numeric" },
                          )}
                        </PastCardCompany>
                        <PastCardStats>
                          <div>
                            <PastStatBig>
                              {formatNumber(s.candidateCount)}
                            </PastStatBig>
                            <PastStatLbl>matched</PastStatLbl>
                          </div>
                        </PastCardStats>
                      </PastCard>
                    ))}
                  </PastGrid>
                )}
              </LandingSection>

              <LandingSection>
                <SectionH>
                  Quick starts
                  <SectionHLine />
                </SectionH>
                <QuickGrid>
                  <QuickCard
                    disabled={searchBlocked && !boardLocked}
                    title={boardLocked ? undefined : blockedReason || undefined}
                    onClick={() => (boardLocked ? navigate("/plans") : startNewSearch("board"))}
                  >
                    <QuickIcon>
                      <Spark size={16} />
                    </QuickIcon>
                    <QuickTitle>From a job in your board</QuickTitle>
                    {boardLocked ? (
                      <PlanLock plan="Starter" label="Searching from your job board" style={{ marginTop: 6 }} />
                    ) : (
                      <QuickSub>Pick from your active openings</QuickSub>
                    )}
                  </QuickCard>
                  <QuickCard
                    disabled={searchBlocked}
                    title={blockedReason || undefined}
                    onClick={() => startNewSearch("people")}
                  >
                    <QuickIcon>
                      <Spark size={16} />
                    </QuickIcon>
                    <QuickTitle>Describe a person</QuickTitle>
                    <QuickSub>"Senior engineer who's worked at…"</QuickSub>
                  </QuickCard>
                  {/* Browsing past work stays open while a search runs. */}
                  <QuickCard
                    onClick={() => navigate("/find-candidates/history")}
                  >
                    <QuickIcon>
                      <Spark size={16} />
                    </QuickIcon>
                    <QuickTitle>
                      Review past searches
                      {hasUnseenFind && <PastNotifDot aria-hidden />}
                    </QuickTitle>
                    <QuickSub>See rankings and AI rationale</QuickSub>
                  </QuickCard>
                  <QuickCard
                    disabled={searchBlocked}
                    title={blockedReason || undefined}
                    onClick={() => startNewSearch("manual")}
                  >
                    <QuickIcon>
                      <Spark size={16} />
                    </QuickIcon>
                    <QuickTitle>Paste a job description</QuickTitle>
                    <QuickSub>Title + JD in, candidates out</QuickSub>
                  </QuickCard>
                </QuickGrid>
              </LandingSection>
            </LandingGrid>
          </Screen>
        )}

        {/* ── Past searches index ─────────────────────── */}
        {onHistoryRoute && !historyId && (
          <Screen>
            <HistoryHead>
              <div>
                <Eyebrow>History</Eyebrow>
                <ScreenTitle>Past searches</ScreenTitle>
                <ScreenSub>
                  Every AI candidate search you've run. Click any to review the
                  ranking and rationale.
                </ScreenSub>
              </div>
              <HeadAction>
                <Button
                  $accent
                  $lg
                  disabled={searchBlocked}
                  title={blockedReason || undefined}
                  onClick={() => {
                    if (searchBlocked) return;
                    startNewSearch();
                    navigate("/find-candidates");
                  }}
                >
                  <ButtonContent>
                    <Spark size={14} />{" "}
                    {searchBlocked ? "Search in progress…" : "New search"}
                  </ButtonContent>
                </Button>
                {searchBlocked && <OneSearchNote onUpgrade={() => navigate("/plans")} />}
              </HeadAction>
            </HistoryHead>
            <HistoryToolbar>
              <BmCount>
                <b>{historyTotal ?? pastSearches.length}</b> total search
                {(historyTotal ?? pastSearches.length) !== 1 ? "es" : ""}
              </BmCount>
            </HistoryToolbar>
            {renderHistoryTable()}
          </Screen>
        )}

        {/* ── History detail by URL id ───────────────── */}
        {onHistoryRoute &&
          historyId &&
          (() => {
            const item = historyItemFromPath;
            if (!historyItemFetched || historyItemLoading) {
              return (
                <Screen>
                  <Skel
                    $w="110px"
                    $h="28px"
                    $r="8px"
                    style={{ marginBottom: 10 }}
                  />
                  <ResultsContext>
                    <ResultsContextMain>
                      <Skel $w="56px" $h="56px" $r="14px" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Skel $w="min(340px, 70%)" $h="24px" />
                        <Skel
                          $w="min(220px, 50%)"
                          $h="13px"
                          style={{ marginTop: 10 }}
                        />
                      </div>
                    </ResultsContextMain>
                  </ResultsContext>
                  <AiSummaryCard>
                    <AiSummaryHead>
                      <AiSummaryIcon>
                        <Spark size={16} />
                      </AiSummaryIcon>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Skel $w="180px" $h="14px" />
                        <Skel $w="240px" $h="11px" style={{ marginTop: 7 }} />
                      </div>
                    </AiSummaryHead>
                    <div style={{ marginTop: 6 }}>
                      <Skel $w="96%" $h="12px" />
                      <Skel $w="92%" $h="12px" style={{ marginTop: 7 }} />
                      <Skel $w="84%" $h="12px" style={{ marginTop: 7 }} />
                    </div>
                  </AiSummaryCard>
                  <RankingCard>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <RankingRow key={i}>
                        <Skel $w="28px" $h="18px" />
                        <RankPerson>
                          <Skel $w="32px" $h="32px" $r="99px" />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Skel $w="55%" $h="13px" />
                            <Skel $w="75%" $h="11px" style={{ marginTop: 6 }} />
                          </div>
                        </RankPerson>
                        <Skel
                          $w="50px"
                          $h="22px"
                          style={{ marginLeft: "auto" }}
                        />
                        <Skel $w="112px" $h="22px" />
                        <Skel $w="70%" $h="11px" />
                        <Skel $w="60%" $h="12px" />
                      </RankingRow>
                    ))}
                  </RankingCard>
                </Screen>
              );
            }
            if (!item) {
              return (
                <Screen>
                  <Button
                    $ghost
                    $sm
                    onClick={() => navigate("/find-candidates/history")}
                  >
                    ← All searches
                  </Button>
                  <ScreenSub style={{ marginTop: 16 }}>
                    Search not found.
                  </ScreenSub>
                </Screen>
              );
            }
            return renderSearchDetail(item, {
              backLabel: "All searches",
              onBack: () => {
                setExpandedCandidateObjectId(null);
                navigate("/find-candidates/history");
              },
              jobLink: `/find-candidates/history/${item.id}`,
              jobId: item.jobId,
            });
          })()}

        {/* ── Step 1: how to start ─────────────── */}
        {screen === "source" && !onHistoryRoute && (
          <Screen>
            <StepWrap>
              {renderStepHead(
                "Step 1 · New search",
                "How do you want to start?",
                "Pick one. You'll fill in the details on the next step.",
              )}
              <ModeList data-tour="fc-modes">
                {(() => {
                  const renderMode = ({ mode, Icon, name, blurb }) => {
                    const locked = mode === "board" && boardLocked;
                    return (
                      <ModeRow
                        key={mode}
                        type="button"
                        $current={sourceMode === mode}
                        $locked={locked}
                        onClick={() => {
                          if (locked) return navigate("/plans");
                          switchSourceMode(mode);
                          setScreen("input");
                        }}
                        data-tour={`fc-mode-${mode === "manual" ? "jd" : mode}`}
                      >
                        <ModeIcon>
                          <Icon size={20} />
                        </ModeIcon>
                        <span style={{ minWidth: 0 }}>
                          <ModeName>{name}</ModeName>
                          <ModeBlurb>
                            {locked ? "Part of the Starter plan and up. See plans to search from the jobs you track." : blurb}
                          </ModeBlurb>
                        </span>
                        <ModeChevron>{locked ? <FiLock size={16} style={{ color: "var(--secondary)" }} /> : <FiChevronRight size={18} />}</ModeChevron>
                      </ModeRow>
                    );
                  };
                  // The two ways in that need nothing set up first, grouped so the walkthrough can spotlight them together.
                  return (
                    <>
                      <ModeList data-tour="fc-mode-pick">
                        {SOURCE_MODES.filter((m) => m.mode !== "board").map(renderMode)}
                      </ModeList>
                      {SOURCE_MODES.filter((m) => m.mode === "board").map(renderMode)}
                    </>
                  );
                })()}
              </ModeList>
            </StepWrap>
          </Screen>
        )}

        {/* ── Step 2: the input for the chosen mode ─────────────── */}
        {screen === "input" && !onHistoryRoute && (
          <Screen>
            <StepWrap>
              {renderStepHead(
                `Step 2 · ${stepLabels[1]}`,
                sourceMode === "board"
                  ? "Which job are we filling?"
                  : sourceMode === "manual"
                    ? "Paste the job description."
                    : "Who are you looking for?",
                sourceMode === "board"
                  ? "Pick one of your jobs and we'll search against it."
                  : sourceMode === "manual"
                    ? "Add the title and paste the full description from anywhere."
                    : "Describe them the way you'd brief a colleague: role, seniority, where they've worked, must-haves.",
              )}
            {sourceMode === "board" && (
              <BoardJobPicker
                selectedId={jobIdOf(selectedBoardJobData)}
                onPick={pickBoardJob}
                onView={(job) => {
                  pickBoardJob(job);
                  openJobPreview();
                }}
              />
            )}
            {sourceMode === "manual" && (
              <FormBlock data-tour="fc-jd">
                <Field data-tour="fc-jd-title">
                  <FormLabel>Job title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Senior Restructuring Associate"
                  />
                </Field>
                <Field data-tour="fc-jd-text">
                  <FormLabel>Job description</FormLabel>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Paste the full job description…"
                  />
                </Field>
                {renderHiringField()}
                <AiCallout>
                  <Spark size={16} />
                  <div>
                    <b>AI will structure your input</b>
                    <span>
                      {" "}
                      into title, level, requirements and skills you can review
                      next.
                    </span>
                  </div>
                </AiCallout>
              </FormBlock>
            )}

            {sourceMode === "people" && (
              <FormBlock>
                <Field data-tour="fc-describe">
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                    }}
                    placeholder={
                      'e.g. "Senior engineer who has worked at a major bank, strong Python, comfortable leading a small team"'
                    }
                  />
                </Field>
                <ExampleRow>
                  <ExampleLbl>Try:</ExampleLbl>
                  {PEOPLE_EXAMPLES.map((q) => (
                    <ExampleChip
                      key={q}
                      type="button"
                      onClick={() => {
                        setDescription(q);
                      }}
                    >
                      {q}
                    </ExampleChip>
                  ))}
                </ExampleRow>
                <AiCallout>
                  <Spark size={16} />
                  <div>
                    <b>AI will turn this into a search brief</b>
                    <span>
                      {" "}
                      — role, seniority, companies and must-haves — then rank
                      every match against it.
                    </span>
                  </div>
                </AiCallout>
              </FormBlock>
            )}
            </StepWrap>
          </Screen>
        )}

        {/* ── Company screen (people mode) ───────────── */}
        {screen === "company" && !onHistoryRoute && (
          <Screen>
            <StepWrap>
              {renderStepHead(
                `Step ${setupStep} · The role`,
                "Name the role and who's hiring.",
                "We save this search as a role on your Jobs page.",
              )}
            <FormBlock>
              {isPeopleMode && (
                <Field data-tour="fc-title">
                  <FormLabel htmlFor="fc-role-title">Job title</FormLabel>
                  <TitleField $loading={titleLoading}>
                    <Input
                      id="fc-role-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      readOnly={titleLoading}
                      aria-busy={titleLoading}
                      placeholder={titleLoading ? "Writing a title from your description…" : "e.g. Software Engineer"}
                    />
                    {titleLoading && <BtnSpinner aria-hidden="true" />}
                  </TitleField>
                  {titleError && !titleLoading && (
                    <FieldHint style={{ color: "var(--danger)" }}>
                      {titleError}{" "}
                      <TextLink type="button" onClick={suggestTitle}>
                        Try again
                      </TextLink>
                    </FieldHint>
                  )}
                </Field>
              )}
              {renderHiringField()}
            </FormBlock>
            </StepWrap>
          </Screen>
        )}

        {screen === "country" && !onHistoryRoute && (
          <Screen>
            <StepWrap>
              {renderStepHead(
                `Step ${setupStep} · Location`,
                isPeopleMode ? "Which countries should they be in?" : "Which countries is the role hiring in?",
                "Pick one country or several — one search covers them all. Or skip the filter and look worldwide.",
              )}
              {(() => {
                const company =
                  sourceMode === "board"
                    ? firstCompany(selectedBoardJobData?.source)
                    : anonymousHiring
                      ? "Confidential"
                      : hiringName.trim();
                return (
                  <BriefStrip
                    as="button"
                    type="button"
                    onClick={openJobPreview}
                    disabled={!previewJob}
                    title="See the full job"
                  >
                    <CompanyLogo
                      name={company}
                      size={40}
                      radius={10}
                      bg={companyColor(company)}
                      fallback={initialOf(company || title)}
                    />
                    <BriefMain>
                      <BriefTitle>
                        {(sourceMode === "board" ? selectedBoardJobData?.title : title) || "Untitled role"}
                      </BriefTitle>
                      {company && <BriefCompany>{company}</BriefCompany>}
                    </BriefMain>
                    <BriefMore>
                      View job <FiChevronRight size={14} />
                    </BriefMore>
                  </BriefStrip>
                );
              })()}

              <CountryCard data-tour="fc-country">
                <FieldHead>
                  <FormLabel as="span">Countries</FormLabel>
                  {countries.length > 0 && (
                    <TokenClear type="button" onClick={() => setCountries([])}>
                      Clear all
                    </TokenClear>
                  )}
                </FieldHead>
                <CountryDropdown
                  values={countries}
                  placeholder="Type a country to add it"
                  onToggle={(name) => chooseCountry(name)}
                />
                <FieldHint>
                  {countries.length >= 2
                    ? `${countries.length} countries in one search — people in any of them are found.`
                    : countries.length === 1
                      ? "Add another country to search both at once."
                      : "Candidates must be based in the countries you pick."}
                </FieldHint>
                <QuickAddLabel as="div">Quick add</QuickAddLabel>
                <PopularGrid>
                  {POPULAR_COUNTRIES.map(({ name, label }) => {
                    const iso = resolveCountryIso2(name);
                    const active = countries.includes(name);
                    return (
                      <CountryChip
                        key={name}
                        type="button"
                        $active={active}
                        aria-pressed={active}
                        onClick={() => chooseCountry(name)}
                      >
                        {iso && (
                          <CircleFlag
                            countryCode={iso.toLowerCase()}
                            height={18}
                            width={18}
                          />
                        )}
                        <span className="nm">{label}</span>
                        {active && <FiCheck size={13} />}
                      </CountryChip>
                    );
                  })}
                </PopularGrid>
                <WorldwideOption
                  type="button"
                  $active={worldwide}
                  aria-pressed={worldwide}
                  onClick={chooseWorldwide}
                >
                  <WorldwideMark $active={worldwide}>
                    {worldwide ? <FiCheck size={13} /> : <FiGlobe size={13} />}
                  </WorldwideMark>
                  <WorldwideTitle>Search worldwide</WorldwideTitle>
                </WorldwideOption>
              </CountryCard>

              {isSubmitting && (
                <StripeNotice style={{ marginTop: 14 }}>
                  Submitting and processing your request…
                </StripeNotice>
              )}
            </StepWrap>
          </Screen>
        )}

        {/* The brief step: one full-screen sheet that derives the filters, lets the
            user edit them, and launches the search. */}
        {((screen === "brief" && !onHistoryRoute) || !!refineItem) && (
          <BriefWorkingScreen
            jobTitle={title}
            country={countries.join(", ")}
            worldwide={worldwide}
            descChars={(description || "").length}
            briefSettled={!briefLoading}
            agentDone={agentIntro.done}
            result={agentIntro.result}
            turns={agentIntro.turns}
            busy={agentBusy}
            onChat={(text) => {
              // The panel refuses overlapping turns; don't add a line for a send it ignores.
              if (agentRef.current?.busy?.()) return;
              setAgentIntro((p) => ({
                ...p,
                turns: [
                  ...p.turns,
                  { id: `u-${Date.now()}`, role: "user", text },
                  { id: `a-${Date.now()}`, role: "assistant", kind: "chat", steps: [], reply: "", done: false },
                ],
              }));
              agentRef.current?.runTurn(text);
            }}
            filters={renderFilterEditor()}
            seeded={!!refineItem}
            launchError={briefError}
            launching={isSubmitting}
            onLaunch={launchFromSheet}
            onSeeJob={previewJob ? openJobPreview : undefined}
            onRetry={refineItem ? undefined : goToBriefScreen}
            onBack={() => {
              setBriefError("");
              if (refineItem) {
                setRefineItem(null);
                return;
              }
              // Filter edits go with the step; Continue from Country derives them afresh.
              setBriefDraft(null);
              setScreen("country");
            }}
          />
        )}
        {/* The assistant behind the sheet: mounted once the brief lands, it is the
            engine the sheet chats through (it renders nothing itself). */}
        {((screen === "brief" && !onHistoryRoute) || !!refineItem) &&
          !briefLoading &&
          briefDraft && (
              <FilterAssistant
                ref={agentRef}
                brief={briefDraft}
                searchId={refineItem?.searchId || draftSearchIdRef.current}
                title={title}
                description={description}
                countries={countries}
                countryCodes={isoListOf(countries)}
                worldwide={worldwide}
                stats={
                  refineItem
                    ? {
                        asked_for: refineItem.targetCount ?? 0,
                        delivered: refineItem.candidateCount ?? 0,
                        ruled_out: refineItem.disqualifiedCount ?? 0,
                        best_match: topScoreOf(refineItem.ranking),
                        rounds_run: (refineItem.batches || []).length || 1,
                      }
                    : undefined
                }
                autoRun={refineItem ? "intro" : false}
                userSub={user?.sub}
                onBusyChange={(v) => {
                  setAgentBusy(v);
                  // A turn ending (however it ends) releases the sheet.
                  if (!v) {
                    setAgentIntro((p) => ({ ...p, done: true }));
                    // A turn that ended without a reply (stopped, failed) still closes.
                    finishAgentTurn("");
                  }
                }}
                onActivity={handleAgentActivity}
                onRecommendation={handleAgentDone}
              />
          )}

        {/* ── Finding / Running ──────────────────────── */}
        {screen === "finding" && !onHistoryRoute && (
          <WorkingSheet role="status" aria-live="polite" data-tour={isSubmitting && !launchAccepted ? undefined : "fc-finding"}>
            <PageScrollLock />
            <SheetBack
              type="button"
              onClick={() => {
                // Leave the progress screen without stopping the run; the dock brings people back to it.
                if (followUpRunFrom) navigate(followUpRunFrom);
                else setScreen("home");
              }}
              data-tour="fc-finding-back"
            >
              <FiArrowLeft size={15} />
              Back
            </SheetBack>
            <WorkingCol $wide={!!runFilters}>
              <FindingGrid $wide={!!runFilters}>
              <FindingMain>
              <WorkingEyebrow>
                <PulseDot />{" "}
                {followUpRunFrom ? "Another round" : "Running search"}
              </WorkingEyebrow>
              <WorkingTitle>
                {title ||
                  activeRun?.title ||
                  (followUpRunFrom
                    ? "Finding more people for this search"
                    : "Finding the right people for this role")}
              </WorkingTitle>
              {runError && (
                <StripeNotice $kind="danger" style={{ marginBottom: 16 }}>
                  {runError}
                </StripeNotice>
              )}

              {liveAgent && (
                <FindingLedger>
                  {[
                    { label: "Reading your brief", state: "done", detail: "" },
                    {
                      label: "Searching and reading profiles",
                      state: agentRanking ? "done" : "active",
                      detail: `${formatNumber(liveFunnel.found || 0)} people found`,
                    },
                    {
                      label: "Ranking candidates suitability",
                      state: agentRanking ? "active" : "todo",
                      detail: `${formatNumber(liveFunnel.qualified || 0)} strong matches so far`,
                    },
                    ...(liveFunnel.goal
                      ? [
                          {
                            label: "Goal",
                            state:
                              (liveFunnel.goalHave || 0) >= liveFunnel.goal
                                ? "done"
                                : "active",
                            detail:
                              `${formatNumber(liveFunnel.goalHave || 0)} of ${formatNumber(liveFunnel.goal)} strong candidates` +
                              (liveFunnel.creditsUsed != null
                                ? ` · ${formatNumber(liveFunnel.creditsUsed)} credits used`
                                : ""),
                          },
                        ]
                      : []),
                  ].map((row) => (
                    <WorkingRow key={row.label} $state={row.state}>
                      <WorkingDotCell $last={false}>
                        {row.state === "done" ? (
                          <WorkingCheck>
                            <FiCheck size={13} />
                          </WorkingCheck>
                        ) : (
                          <WorkingDot $state={row.state} />
                        )}
                      </WorkingDotCell>
                      <WorkingLabel $state={row.state}>
                        {row.label}
                      </WorkingLabel>
                      {row.state !== "todo" && row.detail && (
                        <WorkingDetail>{row.detail}</WorkingDetail>
                      )}
                    </WorkingRow>
                  ))}
                  <FeedScroll ref={feedRef} $clipped={feedClipped}>
                  {(liveFunnel.feed || []).map((l, i, arr) => (
                    <WorkingRow key={l.id} $state="done">
                      <WorkingDotCell $last={i === arr.length - 1}>
                        {l.state === "running" ? (
                          <WorkingDot $state="active" />
                        ) : l.state === "error" ? (
                          <WorkingDot />
                        ) : (
                          <WorkingCheck>
                            <FiCheck size={13} />
                          </WorkingCheck>
                        )}
                      </WorkingDotCell>
                      <WorkingLineLabel
                        $state={l.state === "running" ? "active" : "done"}
                      >
                        {l.label}
                      </WorkingLineLabel>
                      {l.detail && <WorkingDetail>{l.detail}</WorkingDetail>}
                    </WorkingRow>
                  ))}
                  </FeedScroll>
                </FindingLedger>
              )}
              {!liveAgent && (
                <WorkingLedger>
                  {displayStages.map((stage, i) => {
                    const state =
                      displayActiveId > stage.id
                        ? "done"
                        : displayActiveId === stage.id
                          ? "active"
                          : "todo";
                    const detail =
                      state === "todo" ? "" : runStageDetail(stage.id);
                    return (
                      <WorkingRow key={stage.id} $state={state}>
                        <WorkingDotCell $last={i === displayStages.length - 1}>
                          {state === "done" ? (
                            <WorkingCheck>
                              <FiCheck size={13} />
                            </WorkingCheck>
                          ) : (
                            <WorkingDot $state={state} />
                          )}
                        </WorkingDotCell>
                        <WorkingLabel $state={state}>
                          {stage.label}
                        </WorkingLabel>
                        {detail && <WorkingDetail>{detail}</WorkingDetail>}
                      </WorkingRow>
                    );
                  })}
                </WorkingLedger>
              )}
              <WorkingNote>
                {followUpRunFrom
                  ? "Anyone you've already been shown is left out — the new round opens when it's ready."
                  : liveAgent
                    ? "The assistant searches, reads profiles and adjusts the filters as it goes. You can leave this tab and come back later."
                    : "You can leave this tab and come back later — we keep working in the background."}
              </WorkingNote>
              </FindingMain>
              {runFilters && <RunFiltersPanel {...runFilters} />}
              </FindingGrid>
            </WorkingCol>
          </WorkingSheet>
        )}

        {/* ── Rate limit ─────────────────────────────── */}
        {screen === "rateLimit" && runError && (
          <Screen>
            <Button
              $ghost
              $sm
              onClick={() => {
                setRunError("");
                setScreen("home");
              }}
              style={{ marginBottom: 10 }}
            >
              ← Back to home
            </Button>
            <ScreenHead>
              <Eyebrow>Rate limit</Eyebrow>
              <ScreenTitle>You're going too fast.</ScreenTitle>
              <ScreenSub>{runError}</ScreenSub>
            </ScreenHead>
            <ButtonRow>
              <Button
                $accent
                $lg
                onClick={() => {
                  setRunError("");
                  setScreen("home");
                }}
              >
                Back to home
              </Button>
            </ButtonRow>
          </Screen>
        )}
      </Canvas>

      {renderRunDock()}

      {/* ─── Modals ──────────────────────────────────── */}
      {launchConfirm && (
        <ChoiceOverlay>
          <PageScrollLock />
          <ChoiceCard
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="launch-confirm-title"
          >
            <ChoiceHead>
              <ChoiceTitle id="launch-confirm-title">
                {launchConfirm.kind === "search"
                  ? "Start this search?"
                  : launchConfirm.kind === "lookalike"
                    ? "Find similar people?"
                    : "Start another round?"}
              </ChoiceTitle>
              <ChoiceSub>
                {launchConfirm.kind === "lookalike"
                  ? "We'll find people similar to the ones you picked. "
                  : launchConfirm.kind === "round"
                    ? "We'll search again with these filters, leaving out anyone you've already been shown. "
                    : "We'll search with the filters you confirmed. "}
                It searches until it has your goal or has used{" "}
                {formatNumber(runCreditsRef.current)} credits, then writes up everyone it
                found (about {formatNumber(Math.round((runCreditsRef.current + 100) / 100) * 100)} credits in all).
                It can't be cancelled once it starts.
              </ChoiceSub>
            </ChoiceHead>
            <ChoiceBody>
                {trialGoal && (
                  <TrialGoalNote data-tour="fc-goal-locked">
                    <FiLock size={12} /> Free trial searches look for {trialGoal} strong candidates. Pick a plan to choose more.
                  </TrialGoalNote>
                )}
                <GoalRow $locked={Boolean(trialGoal)} aria-disabled={trialGoal ? true : undefined}>
                  <div>
                    <GoalLabel>Strong candidates wanted</GoalLabel>
                    <GoalHint>Strong matches. The search stops once it has this many.</GoalHint>
                  </div>
                  <Segmented role="radiogroup" aria-label="Strong candidates wanted" data-tour="fc-confirm-goal">
                    {RUN_GOAL_PRESETS.map((n) => (
                      <SegmentedItem
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={!goalCustom && runGoal === n}
                        $active={!goalCustom && runGoal === n}
                        onClick={() => {
                          setGoalCustom(false);
                          setRunGoal(n);
                        }}
                      >
                        {n}
                      </SegmentedItem>
                    ))}
                    {goalCustom ? (
                      <SegmentedInput
                        type="number"
                        min={1}
                        max={RUN_GOAL_MAX}
                        value={runGoal || ""}
                        aria-label="Strong candidates wanted"
                        autoFocus
                        onChange={(e) => {
                          const n = Math.floor(Number(e.target.value) || 0);
                          setRunGoal(Math.max(0, Math.min(RUN_GOAL_MAX, n)));
                        }}
                        onBlur={() => {
                          if (!runGoal) setRunGoal(1);
                        }}
                      />
                    ) : (
                      <SegmentedItem
                        type="button"
                        role="radio"
                        aria-checked={false}
                        $active={false}
                        onClick={() => {
                          setGoalCustom(true);
                          if (RUN_GOAL_PRESETS.includes(runGoal)) setRunGoal(15);
                        }}
                      >
                        Custom
                      </SegmentedItem>
                    )}
                  </Segmented>
                </GoalRow>
                <GoalRow $locked={Boolean(trialGoal)} aria-disabled={trialGoal ? true : undefined}>
                  <div>
                    <GoalLabel>Extra credits</GoalLabel>
                    <GoalHint>
                      On top of the usual {formatNumber(RUN_SEARCH_CREDITS)}. Keeps it looking longer when matches are scarce.
                    </GoalHint>
                  </div>
                  <Segmented role="radiogroup" aria-label="Extra credits" data-tour="fc-confirm-credits">
                    {RUN_EXTRA_PRESETS.map((n) => (
                      <SegmentedItem
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={!extraCustom && runExtra === n}
                        $active={!extraCustom && runExtra === n}
                        onClick={() => {
                          setExtraCustom(false);
                          setRunExtra(n);
                        }}
                      >
                        {n ? `+${formatNumber(n)}` : "Default"}
                      </SegmentedItem>
                    ))}
                    {extraCustom ? (
                      <SegmentedInput
                        type="number"
                        min={0}
                        max={RUN_EXTRA_MAX}
                        step={100}
                        value={runExtra || ""}
                        placeholder="+"
                        aria-label="Extra credits"
                        autoFocus
                        onChange={(e) => {
                          const n = Math.floor(Number(e.target.value) || 0);
                          setRunExtra(Math.max(0, Math.min(RUN_EXTRA_MAX, n)));
                        }}
                      />
                    ) : (
                      <SegmentedItem
                        type="button"
                        role="radio"
                        aria-checked={false}
                        $active={false}
                        onClick={() => {
                          setExtraCustom(true);
                          if (RUN_EXTRA_PRESETS.includes(runExtra)) setRunExtra(2000);
                        }}
                      >
                        Custom
                      </SegmentedItem>
                    )}
                  </Segmented>
                </GoalRow>
            </ChoiceBody>
            <ConfirmFoot>
              <Button $ghost onClick={() => setLaunchConfirm(null)} data-tour="fc-confirm-back">
                Go back
              </Button>
              <Button
                $accent
                autoFocus
                data-tour="fc-confirm-start"
                onClick={() => {
                  const { run } = launchConfirm;
                  setLaunchConfirm(null);
                  run();
                }}
              >
                <Spark size={14} /> Yes, start
              </Button>
            </ConfirmFoot>
          </ChoiceCard>
        </ChoiceOverlay>
      )}
      {previewOpen && previewJob && (
        <ProfileModalOverlay>
          <PageScrollLock />
          <JobPreviewCard>
            <JobDetailView
              job={previewJob}
              onBack={() => setPreviewOpen(false)}
              backLabel="search setup"
              descriptionOnly
            />
          </JobPreviewCard>
        </ProfileModalOverlay>
      )}

      {expandedCandidateObjectId && expandedCandidateDetail && (
        <ProfileModalOverlay>
          <PageScrollLock />
          <ProfileModalCard data-tour="fc-profile">
            <ProfileModalHeader>
              <ProfileModalHeaderLeft>
                <ProfileModalAvatar>
                  <CandidatePhoto
                    src={expandedProfileNormalized.profile_picture}
                    fallback={<Spark size={20} style={{ opacity: 0.5 }} />}
                  />
                </ProfileModalAvatar>
                <ProfileModalNameRow>
                  <ProfileModalName>
                    {getCandidateDisplayName(expandedCandidateDetail) ||
                      "Candidate profile"}
                  </ProfileModalName>
                  {getCandidateLink(expandedCandidateDetail) && (
                    <ProfileModalLinkedInButton
                      href={getCandidateLink(expandedCandidateDetail)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View on LinkedIn"
                      data-tour="fc-profile-linkedin"
                    >
                      <BrandLinkedIn size={16} />
                    </ProfileModalLinkedInButton>
                  )}
                </ProfileModalNameRow>
              </ProfileModalHeaderLeft>
              <Button $sm onClick={() => setExpandedCandidateObjectId(null)} data-tour="fc-profile-close">
                Close
              </Button>
            </ProfileModalHeader>
            <ProfileModalBody>
              {(() => {
                const sp = expandedProfileNormalized;
                const hasBody =
                  (sp.experiences?.length || 0) > 0 ||
                  (sp.education?.length || 0) > 0 ||
                  (sp.languages?.length || 0) > 0 ||
                  (sp.skills?.length || 0) > 0 ||
                  (sp.certifications?.length || 0) > 0 ||
                  Boolean(sp.summary || sp.headline || sp.locationLine);
                return (
                  <>
                    {!sp.full && (
                      <ProfileMetaLine style={{ marginBottom: "0.6rem" }}>
                        Loading the full profile…
                      </ProfileMetaLine>
                    )}
                    {(sp.headline || sp.summary || sp.locationLine) && (
                      <ProfileSection>
                        <ProfileSectionTitle>About</ProfileSectionTitle>
                        {sp.locationLine && (
                          <ProfileMetaLine style={{ marginBottom: "0.35rem" }}>
                            {sp.locationLine}
                          </ProfileMetaLine>
                        )}
                        {sp.headline && (
                          <div
                            style={{
                              fontWeight: 600,
                              marginBottom: sp.summary ? "0.35rem" : 0,
                            }}
                          >
                            {sp.headline}
                          </div>
                        )}
                        {sp.summary && (
                          <div style={{ lineHeight: 1.55 }}>{sp.summary}</div>
                        )}
                      </ProfileSection>
                    )}
                    {sp.skills?.length > 0 && (
                      <ProfileSection>
                        <ProfileSectionTitle>Skills</ProfileSectionTitle>
                        <ChipsRow>
                          {sp.skills.map((skill, i) => (
                            <StaticChip key={`${skill}-${i}`}>{skill}</StaticChip>
                          ))}
                        </ChipsRow>
                      </ProfileSection>
                    )}
                    {sp.experiences?.length > 0 && (
                      <ProfileSection>
                        <ProfileSectionTitle>Experience</ProfileSectionTitle>
                        {sp.experiences.map((exp, i) => (
                          <ProfileEntryCard key={i}>
                            <div
                              style={{
                                fontWeight: 600,
                                marginBottom: "0.45rem",
                              }}
                            >
                              {exp.company}
                            </div>
                            {exp.positions.map((p, j) => (
                              <React.Fragment key={`${i}-${j}`}>
                                <div style={{ fontWeight: 600 }}>
                                  {p.title || "Role"}
                                </div>
                                {(p.date_range || p.location) && (
                                  <ProfileMetaLine>
                                    {[p.date_range, p.location]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </ProfileMetaLine>
                                )}
                                {p.description && (
                                  <ExpandableText text={p.description} />
                                )}
                              </React.Fragment>
                            ))}
                          </ProfileEntryCard>
                        ))}
                      </ProfileSection>
                    )}
                    {sp.education?.length > 0 && (
                      <ProfileSection>
                        <ProfileSectionTitle>Education</ProfileSectionTitle>
                        {sp.education.map((ed, i) => (
                          <ProfileEntryCard key={i}>
                            <strong>{ed.school || ed.degree}</strong>
                            {(ed.degree || ed.field) && (
                              <div style={{ marginTop: "0.15rem" }}>
                                {ed.degree || ed.field}
                              </div>
                            )}
                            {ed.date_range && (
                              <ProfileMetaLine>{ed.date_range}</ProfileMetaLine>
                            )}
                            {ed.description && (
                              <div style={{ marginTop: "0.2rem" }}>
                                {ed.description}
                              </div>
                            )}
                          </ProfileEntryCard>
                        ))}
                      </ProfileSection>
                    )}
                    {sp.certifications?.length > 0 && (
                      <ProfileSection>
                        <ProfileSectionTitle>Certifications</ProfileSectionTitle>
                        {sp.certifications.map((c, i) => (
                          <ProfileEntryCard key={`${c.name}-${i}`}>
                            <strong>{c.name}</strong>
                            {(c.authority || c.date) && (
                              <ProfileMetaLine>
                                {[c.authority, c.date].filter(Boolean).join(" · ")}
                              </ProfileMetaLine>
                            )}
                          </ProfileEntryCard>
                        ))}
                      </ProfileSection>
                    )}
                    {sp.languages?.length > 0 && (
                      <ProfileSection>
                        <ProfileSectionTitle>Languages</ProfileSectionTitle>
                        {sp.languages
                          .map((l) =>
                            typeof l === "string" ? { language: l } : l,
                          )
                          .filter(Boolean)
                          .map((l, i) => (
                            <ProfileEntryCard key={i}>
                              <strong>
                                {l?.name || l?.language || "Language"}
                              </strong>
                              {formatLanguageProficiency(
                                l?.proficiency || l?.level,
                              ) && (
                                <ProfileMetaLine>
                                  {formatLanguageProficiency(
                                    l?.proficiency || l?.level,
                                  )}
                                </ProfileMetaLine>
                              )}
                            </ProfileEntryCard>
                          ))}
                      </ProfileSection>
                    )}
                    {!hasBody && (
                      <ScreenSub>No profile data available.</ScreenSub>
                    )}
                  </>
                );
              })()}
            </ProfileModalBody>
          </ProfileModalCard>
        </ProfileModalOverlay>
      )}
    </Page>
  );
}

// Shared with the Manual search tab (pages/ManualSearchPage.js) so both People screens read as one page.
export {
  Page as PeoplePage,
  TopBar as PeopleTopBar,
  Crumbs,
  Crumb,
  CrumbSep,
  Canvas as PeopleCanvas,
  Screen as PeopleScreen,
  Eyebrow,
  ScreenTitle,
  ScreenSub,
  Button as PeopleButton,
  ButtonContent,
  CandidatePhoto,
  RankAvatar,
  ActionPills,
  ActionPill,
  PillLabel,
  Skel,
};
